import React, { useState, useEffect, useReducer, useRef, useContext, useCallback, useMemo } from "react";
import { useHistory } from "react-router-dom";
import { AuthContext } from "../../context/Auth/AuthContext"; // Certifique-se de importar o contexto correto
import { canSeeFullHistory } from "../../helpers/menuAccess";
import { isAdminUser, isInactiveTicket } from "../../helpers/canAccessTicket";
import "emoji-mart/css/emoji-mart.css";
import { Picker } from "emoji-mart";

import { isSameDay, parseISO, format } from "date-fns";
import clsx from "clsx";

import { green, blue } from "@material-ui/core/colors";
import {
  Avatar,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  LinearProgress,
  ClickAwayListener,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  IconButton,
  Link,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  makeStyles,
  Menu,
  MenuItem,
  Popover,
  Tab,
  Tabs,
  Tooltip,
  Typography,
} from "@material-ui/core";

import {
  AccessTime,
  Add,
  Block,
  ChatBubbleOutline,
  Comment,
  Done,
  DoneAll,
  ErrorOutline,
  ExpandMore,
  GetApp,
  InsertDriveFile,
  Instagram as InstagramIcon,
  Mood,
  OpenInNew,
  PlayCircleFilled,
  Refresh,
  Reply,
  ReplyAll,
  ZoomOutMap,
} from "@material-ui/icons";

import ChannelIcon from "../../helpers/channelIcon";
import { isDarkTheme } from "../../helpers/darkTint";
import MarkdownWrapper from "../MarkdownWrapper";
import ModalImageCors from "../ModalImageCors";
import MediaGalleryViewer, { toMediaGalleryItem } from "../MediaGalleryViewer";
import MessageOptionsMenu from "../MessageOptionsMenu";
import whatsBackground from "../../assets/wa-background.png";
import LocationPreview from "../LocationPreview";
import InteractiveMessageContent from "../InteractiveMessageContent";

import whatsBackgroundDark from "../../assets/wa-background-dark.png"; //DARK MODE PLW DESIGN//

import api from "../../services/api";
import toastError from "../../errors/toastError";
import { isRequestCanceled } from "../../utils/requestCancel";
import { toast } from "react-toastify";
import { i18n } from "../../translate/i18n";
import { SocketContext } from "../../context/Socket/SocketContext";
import { WhatsAppsContext } from "../../context/WhatsApp/WhatsAppsContext";
import PendingMessagesContext from '../../context/PendingMessages/PendingMessagesContext';
import OptimisticMessagesContext from '../../context/OptimisticMessages/OptimisticMessagesContext';
import { ForwardMessageContext } from "../../context/ForwardingMessage/ForwardingMessageContext";
import { ReplyMessageContext } from "../../context/ReplyingMessage/ReplyingMessageContext";
import { normalizeMediaUrl, extractMediaFilename } from "../../utils/mediaUrl";
import downloadMediaFile from "../../utils/downloadMediaFile";
import { formatPhoneNumber, isBrazilianNumber } from "../../utils/phoneFormat";
import {
  MEDIA_SLOT_TYPES,
  isMediaSlotEmpty,
  isMediaDownloading,
  isMediaFailed,
  getMediaThumbUrl
} from "../../utils/mediaState";
import { resolveGroupSenderName } from "../../helpers/groupSenderName";
import {
  hasNoTicket,
  hasSenderSign,
  getNoTicketChipLabel,
  findPreviousTicketId as findPreviousTicketIdIn,
  getSentMessageUserName as resolveSentMessageUserName,
} from "../../helpers/sentMessageOrigin";
import { asSeenByConnection } from "../../helpers/groupConnectionPerspective";
import { translateSystemMessage } from "../../helpers/systemMessageText";
import RatingSystemText from "../RatingSystemText";
import DOMPurify from "dompurify";

// Bolha cujo conteudo ocupa a largura toda: o relogio absoluto
// (timestamp: bottom 2 / right 5) cairia em cima dele, entao entra no fluxo
// logo abaixo e o textContentItem devolve os 80px que reservava para o relogio.
//
// FABRICA, nao constante: o jss-plugin-nested faz `delete style[prop]` nas chaves
// "&..." ao processar cada regra. Duas regras apontando para o MESMO objeto
// deixariam a segunda vazia — sem erro de build, so sem estilo em runtime.
const cardBleed = () => ({
  "& $textContentItem": {
    padding: "3px 6px 4px 6px",
  },
  "& $timestamp": {
    position: "static",
    display: "block",
    textAlign: "right",
    paddingTop: 2,
  },
});

// Links de emails sanitizados sempre abrem em nova aba
DOMPurify.addHook("afterSanitizeAttributes", (node) => {
  if (node.tagName === "A") {
    node.setAttribute("target", "_blank");
    node.setAttribute("rel", "noopener noreferrer");
  }
});

// <style> é proibido porque seus seletores vazariam para o app inteiro;
// a formatação de emails sobrevive via atributos style inline (sanitizados).
const EMAIL_HTML_SANITIZE_OPTIONS = {
  USE_PROFILES: { html: true },
  FORBID_TAGS: ["style", "form", "input", "button", "select", "textarea"],
};

// HTML acima disso degrada o render; cai para o texto plano do body.
const MAX_EMAIL_HTML_LENGTH = 300000;

const EmailHtmlBody = React.memo(({ html, subject }) => {
  const clean = useMemo(
    () => DOMPurify.sanitize(html, EMAIL_HTML_SANITIZE_OPTIONS),
    [html]
  );

  return (
    <div translate="no" style={{ maxWidth: "100%" }}>
      {subject ? (
        <div style={{ fontWeight: 600, marginBottom: 4 }}>{subject}</div>
      ) : null}
      <div
        style={{
          // Emails assumem fundo claro; fixa branco para legibilidade no dark mode
          backgroundColor: "#fff",
          color: "#111",
          borderRadius: 4,
          padding: 8,
          maxWidth: "100%",
          overflowX: "auto",
          whiteSpace: "normal",
          wordBreak: "break-word",
        }}
        dangerouslySetInnerHTML={{ __html: clean }}
      />
    </div>
  );
});

// createdAt pode chegar null/epoch/não-ISO (mensagens de sistema, chamadas,
// vcard importado); format() sobre data inválida lança RangeError e derruba a
// página inteira no ErrorBoundary — sempre formatar via este helper no render.
const safeFormatDate = (value, pattern, fallback = "") => {
  try {
    const parsed = typeof value === "string" ? parseISO(value) : new Date(value);
    if (!value || Number.isNaN(parsed.getTime())) return fallback;
    return format(parsed, pattern);
  } catch (_err) {
    return fallback;
  }
};

const ACK_PENDING_FALLBACK_MINUTES = 3;
const OPTIMISTIC_FALLBACK_BASE_DELAY_MS = 700;
const OPTIMISTIC_FALLBACK_MAX_DELAY_MS = 6000;
const OPTIMISTIC_FALLBACK_MAX_RETRIES = 4;
const QUICK_REACTIONS = ["👍", "❤️", "😂", "😮", "😢", "🙏"];

// Placeholder textual que o backend ANTIGO gravava no `body` quando o download
// de mídia falhava, no lugar da legenda real (mediaLabelMap em
// wbotMessageListener). Hoje o backend preserva a legenda e quem desenha o
// "indisponível" é a própria bolha, mas as linhas já gravadas continuam no
// banco — daí o regex, que é fechado (^...$) para nunca esconder uma legenda
// legítima do cliente.
const LEGACY_MEDIA_PLACEHOLDER_BODY =
  /^\s*(?:📷|🎥|🎵|📄|🖼️|📎)?\s*\[\s*(?:imagem|v[íi]deo|[áa]udio|arquivo|sticker|m[íi]dia)\s+n[ãa]o\s+dispon[íi]vel\s*\]\s*$/i;

// Tipo de anexo de DM do Instagram → selo na bolha (messagesList.instagram.dm.*).
const IG_DM_KIND_BY_ATTACHMENT_TYPE = {
  story_mention: "storyMention",
  ig_reel: "reel",
  reel: "reel",
  ig_post: "post",
  share: "shared",
  unsupported_type: "shared",
};
const IG_STORY_TTL_MS = 24 * 60 * 60 * 1000;
// Recorte do embed do reel (instagram.com/reel/<código>/embed/): a página traz
// cabeçalho com "View profile", "View more on Instagram", curtidas e campo de
// comentário. Só a mídia interessa no chat — ela fica numa caixa 4:5 logo
// abaixo do cabeçalho de altura fixa. Medido no embed em 19/10/2026; se o
// Instagram mudar o layout, é aqui que se ajusta.
const IG_EMBED_WIDTH = 240;
const IG_EMBED_HEADER_HEIGHT = 54;
const IG_EMBED_MEDIA_HEIGHT = IG_EMBED_WIDTH * 1.25;

// dataJson do Instagram já interpretado, por objeto de mensagem. Os predicados
// da bolha (isStickerMessage, hasMediaBubble...) rodam várias vezes por render
// para cada mensagem; sem o cache seria um JSON.parse por chamada.
const instagramDataCache = new WeakMap();

const getInstagramData = (message) => {
  if (!message || typeof message !== "object") return null;
  if (instagramDataCache.has(message)) return instagramDataCache.get(message);
  let data = null;
  try {
    const raw = message.dataJson;
    // Filtro barato: a maioria das mensagens é WhatsApp e nem precisa do parse.
    if (raw && (typeof raw !== "string" || raw.includes('"instagram"'))) {
      const parsed = typeof raw === "string" ? JSON.parse(raw) : raw;
      if (parsed?.source === "instagram") data = parsed;
    }
  } catch {
    data = null;
  }
  instagramDataCache.set(message, data);
  return data;
};

// Anexo de DM do Instagram (story, reel, post) lido do evento cru da Meta que o
// webhook guarda em dataJson.raw — por isso vale também para mensagens antigas.
// isPage: o payload.url é a página do instagram.com (o reel), não um arquivo.
const getInstagramDmAttachment = (message) => {
  const data = getInstagramData(message);
  if (!data || data.channel !== "dm") return null;
  const igMessage = data.raw?.message;
  const attachment = igMessage?.attachments?.[0];
  const storyUrl = igMessage?.reply_to?.story?.url;
  const kind = attachment
    ? IG_DM_KIND_BY_ATTACHMENT_TYPE[attachment.type]
    : storyUrl
      ? "storyReply"
      : null;
  if (!kind) return null;

  const url = String((attachment ? attachment.payload?.url : storyUrl) || "");
  let isPage = false;
  try {
    isPage = /(^|\.)instagram\.com$/i.test(new URL(url).hostname);
  } catch {
    isPage = false;
  }
  const pageMatch = isPage ? url.match(/instagram\.com\/(reels?|p|tv)\/([\w-]+)/i) : null;
  const embedUrl = pageMatch
    ? `https://www.instagram.com/${pageMatch[1].toLowerCase() === "reels" ? "reel" : pageMatch[1].toLowerCase()}/${pageMatch[2]}/embed/`
    : null;

  return {
    kind,
    url: url || null,
    title: attachment?.payload?.title || null,
    isPage,
    embedUrl,
  };
};

// O body de um anexo sem arquivo é a URL crua (ou "legenda\nURL", gravado pelo
// script de correção dos .bin). O card já mostra o conteúdo; a URL some.
const stripInstagramAttachmentUrl = (message, body) => {
  const url = getInstagramDmAttachment(message)?.url;
  if (!url || !body) return body;
  return String(body)
    .split("\n")
    .filter((line) => line.trim() !== url)
    .join("\n")
    .trim();
};
// Paleta de cores para os nomes dos participantes em grupos (estilo WhatsApp).
// A escolha é estável por participante via hash, então a mesma pessoa mantém
// sempre a mesma cor. Tons médios/vivos legíveis sobre fundo claro e escuro.
const SENDER_COLOR_PALETTE = [
  "#e57373", "#f06292", "#ba68c8", "#9575cd", "#7986cb",
  "#5c9ce6", "#039be5", "#0097a7", "#00897b", "#43a047",
  "#7cb342", "#c0a000", "#f57c00", "#ff7043", "#8d6e63",
  "#78909c", "#d81b60", "#8e24aa", "#3949ab", "#00838f",
];
const SUPPLEMENTAL_HEART_REACTIONS = [
  "❤️", "🧡", "💛", "💚", "💙", "💜", "🖤", "🤍", "🤎",
  "💔", "❤️‍🔥", "❤️‍🩹", "❣️",
  "💕", "💞", "💓", "💗", "💖", "💘", "💝", "💟",
  "🩷", "🩵", "🩶",
];

// Avisos de sistema (chips centralizados no meio do chat). Não existe flag
// dedicada no model: a marcação vem dentro do dataJson gravado pelo backend
// (UpdateTicketService), então a detecção é por parse + whitelist de tipo.
const SYSTEM_MESSAGE_TYPES = [
  "ticket_closed",
  "ticket_transfer",
  "ticket_start",
  "ticket_opened_by_client",
  "ticket_ai_transfer",
  // Gravado pelo HandleRatingReplyService quando o cliente responde a nota de 0 a 10.
  "ticket_rating",
  // Gravado pelo AIHubWebhookController quando a resposta da IA não conseguiu ser
  // enviada por um motivo que não gerou bolha vermelha (falha de pré-voo).
  "ai_send_failed",
];

// Aviso de nota do cliente: a nota só aparece para quem tem acesso às
// Avaliações (components/RatingSystemText).
const isRatingSystemMessage = (message) => {
  try {
    return JSON.parse(message?.dataJson || "{}")?.type === "ticket_rating";
  } catch (e) {
    return false;
  }
};

const isSystemMessage = (message) => {
  if (!message?.dataJson) return false;
  try {
    const dataJson = JSON.parse(message.dataJson);
    return Boolean(
      dataJson.isSystemMessage && SYSTEM_MESSAGE_TYPES.includes(dataJson.type)
    );
  } catch (e) {
    // dataJson inválido: cai no fluxo de mensagem normal, como antes.
    return false;
  }
};

const useStyles = makeStyles((theme) => ({
  messagesListWrapper: {
    overflow: "hidden",
    position: "relative",
    display: "flex",
    flexDirection: "column",
    flexGrow: 1,
    width: "100%",
    minWidth: 300,
    minHeight: 200,
  },

  messagesList: {
    backgroundImage: theme.mode === 'light' ? `url(${whatsBackground})` : `url(${whatsBackgroundDark})`, //DARK MODE PLW DESIGN//
    display: "flex",
    flexDirection: "column",
    flexGrow: 1,
    padding: "8px 12px",
    overflowY: "scroll",
    position: "relative",
    ...theme.scrollbarStyles,
  },

  forwardSelectionBar: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: 8,
    padding: "8px 12px",
    backgroundColor: theme.palette.type === "dark" ? "#2c2c2c" : "#eef9ff",
    borderBottom: `1px solid ${theme.palette.type === "dark" ? "#444" : "#cdeeff"}`,
  },

  forwardSelectionActions: {
    display: "flex",
    gap: 8,
  },

  forwardSelectableRow: {
    display: "flex",
    alignItems: "flex-start",
    gap: 4,
  },

  forwardSelectableRowRight: {
    justifyContent: "flex-end",
  },

  forwardSelectableContent: {
    flex: 1,
    minWidth: 0,
  },

  forwardSelectableContentRight: {
    flex: "0 1 auto",
    minWidth: "unset",
  },

  circleLoading: {
    color: green[500],
    opacity: "70%",
  },

  circleLoadingContainer: {
    position: "absolute",
    top: 12,
    left: 0,
    right: 0,
    display: "flex",
    justifyContent: "center",
    zIndex: 2,
    pointerEvents: "none",
  },

  historyLoadingContainer: {
    position: "sticky",
    top: 0,
    zIndex: 1,
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    minHeight: 40,
    padding: "8px 0 12px",
    pointerEvents: "none",
  },

  messageLeft: {
    marginRight: 20,
    // Fundo claro fixo nos dois temas: o link sem classe volta ao azul do
    // navegador (a regra global do App.js o deixaria branco no modo escuro).
    "& a:not([class])": { color: "revert", textDecoration: "revert" },
    // Não desce abaixo de 4: o boxShadow "0 1px 1px" alcança 1,5px além da
    // borda, então com margem menor a sombra de uma bolha é pintada dentro da
    // bolha seguinte e a sequência lê como um bloco único. Somado ao
    // messageDivider (8), dá os 12px da troca de remetente.
    marginTop: 4,
    minWidth: 100,
    maxWidth: 600,
    height: "auto",
    display: "block",
    position: "relative",
    "&:hover #messageActionsButton": {
      display: "flex",
      position: "absolute",
      top: 0,
      right: 0,
    },
    "&:hover $reactionTriggerButton": {
      opacity: 1,
      visibility: "visible",
      pointerEvents: "auto",
    },
    // Faixa invisível na lateral que mantém o :hover vivo enquanto o ponteiro
    // viaja até o reactionTriggerButton (right:-34, 28px de largura). NÃO
    // estender na vertical: o botão fica todo dentro da altura da bolha, e
    // qualquer top/bottom negativo faz a faixa invadir a bolha vizinha — que
    // pinta depois e rouba o hover, mostrando o botão de reação na bolha errada.
    "&::after": {
      content: '""',
      position: "absolute",
      top: 0,
      bottom: 0,
      right: -44,
      width: 44,
    },

    whiteSpace: "pre-wrap",
    backgroundColor: "#ffffff",
    color: "#303030",
    alignSelf: "flex-start",
    borderTopLeftRadius: 0,
    borderTopRightRadius: 8,
    borderBottomLeftRadius: 8,
    borderBottomRightRadius: 8,
    paddingLeft: 5,
    paddingRight: 5,
    paddingTop: 3,
    paddingBottom: 0,
    boxShadow: "0 1px 1px #b3b3b3",
  },

  quotedContainerLeft: {
    margin: "-3px -80px 6px -6px",
    overflow: "hidden",
    backgroundColor: "#f0f0f0",
    borderRadius: "7.5px",
    display: "flex",
    position: "relative",
  },

  // Aplicada só quando o salto está habilitado, para o modo somente-leitura não
  // sugerir uma interação que não existe lá.
  quotedClickable: {
    cursor: "pointer",
    transition: "background-color 140ms ease",
    "&:hover": {
      backgroundColor: "rgba(0, 0, 0, 0.06)",
    },
    "&:focus-visible": {
      outline: `2px solid ${theme.palette.primary.main}`,
      outlineOffset: 1,
    },
  },

  quotedMsg: {
    padding: 10,
    flex: 1,
    minWidth: 0,
    height: "auto",
    display: "block",
    whiteSpace: "pre-wrap",
    overflow: "hidden",
  },

  quotedSideColorLeft: {
    flex: "none",
    width: "4px",
    backgroundColor: "#6bcbef",
  },

  quotedStatusPreview: {
    position: "relative",
    display: "inline-block",
    maxWidth: 120,
  },

  quotedStatusPlayIcon: {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    color: "#fff",
    fontSize: 32,
    pointerEvents: "none",
    filter: "drop-shadow(0 0 3px rgba(0,0,0,0.6))",
  },

  messageRight: {
    marginLeft: 20,
    // Fundo claro fixo nos dois temas: o link sem classe volta ao azul do
    // navegador (a regra global do App.js o deixaria branco no modo escuro).
    "& a:not([class])": { color: "revert", textDecoration: "revert" },
    // Mesma regra do messageLeft: não desce abaixo de 4 por causa do boxShadow.
    marginTop: 4,
    minWidth: 100,
    maxWidth: 600,
    height: "auto",
    display: "block",
    position: "relative",
    "&:hover #messageActionsButton": {
      display: "flex",
      position: "absolute",
      top: 0,
      right: 0,
    },
    "&:hover $reactionTriggerButton": {
      opacity: 1,
      visibility: "visible",
      pointerEvents: "auto",
    },
    // Ver a nota do ::after em messageLeft: sem extensão vertical, senão a
    // faixa invade a bolha vizinha e rouba o hover.
    "&::after": {
      content: '""',
      position: "absolute",
      top: 0,
      bottom: 0,
      left: -44,
      width: 44,
    },

    whiteSpace: "pre-wrap",
    backgroundColor: "#dcf8c6",
    color: "#303030",
    alignSelf: "flex-end",
    borderTopLeftRadius: 8,
    borderTopRightRadius: 8,
    borderBottomLeftRadius: 8,
    borderBottomRightRadius: 0,
    paddingLeft: 5,
    paddingRight: 5,
    paddingTop: 3,
    paddingBottom: 0,
    boxShadow: "0 1px 1px #b3b3b3",
  },

  // Bolha de email HTML: ocupa 50% da área de conversa para melhor
  // visualização (emails têm ~600px de largura fixa). Nunca fica menor que
  // os 600px padrão quando a área comporta.
  messageEmail: {
    width: "50%",
    maxWidth: "none",
    minWidth: "min(600px, 100%)",
    // Sem os 80px reservados ao timestamp: padding direito igual ao esquerdo
    "& $textContentItem": {
      padding: "3px 6px 6px 6px",
    },
    // Timestamp entra no fluxo, abaixo do corpo do email
    "& $timestamp": {
      position: "static",
      display: "block",
      textAlign: "right",
      paddingTop: 6,
    },
  },

  // Bolha com anexo de documento: o card (ou o preview em iframe) ocupa a
  // largura toda e termina numa linha de botoes, e o relogio absoluto
  // (timestamp: bottom 2 / right 5) cai em cima deles — o paddingRight de 80
  // do textContentItem nao protege nada aqui, porque o anexo e irmao dele, nao
  // filho. Mesmo tratamento do messageEmail: relogio entra no fluxo, abaixo do
  // anexo, e o textContentItem perde os 80px reservados.
  messageDocumentCard: cardBleed(),

  // Enquete, botoes, lista, template e carrossel: mesma situacao do documento —
  // o conteudo ocupa a largura toda da bolha, entao o relogio precisa sair do
  // absoluto e o textContentItem devolver os 80px que reservava para ele.
  // Resposta de botao (kind "reply") fica de fora: e um chip curto, que continua
  // melhor no fluxo normal de texto.
  messageInteractiveCard: cardBleed(),

  // Mídia sem legenda (imagem, vídeo, localização): o textContentItem fica vazio
  // e o relógio, absoluto no rodapé da bolha, cai em cima da imagem. Mesma saída
  // do card de documento: relógio no fluxo, logo abaixo da mídia.
  messageMediaOnly: cardBleed(),

  // Audio: o player ocupa a largura da bolha e a transcricao e irma do
  // textContentItem, nao filha — os 80px dele nao protegem nada aqui, e o
  // relogio absoluto cai sobre a ultima linha da transcricao (ou, sem
  // transcricao, sobre o canto do player).
  messageAudioCard: cardBleed(),

  messageJumpHighlight: {
    boxShadow: `0 0 0 3px ${theme.palette.warning.main}, 0 0 0 6px rgba(255, 193, 7, 0.28), 0 1px 1px #b3b3b3`,
    backgroundColor: "rgba(255, 235, 59, 0.22)",
    transition: "background-color 220ms ease, box-shadow 220ms ease",
    animation: "$messageJumpPulse 1.1s ease-in-out 3",
  },

  "@keyframes messageJumpPulse": {
    "0%": {
      transform: "scale(1)",
    },
    "50%": {
      transform: "scale(1.015)",
    },
    "100%": {
      transform: "scale(1)",
    },
  },

  messageWithReactions: {
    // Não vai abaixo de 10: reactionsBar fica em bottom:-12 e precisa de
    // espaço para não encostar na bolha seguinte.
    marginBottom: 10,
  },

  messageSticker: {
    backgroundColor: "transparent",
    boxShadow: "none",
    paddingLeft: 0,
    paddingRight: 0,
    paddingTop: 0,
    minWidth: "auto",
    maxWidth: 160,
  },

  messageMediaBleed: {
    display: "block",
  },

  // Vídeo da bolha: o botão do canto abre o visualizador da conversa; o player
  // continua tocando ali mesmo.
  videoMediaFrame: {
    position: "relative",
    display: "inline-block",
    lineHeight: 0,
  },
  videoViewerButton: {
    position: "absolute",
    top: 6,
    right: 6,
    padding: 6,
    color: "#fff",
    backgroundColor: "rgba(0, 0, 0, 0.45)",
    "&:hover": {
      backgroundColor: "rgba(0, 0, 0, 0.65)",
    },
  },

  // Centraliza a mídia da resposta na largura da bolha (ver shouldCenterMedia).
  centeredMediaWrapper: {
    display: "flex",
    justifyContent: "center",
  },

  quotedContainerRight: {
    margin: "-3px -80px 6px -6px",
    overflowY: "hidden",
    backgroundColor: "#cfe9ba",
    borderRadius: "7.5px",
    display: "flex",
    position: "relative",
  },

  quotedMsgRight: {
    padding: 10,
    flex: 1,
    minWidth: 0,
    height: "auto",
    whiteSpace: "pre-wrap",
  },

  quotedSideColorRight: {
    flex: "none",
    width: "4px",
    backgroundColor: "#35cd96",
  },

  messageActionsButton: {
    display: "none",
    position: "relative",
    color: "#999",
    zIndex: 1,
    backgroundColor: "inherit",
    opacity: "90%",
    "&:hover, &.Mui-focusVisible": { backgroundColor: "inherit" },
  },

  reactionTriggerButton: {
    display: "flex",
    position: "absolute",
    top: 2,
    width: 28,
    height: 28,
    zIndex: 2,
    opacity: 0,
    visibility: "hidden",
    pointerEvents: "none",
    transition: "opacity 120ms ease",
    color: "#667781",
    backgroundColor: "rgba(255, 255, 255, 0.92)",
    boxShadow: "0 1px 2px rgba(0,0,0,0.2)",
    "&:hover, &.Mui-focusVisible": {
      backgroundColor: "rgba(255, 255, 255, 1)",
    },
  },

  reactionTriggerButtonLeft: {
    right: -34,
  },

  reactionTriggerButtonRight: {
    left: -34,
  },

  reactionPopoverPaper: {
    borderRadius: 22,
    padding: "4px 6px",
    boxShadow: "0 2px 12px rgba(0,0,0,0.18)",
    overflow: "hidden",
    backgroundColor: "#1f1f1f",
  },

  reactionQuickBar: {
    display: "flex",
    alignItems: "center",
    gap: 4,
  },

  reactionQuickButton: {
    minWidth: 30,
    width: 30,
    height: 30,
    borderRadius: "50%",
    padding: 0,
    lineHeight: 1,
    fontSize: 20,
    color: "#fff",
  },

  reactionPlusButton: {
    minWidth: 30,
    width: 30,
    height: 30,
    borderRadius: "50%",
    padding: 0,
    color: "#fff",
    backgroundColor: "rgba(255,255,255,0.14)",
    "&:hover": {
      backgroundColor: "rgba(255,255,255,0.2)",
    },
  },

  reactionPalettePaper: {
    borderRadius: 8,
    padding: 0,
    boxShadow: "0 2px 12px rgba(0,0,0,0.18)",
    overflow: "hidden",
  },

  reactionPickerWrapper: {
    display: "flex",
    flexDirection: "column",
  },

  reactionSupplementalRow: {
    display: "flex",
    alignItems: "center",
    gap: 4,
    padding: "6px 8px 4px",
    borderBottom: "1px solid rgba(0, 0, 0, 0.08)",
    backgroundColor: "rgba(0, 0, 0, 0.02)",
    overflowX: "auto",
    ...theme.scrollbarStyles,
  },

  reactionSupplementalLabel: {
    fontSize: 11,
    color: "rgba(0,0,0,0.56)",
    paddingLeft: 8,
    paddingTop: 4,
    backgroundColor: "rgba(0, 0, 0, 0.02)",
  },

  reactionSupplementalButton: {
    minWidth: 28,
    width: 28,
    height: 28,
    borderRadius: "50%",
    padding: 0,
    lineHeight: 1,
    fontSize: 18,
  },

  messageGroupName: {
    display: "block",
    color: "#1e88e5",
    fontWeight: 600,
    fontSize: "0.8rem",
    marginBottom: 1,
  },

  messageContactName: {
    display: "flex",
    color: "#6bcbef",
    fontWeight: 500,
  },

  sentMessageMeta: {
    display: "block",
    fontSize: "0.68rem",
    lineHeight: 1.25,
    fontStyle: "italic",
    opacity: 0.6,
    margin: "0 0 2px 0",
  },

  forwardedLabel: {
    display: "flex",
    alignItems: "center",
    gap: 2,
    fontSize: "0.72rem",
    lineHeight: 1.25,
    fontStyle: "italic",
    opacity: 0.6,
    margin: "0 0 2px 0",
  },

  // Seta curva do WhatsApp = Reply espelhado.
  forwardedIcon: {
    fontSize: 14,
    transform: "scaleX(-1)",
  },

  mentionClickable: {
    color: "#1565c0",
    fontWeight: 600,
    cursor: "pointer",
    textDecoration: "underline",
  },

  mentionDialogInfo: {
    marginTop: 8,
    lineHeight: 1.6,
  },

  // Os 80px (120px quando editada) de padding à direita são a área reservada
  // ao relógio, que é position:absolute dentro de messageLeft/messageRight.
  // Citação renderizada ACIMA da mídia. O padding horizontal precisa ser o
  // mesmo do textContentItem: as margens negativas de quotedContainerLeft/Right
  // (-80px à direita) são calibradas para cancelá-lo — fora daqui a citação
  // vazaria 80px para fora do balão.
  quotedAboveMediaWrapper: {
    padding: "2px 80px 0 6px",
  },

  // Ao apertar o padding vertical, NÃO mexer nesses dois valores: são eles que
  // impedem o texto de passar por baixo do horário.
  textContentItem: {
    overflowWrap: "break-word",
    padding: "2px 80px 4px 6px",
    // explícito: sem isto o corpo da mensagem herda os 14px que o CssBaseline
    // aplica no body (body2), já que o tema não define typography
    fontSize: 16,
  },

  textContentItemEdited: {
    overflowWrap: "break-word",
    padding: "2px 120px 4px 6px",
  },

  textContentItemDeleted: {
    fontStyle: "italic",
    color: "rgba(0, 0, 0, 0.36)",
    overflowWrap: "break-word",
    padding: "2px 80px 4px 6px",
  },

  textContentItemSticker: {
    padding: "2px 6px 0 6px",
    minHeight: 16,
  },

  messageMedia: {
    display: "block",
    objectFit: "contain",
    width: "auto",
    height: "auto",
    maxWidth: 250,
    maxHeight: 420,
    backgroundColor: "#000",
    borderTopLeftRadius: 8,
    borderTopRightRadius: 8,
    borderBottomLeftRadius: 8,
    borderBottomRightRadius: 8,
    "&:fullscreen": {
      objectFit: "contain",
      width: "100%",
      height: "100%",
      maxWidth: "100vw",
      maxHeight: "100vh",
      borderRadius: 0,
      backgroundColor: "#000",
    },
    "&:-webkit-full-screen": {
      objectFit: "contain",
      width: "100%",
      height: "100%",
      maxWidth: "100vw",
      maxHeight: "100vh",
      borderRadius: 0,
      backgroundColor: "#000",
    },
    "&:-moz-full-screen": {
      objectFit: "contain",
      width: "100%",
      height: "100%",
      maxWidth: "100vw",
      maxHeight: "100vh",
      borderRadius: 0,
      backgroundColor: "#000",
    },
  },

  documentPreviewFrame: {
    width: "100%",
    minHeight: 280,
    border: 0,
    borderRadius: 8,
    backgroundColor: "#fff",
  },

  timestamp: {
    fontSize: 11,
    position: "absolute",
    // O bottom resolve contra o PADDING BOX da bolha (= borda interna), então
    // aumentar o paddingBottom dela não descola o relógio da borda: cresce o
    // border box e o padding box juntos e só o texto sobe. O bottom é o único
    // mecanismo que descola, e custa 0px de altura. Também tira o relógio de
    // dentro do arco do borderRadius. Não passar de 2-3: em mídia sem legenda
    // o relógio já cai sobre o canto da imagem, e cada px aqui piora isso.
    bottom: 2,
    right: 5,
    color: "#999",
  },

  // Mesmo visual do chip de conexão da lista de chamados (TicketListItemCustom)
  connectionBadge: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    color: "#FFF",
    padding: 2,
    borderRadius: 3,
    marginRight: 4,
    marginBottom: 4,
    verticalAlign: "middle",
    cursor: "default",
  },

  timestampSticker: {
    position: "static",
    display: "block",
    textAlign: "right",
    marginTop: 2,
    marginRight: 4,
  },

  // Divisor de data: gastava 56px de altura (10+10 de margem + 8+8 de padding
  // + linha) para exibir "03/08/2026"; agora ocupa ~32px.
  dailyTimestamp: {
    alignItems: "center",
    textAlign: "center",
    alignSelf: "center",
    width: "110px",
    backgroundColor: "#e1f3fb",
    // 8 em cima e 4 embaixo dá 8/8 na tela: embaixo estes 4 somam com o
    // marginTop:4 da bolha seguinte.
    margin: "8px auto 4px",
    borderRadius: "10px",
    boxShadow: "0 1px 1px #b3b3b3",
  },

  dailyTimestampText: {
    color: "#808888",
    padding: "2px 8px",
    alignSelf: "center",
    marginLeft: "0px",
  },

  ackIcons: {
    fontSize: 18,
    verticalAlign: "middle",
    marginLeft: 4,
  },

  deletedIcon: {
    fontSize: 18,
    verticalAlign: "middle",
    marginRight: 4,
  },

  deletedNoticeText: {
    display: "inline-flex",
    alignItems: "center",
    fontStyle: "italic",
    color: "rgba(0, 0, 0, 0.36)",
  },

  revealDeletedButton: {
    display: "inline-block",
    marginLeft: 4,
    cursor: "pointer",
    color: "#0b93e0",
    fontStyle: "normal",
    fontSize: "0.75rem",
    textDecoration: "underline",
    userSelect: "none",
  },

  igCommentTag: {
    display: "flex",
    alignItems: "center",
    flexWrap: "wrap",
    gap: 6,
    marginBottom: 4,
    paddingBottom: 4,
    borderBottom: "1px dashed rgba(225, 48, 108, 0.4)",
  },
  igCommentChip: {
    height: 18,
    fontSize: 10,
    fontWeight: 600,
    background: "#E1306C",
    color: "#fff",
    "& .MuiChip-label": { paddingLeft: 6, paddingRight: 8 },
  },
  igPostLink: {
    display: "inline-flex",
    alignItems: "center",
    gap: 2,
    fontSize: 11,
    color: "#E1306C",
    cursor: "pointer",
  },
  // Miniatura da publicação comentada: identifica o post de relance, sem sair da
  // conversa. Clicar abre o permalink (quando a Meta devolveu um).
  igPostThumb: {
    position: "relative",
    display: "inline-flex",
    width: 34,
    height: 34,
    flexShrink: 0,
    borderRadius: 4,
    overflow: "hidden",
    border: "1px solid rgba(225, 48, 108, 0.4)",
    "& img": {
      width: "100%",
      height: "100%",
      objectFit: "cover",
    },
  },
  igPostThumbBadge: {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    fontSize: 16,
    color: "#fff",
    filter: "drop-shadow(0 0 2px rgba(0,0,0,0.6))",
  },
  igReplyButton: {
    minWidth: 0,
    height: 20,
    padding: "0 6px",
    fontSize: 11,
    color: "#833AB4",
    textTransform: "none",
  },
  // Card de anexo de DM (story/reel/post): legenda do post, player do reel sob
  // demanda e aviso de story expirado.
  igDmCaption: {
    fontSize: 12,
    opacity: 0.85,
    whiteSpace: "pre-wrap",
    wordBreak: "break-word",
    display: "-webkit-box",
    WebkitLineClamp: 3,
    WebkitBoxOrient: "vertical",
    overflow: "hidden",
    marginBottom: 4,
  },
  igDmActions: {
    display: "flex",
    alignItems: "center",
    flexWrap: "wrap",
    gap: 8,
    marginBottom: 4,
  },
  igDmNotice: {
    fontSize: 11,
    fontStyle: "italic",
    opacity: 0.75,
    marginBottom: 4,
  },
  igReelFrame: {
    position: "relative",
    width: IG_EMBED_WIDTH,
    height: IG_EMBED_MEDIA_HEIGHT,
    overflow: "hidden",
    borderRadius: 8,
    marginBottom: 4,
    background: "#000",
  },
  igReelEmbed: {
    position: "absolute",
    top: -IG_EMBED_HEADER_HEIGHT,
    left: 0,
    width: IG_EMBED_WIDTH,
    // Só precisa caber cabeçalho + mídia; o resto da página fica cortado.
    height: IG_EMBED_HEADER_HEIGHT + IG_EMBED_MEDIA_HEIGHT + 120,
    border: 0,
  },

  ackDoneAllIcon: {
    color: blue[500],
    fontSize: 18,
    verticalAlign: "middle",
    marginLeft: 4,
  },

  ackErrorIcon: {
    color: "#e53935",
    fontSize: 18,
    verticalAlign: "middle",
    marginLeft: 4,
    cursor: "pointer",
    // Numa bolha de uma linha (~47px) o #messageActionsButton, que é absolute em
    // top:0/right:0 com z-index 1, cobre a metade de cima deste ícone e ainda
    // intercepta o clique. Empilhar acima dele mantém a falha de envio visível e
    // o tooltip acessível; o botão de opções continua clicável pelo topo, que é a
    // maior parte da sua área.
    position: "relative",
    zIndex: 2,
  },

  ackErrorTooltip: {
    maxWidth: 320,
    fontSize: 12,
    lineHeight: 1.4,
    whiteSpace: "normal",
    wordBreak: "break-word",
    overflowWrap: "anywhere",
    textAlign: "left",
  },

  ackPendingTooltip: {
    maxWidth: 320,
    fontSize: 12,
    lineHeight: 1.4,
    whiteSpace: "normal",
    wordBreak: "break-word",
    overflowWrap: "anywhere",
    textAlign: "left",
  },

  ackPendingStaleIcon: {
    color: theme.palette.warning.main,
  },

  downloadMedia: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "inherit",
    padding: 10,
  },

  // ── Placeholder de mídia baixando ────────────────────────────────────────
  // A moldura carrega a proporção da mídia final (vinda do dataJson ou da
  // própria miniatura), para a bolha não "saltar" quando o arquivo chega.
  mediaThumbFrame: {
    position: "relative",
    overflow: "hidden",
    borderRadius: 8,
    // Fundo escuro evita o flash branco antes de a miniatura pintar.
    backgroundColor: "#0b141a",
    lineHeight: 0,
    maxWidth: 250,
  },
  mediaThumbBlurred: {
    width: "100%",
    height: "100%",
    objectFit: "cover",
    filter: "blur(10px)",
    // filter: blur() amostra os pixels TRANSPARENTES de fora do elemento e
    // devolve uma borda desbotada. O scale empurra esse halo para fora do
    // overflow:hidden do pai — 1.1 cobre folgado um blur de 10px.
    transform: "scale(1.1)",
    transformOrigin: "center",
  },
  mediaThumbScrim: {
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    gap: 6,
    // Sem este véu, o spinner some numa foto clara.
    backgroundColor: "rgba(0,0,0,0.28)",
  },
  mediaThumbLabel: {
    fontSize: 11,
    color: "#fff",
    textShadow: "0 1px 2px rgba(0,0,0,0.6)",
  },
  mediaPlaceholderBox: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    gap: 8,
    width: 250,
    maxWidth: "100%",
    height: 160,
    backgroundColor: "#f0f2f5",
    borderRadius: 8,
    color: "#667781",
    fontSize: 12,
  },
  // Imita a pegada do <audio controls> (~300x54) para a troca não mexer na
  // altura da bolha.
  mediaAudioPlaceholder: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    width: 300,
    maxWidth: "100%",
    height: 54,
    padding: "0 14px",
    boxSizing: "border-box",
    backgroundColor: "#f0f2f5",
    borderRadius: 27,
    color: "#667781",
  },
  mediaAudioPlaceholderTrack: {
    flex: 1,
  },
  mediaPlaceholderError: {
    fontSize: 12,
    color: "#b71c1c",
    padding: "0 10px 8px",
    textAlign: "center",
  },

  audioTranscription: {
    fontSize: 12,
    // Cor fixa: a bolha é sempre clara nos dois temas, então uma cor
    // dependente do tema sumiria no modo escuro.
    color: "#667781",
    whiteSpace: "pre-wrap",
    maxWidth: 330,
    padding: "2px 4px 0",
  },

  audioTranscriptionLabel: {
    fontWeight: 500,
    marginRight: 4,
  },

  audioTranscriptionRetry: {
    fontSize: 12,
    textTransform: "none",
    padding: "0 4px",
    minWidth: 0,
    // Cor fixa pelo mesmo motivo do audioTranscription: o primary do tema
    // escuro é branco e sumia na bolha.
    color: "#027eb5",
    "&:hover": {
      backgroundColor: "rgba(2, 126, 181, 0.08)",
    },
  },

  audioTranscriptionError: {
    fontSize: 12,
    fontStyle: "italic",
    color: "#667781",
    whiteSpace: "pre-wrap",
    maxWidth: 330,
    padding: "2px 4px 0",
  },

  documentCard: {
    width: "100%",
    minWidth: 220,
    borderRadius: 8,
    backgroundColor: "rgba(214, 232, 218, 0.88)",
    border: "1px solid rgba(25, 89, 56, 0.20)",
    overflow: "hidden",
  },

  documentCardCompact: {
    width: "100%",
    minWidth: 220,
  },

  documentCardHeader: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    padding: "10px 12px",
  },

  documentCardIcon: {
    width: 40,
    height: 40,
    borderRadius: 8,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "#e7f0ff",
    color: "#1f6feb",
    flexShrink: 0,
  },

  documentCardInfo: {
    minWidth: 0,
    display: "flex",
    flexDirection: "column",
    gap: 2,
  },

  documentCardFilename: {
    fontSize: 14,
    fontWeight: 500,
    color: "#1f2937",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },

  documentCardMeta: {
    fontSize: 12,
    color: "#667781",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },

  documentCardActions: {
    display: "flex",
    justifyContent: "space-between",
    gap: 8,
    padding: "8px 10px 10px",
  },

  documentCardButton: {
    flex: 1,
    textTransform: "none",
    fontSize: 12,
    fontWeight: 600,
    borderRadius: 8,
    minHeight: 34,
    padding: "6px 10px",
  },

  // Chips "Atendimento iniciado/finalizado por ...". Gastavam 96px (somando o
  // wrapper systemMessageRow) para uma linha de texto; agora ocupam ~36px
  // (~27px quando o chip vem colado em outro chip de sistema, ver
  // systemChipStacked). O padding vertical é 2 e não 4 por causa das telas em
  // 125% de escala, onde a pilha de chips comia a área útil do chat.
  // As duas classes são idênticas de propósito (já eram antes) e precisam
  // continuar iguais.
  ticketNunberClosed: {
    backgroundColor: "#f5f5f5",
    color: "#666",
    padding: "2px 12px",
    margin: "4px auto",
    borderRadius: "8px",
    fontSize: "12px",
    fontWeight: 500,
    textAlign: "center",
    border: "1px solid #e0e0e0",
    maxWidth: "500px",
    width: "auto",
    display: "inline-block",
  },

  ticketNunberOpen: {
    backgroundColor: "#f5f5f5",
    color: "#666",
    padding: "2px 12px",
    margin: "4px auto",
    borderRadius: "8px",
    fontSize: "12px",
    fontWeight: 500,
    textAlign: "center",
    border: "1px solid #e0e0e0",
    maxWidth: "500px",
    width: "auto",
    display: "inline-block",
  },

  // Linha que centraliza o chip de mensagem de sistema. Era estilo inline;
  // virou classe porque a margem aqui SOMA com a do chip (nada colapsa, o
  // messagesList é flex column) e sozinha respondia por 20 dos 96px.
  systemMessageRow: {
    display: "flex",
    justifyContent: "center",
    width: "100%",
    // Resulta em 8/8: em cima estes 4 somam com os 4 do próprio chip
    // (ticketNunber*), embaixo os 4 do chip somam com o marginTop:4 da bolha.
    margin: "4px 0 0",
  },

  // Modificadores para chip de sistema logo abaixo de outro chip de sistema.
  // Sem eles o espaço entre dois chips seguidos vira 12px (4 da linha + 4 do
  // chip novo + 4 de baixo do chip anterior); zerando as duas margens de topo
  // sobram só os 4px do chip anterior — o mesmo ritmo usado entre as bolhas.
  // São duas classes porque a margem mora nos dois elementos (linha e chip).
  systemMessageRowStacked: {
    marginTop: 0,
  },

  systemChipStacked: {
    marginTop: 0,
  },

  // Espaçador inserido quando a conversa troca de remetente. É um <span>, mas
  // como filho direto do messagesList (flex column) vira item flex, então a
  // margem vertical vale.
  messageDivider: {
    display: "block",
    marginTop: 8,
  },

  // Linha das mensagens otimistas/pendentes (ainda não confirmadas pelo envio).
  optimisticRow: {
    display: "flex",
    justifyContent: "flex-end",
    // Zero de propósito: o espaçamento vem do marginTop da bolha filha, que é
    // flex item e por isso NÃO colapsa com a margem daqui — as duas somariam,
    // deixando a bolha pendente 4px mais afastada e causando um pulo no
    // instante em que o envio confirma.
    marginTop: 0,
  },

  // Estilos para o card de contato (vCard)
  contactCard: {
    display: "flex",
    flexDirection: "column",
    minWidth: 280,
    maxWidth: 320,
    borderRadius: 8,
    overflow: "hidden",
  },

  contactCardHeader: {
    display: "flex",
    alignItems: "center",
    padding: "12px 12px 8px 12px",
    gap: 12,
  },

  contactCardAvatar: {
    width: 48,
    height: 48,
    borderRadius: "50%",
    backgroundColor: "#128c7e",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: "#fff",
    fontSize: 24,
    fontWeight: 500,
    flexShrink: 0,
  },

  contactCardInfo: {
    display: "flex",
    flexDirection: "column",
    flex: 1,
    minWidth: 0,
  },

  contactCardName: {
    fontSize: 15,
    fontWeight: 500,
    color: "#303030",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    marginBottom: 2,
  },

  contactCardNumber: {
    fontSize: 13,
    color: "#667781",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },

  contactCardDivider: {
    height: 1,
    backgroundColor: "rgba(0,0,0,0.08)",
    margin: "0 12px",
  },

  contactCardActions: {
    display: "flex",
    justifyContent: "center",
    gap: 8,
    padding: "8px 12px 6px 12px",
  },

  contactCardSingleAction: {
    width: "100%",
  },

  contactCardButton: {
    flex: 1,
    textTransform: "none",
    fontSize: 13,
    fontWeight: 500,
    padding: "6px 12px",
    borderRadius: 6,
    boxShadow: "none",
    "&:hover": {
      boxShadow: "none",
    },
  },

  contactCardButtonPrimary: {
    backgroundColor: "#dcf8c6",
    color: "#075e54",
    border: "1px solid #34b7a7",
    "&:hover": {
      backgroundColor: "#c5e8b0",
    },
  },

  contactCardButtonSecondary: {
    backgroundColor: "transparent",
    color: "#128c7e",
    border: "1px solid #128c7e",
    "&:hover": {
      backgroundColor: "rgba(18, 140, 126, 0.08)",
    },
  },

  contactCardTimestamp: {
    fontSize: 11,
    color: "#999",
    textAlign: "right",
    padding: "0 8px 6px 0",
    display: "flex",
    justifyContent: "flex-end",
    alignItems: "center",
    gap: 4,
  },

  sharedContactsDialogList: {
    paddingTop: 0,
    paddingBottom: 0,
    maxHeight: 420,
    overflowY: "auto",
  },

  sharedContactItem: {
    alignItems: "center",
    gap: 8,
  },

  sharedContactPrimary: {
    fontWeight: 500,
    color: "#1f2937",
  },

  sharedContactSecondary: {
    color: "#667781",
    fontSize: 12,
  },

  sharedContactActions: {
    display: "flex",
    gap: 8,
    flexWrap: "wrap",
    justifyContent: "flex-end",
  },

  sharedContactActionButton: {
    textTransform: "none",
    fontSize: 12,
    minWidth: 88,
  },

  reactionsBar: {
    display: "flex",
    flexWrap: "wrap",
    gap: 4,
    position: "absolute",
    bottom: -12,
    zIndex: 3,
  },

  reactionsBarLeft: {
    left: 8,
  },

  reactionsBarRight: {
    right: 8,
  },

  reactionChip: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    borderRadius: 12,
    padding: "2px 8px",
    fontSize: 12,
    lineHeight: 1.2,
    backgroundColor: isDarkTheme(theme) ? theme.palette.background.paper : "#ffffff",
    // <button> não herda a cor do texto: no escuro fixa a do tema (contagem legível)
    color: isDarkTheme(theme) ? theme.palette.text.primary : undefined,
    boxShadow: "0 1px 2px rgba(0,0,0,0.22)",
    cursor: "pointer",
    border: "none",
  },

  reactionDetailsPaper: {
    width: 320,
    maxWidth: "90vw",
    borderRadius: 10,
    overflow: "hidden",
    backgroundColor: theme.mode === "light" ? "#1f1f1f" : "#202c33",
    color: "#f1f1f1",
  },

  reactionDetailsTabs: {
    minHeight: 40,
    backgroundColor: "rgba(255,255,255,0.04)",
    borderBottom: "1px solid rgba(255,255,255,0.12)",
  },

  reactionDetailsTab: {
    minHeight: 40,
    minWidth: 48,
    textTransform: "none",
    color: "rgba(255,255,255,0.78)",
    fontWeight: 500,
    fontSize: 13,
  },

  reactionDetailsList: {
    maxHeight: 280,
    overflowY: "auto",
    ...theme.scrollbarStyles,
    padding: 0,
  },

  reactionDetailsItem: {
    paddingTop: 8,
    paddingBottom: 8,
    borderBottom: "1px solid rgba(255,255,255,0.08)",
    "&:last-child": {
      borderBottom: "none",
    },
  },

  reactionDetailsAvatar: {
    width: 30,
    height: 30,
    fontSize: 12,
    backgroundColor: "#6bcbef",
    color: "#073b4c",
  },

  reactionDetailsPrimary: {
    color: "#ffffff",
    fontSize: 14,
  },

  reactionDetailsSecondary: {
    color: "rgba(255,255,255,0.65)",
    fontSize: 12,
  },

  reactionDetailsEmoji: {
    marginLeft: 10,
    fontSize: 20,
    lineHeight: 1,
  },

  reactionDetailsEmpty: {
    padding: "16px 12px",
    textAlign: "center",
    color: "rgba(255,255,255,0.7)",
    fontSize: 13,
  },

  goToBottomButtonWrapper: {
    position: "absolute",
    right: 20,
    bottom: 20,
    zIndex: 4,
  },

  goToBottomButton: {
    width: 40,
    height: 40,
    color: "#fff",
    backgroundColor: "rgba(18, 140, 126, 0.92)",
    boxShadow: "0 2px 8px rgba(0,0,0,0.28)",
    "&:hover": {
      backgroundColor: "rgba(12, 109, 97, 0.95)",
    },
  },

  goToBottomIndicator: {
    position: "absolute",
    top: -3,
    right: -3,
    minWidth: 16,
    height: 16,
    borderRadius: 8,
    padding: "0 4px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 11,
    fontWeight: 700,
    lineHeight: 1,
    color: "#fff",
    backgroundColor: "#e53935",
    boxShadow: "0 1px 4px rgba(0,0,0,0.3)",
  },
}));

const mergeFavoriteValue = (incoming, previous) => {
  const isMissingValue = (value) => {
    if (value === undefined || value === null) {
      return true;
    }

    if (typeof value === "string" && value.trim() === "") {
      return true;
    }

    return false;
  };

  const mergedMessage = { ...incoming };

  if (isMissingValue(mergedMessage?.isFavorite) && !isMissingValue(previous?.isFavorite)) {
    mergedMessage.isFavorite = previous.isFavorite;
  }

  const fieldsToPreserve = [
    // Campo virtual calculado pelo backend na listagem/criação; os "update"
    // (ack, edição, reação) recarregam a mensagem sem ele.
    "sentByWhatsappId",
    "mediaUrl",
    "mediaType",
    "mediaName",
    "mediaSize",
    "mediaMimeType",
    "body",
    "dataJson",
    // Preserva a citação (reply) quando um update (ex.: ACK de grupo) chega sem ela,
    // evitando que a referência da mensagem respondida desapareça da tela.
    "quotedMsg",
    "quotedMsgId",
    // Transcrição de áudio: updates parciais (ex.: ACK) não devem apagá-la.
    "transcription",
    "transcriptionStatus"
  ];

  fieldsToPreserve.forEach((field) => {
    if (isMissingValue(mergedMessage?.[field]) && !isMissingValue(previous?.[field])) {
      mergedMessage[field] = previous[field];
    }
  });

  // Campos preservados apenas quando o update NEM MENCIONA a chave.
  //
  // Diferente do fieldsToPreserve acima: lá, `null` e string vazia contam como
  // "veio sem valor". Para o estado do download isso seria fatal — a transição
  // final manda `mediaDownloadStatus` para 'done'/null, e a regra do
  // isMissingValue restauraria o 'downloading' anterior, prendendo o spinner
  // para sempre (inclusive num refetch, porque LOAD_MESSAGES passa pelo mesmo
  // merge). Aqui um null explícito é INFORMAÇÃO, não ausência; só a chave
  // faltando de fato preserva o valor antigo.
  const fieldsToPreserveWhenAbsent = [
    "mediaDownloadStatus",
    "mediaDownloadError",
    "mediaThumbUrl"
  ];

  fieldsToPreserveWhenAbsent.forEach((field) => {
    const incomingHasField = Object.prototype.hasOwnProperty.call(
      incoming || {},
      field
    );
    const previousHasField = Object.prototype.hasOwnProperty.call(
      previous || {},
      field
    );
    if (!incomingHasField && previousHasField) {
      mergedMessage[field] = previous[field];
    }
  });

  // O include `ticket` (atendente, openedBy) alimenta o chip de abertura do
  // chamado. Update parcial (ACK, transcrição) chega sem ele; se o chamado é o
  // mesmo, mantém o objeto anterior em vez de degradar o rótulo. Um `ticket:
  // null` explícito do backend (LEFT JOIN sem linha) é informação e fica.
  const incomingHasTicket = Object.prototype.hasOwnProperty.call(incoming || {}, "ticket");
  const sameTicket =
    incoming?.ticketId === undefined || incoming?.ticketId === previous?.ticketId;
  if (!incomingHasTicket && sameTicket && previous?.ticket) {
    mergedMessage.ticket = previous.ticket;
  }

  return mergedMessage;
};

const getMessageSortTime = (message) => {
  const timestamp = Date.parse(message?.createdAt || "");
  if (Number.isFinite(timestamp)) {
    return timestamp;
  }

  return Number.MAX_SAFE_INTEGER;
};

const sortMessagesByCreatedAt = (messages) => {
  return [...messages].sort((left, right) => {
    const timeDiff = getMessageSortTime(left) - getMessageSortTime(right);
    if (timeDiff !== 0) {
      return timeDiff;
    }

    return String(left?.id || "").localeCompare(String(right?.id || ""));
  });
};

const getMessageKey = (value) => {
  if (value === undefined || value === null) {
    return null;
  }

  return String(value);
};

// Chaves onde uma mensagem otimista pode estar guardada, sem repetição e sem
// vazios. São duas porque no chat de contato a bolha nasce sob o pseudo-ticket
// e migra para o ticket real assim que o backend o cria.
const dedupeOptimisticKeys = (keys) => {
  const seen = new Set();
  return (keys || []).filter((key) => {
    if (key === undefined || key === null || key === "") return false;
    const normalized = String(key);
    if (seen.has(normalized)) return false;
    seen.add(normalized);
    return true;
  });
};

const findMessageIndexByIdentity = (messages, incomingMessage) => {
  const incomingOptimisticId = getMessageKey(incomingMessage?.optimisticId);
  if (incomingOptimisticId) {
    const optimisticIndex = messages.findIndex(
      (message) => getMessageKey(message?.optimisticId) === incomingOptimisticId
    );
    if (optimisticIndex !== -1) {
      return optimisticIndex;
    }
  }

  const incomingId = getMessageKey(incomingMessage?.id);
  if (!incomingId) {
    return -1;
  }

  return messages.findIndex((message) => getMessageKey(message?.id) === incomingId);
};

const reducer = (state, action) => {
  if (action.type === "LOAD_MESSAGES") {
    const incomingMessages = Array.isArray(action.payload) ? action.payload : [];

    if (incomingMessages.length === 0) {
      return state;
    }

    const stateById = new Map(
      state
        .filter((message) => message?.id !== undefined && message?.id !== null)
        .map((message) => [String(message.id), message])
    );
    const stateByOptimisticId = new Map(
      state
        .filter((message) => message?.optimisticId)
        .map((message) => [String(message.optimisticId), message])
    );
    const mergedMessages = [];
    const seenIds = new Set();
    const seenOptimisticIds = new Set();

    incomingMessages.forEach((message) => {
      const messageId = getMessageKey(message?.id);
      const optimisticId = getMessageKey(message?.optimisticId);

      if (!messageId && !optimisticId) {
        return;
      }

      const previousMessage =
        (optimisticId ? stateByOptimisticId.get(optimisticId) : undefined) ||
        (messageId ? stateById.get(messageId) : undefined);
      mergedMessages.push(mergeFavoriteValue(message, previousMessage));

      if (messageId) {
        seenIds.add(messageId);
      }
      if (optimisticId) {
        seenOptimisticIds.add(optimisticId);
      }
    });

    state.forEach((message) => {
      const messageId = getMessageKey(message?.id);
      const optimisticId = getMessageKey(message?.optimisticId);

      if (!messageId && !optimisticId) {
        mergedMessages.push(message);
        return;
      }

      const seenById = messageId ? seenIds.has(messageId) : false;
      const seenByOptimisticId = optimisticId ? seenOptimisticIds.has(optimisticId) : false;

      if (!seenById && !seenByOptimisticId) {
        mergedMessages.push(message);
      }
    });

    return sortMessagesByCreatedAt(mergedMessages);
  }

  if (action.type === "ADD_MESSAGE") {
    const newMessage = action.payload;
    const messageIndex = findMessageIndexByIdentity(state, newMessage);

    // Prevent duplicate messages
    if (messageIndex !== -1) {
      const updatedState = [...state];
      const previousMessage = updatedState[messageIndex];
      updatedState[messageIndex] = mergeFavoriteValue(newMessage, previousMessage);
      return updatedState;
    } else {
      return sortMessagesByCreatedAt([...state, newMessage]);
    }
  }

  if (action.type === "UPDATE_MESSAGE") {
    const messageToUpdate = action.payload;
    const messageIndex = findMessageIndexByIdentity(state, messageToUpdate);

    if (messageIndex !== -1) {
      const updatedState = [...state];
      const previousMessage = updatedState[messageIndex];
      updatedState[messageIndex] = mergeFavoriteValue(messageToUpdate, previousMessage);
      return sortMessagesByCreatedAt(updatedState);
    }

    // Alguns fluxos (principalmente mídia/ack) podem chegar primeiro como "update"
    // sem o evento "create". Nesse caso, fazemos upsert para não perder a mensagem.
    return sortMessagesByCreatedAt([...state, messageToUpdate]);
  }

  // Conexão excluída: as mensagens dos chamados dela viraram inativas e só
  // admin/super pode vê-las. Sem isto, o que já estava na tela ficava: o
  // LOAD_MESSAGES só mescla, então a resposta vazia do backend não limpava nada.
  if (action.type === "REMOVE_MESSAGES_BY_WHATSAPP") {
    const { whatsappId, ticketId: fallbackTicketId } = action.payload || {};
    const removedWhatsappId = Number(whatsappId);
    if (!removedWhatsappId) return state;

    const next = state.filter((message) => {
      if (message?.ticket?.whatsappId != null) {
        return Number(message.ticket.whatsappId) !== removedWhatsappId;
      }
      // Mensagem sem o ticket embutido: só sai se for do chamado em tela, que é
      // da conexão excluída.
      return !(fallbackTicketId && Number(message?.ticketId) === Number(fallbackTicketId));
    });

    return next.length === state.length ? state : next;
  }

  if (action.type === "RESET") {
    return [];
  }

  return state;
};

// Data/hora dos chips de abertura de chamado; o "às" vem do idioma.
const formatChipDateTime = (value) => {
  const date = safeFormatDate(value, "dd/MM/yyyy", "");
  const time = safeFormatDate(value, "HH:mm:ss", "");
  if (!date || !time) return i18n.t("messagesList.chips.unknownDate");
  return i18n.t("common.dateTimeAt", { date, time });
};

const MessagesList = ({
  ticket,
  ticketId,
  isGroup,
  contactId,
  jumpToMessageId,
  jumpToMessageTs,
  readOnly = false,
  allowedMessageIds = null,
  messageHistoryModeOverride = null,
  contactHistoryFilter = null,
  // Conexão de quem está olhando (ContactChat: a selecionada). Sem ela, a do chamado.
  viewerWhatsappId = null,
}) => {

    const history = useHistory();
    const { user, menus } = useContext(AuthContext); // Extraindo o usuário do contexto de autenticação
    // Modo "Por ticket" + permissão de histórico completo: o backend devolve o
    // histórico do contato inteiro, então a tela o trata como modo "contact".
    const hasFullHistoryPermission = canSeeFullHistory(user, menus);
    const [openAlert, setOpenAlert] = useState(false); // Estado para controlar o alerta
    const [userTicketOpen, setUserTicketOpen] = useState(""); // Estado para armazenar o nome do usuário do ticket
    const [queueTicketOpen, setQueueTicketOpen] = useState(""); // Estado para armazenar o nome da fila do ticket
    const [existingTicketData, setExistingTicketData] = useState(null); // Estado para armazenar dados do ticket existente
    const [openExistingTicketDialog, setOpenExistingTicketDialog] = useState(false); // Estado para controlar o diálogo de ticket existente
    const [groupParticipants, setGroupParticipants] = useState([]);
    const [selectedMentionParticipant, setSelectedMentionParticipant] = useState(null);
    const [openMentionDialog, setOpenMentionDialog] = useState(false);
    const [openSharedContactsDialog, setOpenSharedContactsDialog] = useState(false);
    const [pollVotingId, setPollVotingId] = useState(null); // Enquete com voto a caminho
    const pollVoteInFlightRef = useRef(false);
    const [selectedSharedContacts, setSelectedSharedContacts] = useState([]);
  

  const classes = useStyles();

  const [messagesList, dispatch] = useReducer(reducer, []);
  // Controla quais mensagens apagadas tiveram o conteúdo original revelado
  const [revealedDeleted, setRevealedDeleted] = useState({});
  const toggleRevealDeleted = useCallback((id) => {
    setRevealedDeleted((prev) => ({ ...prev, [id]: !prev[id] }));
  }, []);
  const isReadOnlyMode = Boolean(readOnly);
  const isInteractionEnabled = !isReadOnlyMode;
  const allowedMessageIdSet = useMemo(() => {
    if (!Array.isArray(allowedMessageIds) || allowedMessageIds.length === 0) {
      return null;
    }

    return new Set(
      allowedMessageIds
        .map((value) => String(value || "").trim())
        .filter(Boolean)
    );
  }, [allowedMessageIds]);

  const displayedMessages = useMemo(() => {
    if (!allowedMessageIdSet) {
      return messagesList;
    }

    return messagesList.filter((message) => allowedMessageIdSet.has(String(message?.id || "")));
  }, [messagesList, allowedMessageIdSet]);

  const [pageNumber, setPageNumber] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(false);
  const [messageHistoryMode, setMessageHistoryMode] = useState(undefined);
  // Setting groupTicketUnify: "disabled" = um chamado de grupo por conexão. Nesse
  // modo a mensagem é gravada uma vez e todo ticket do grupo exibe a conversa
  // inteira — o tempo real precisa aceitar mensagens do grupo (por contato) em
  // qualquer modo de histórico, como no modo "contact".
  const [groupTicketUnify, setGroupTicketUnify] = useState("enabled");
  const groupSharedHistory = Boolean(isGroup) && groupTicketUnify === "disabled";
  const [isNearBottom, setIsNearBottom] = useState(true);
  const [hasUnreadWhileAwayFromBottom, setHasUnreadWhileAwayFromBottom] = useState(false);
  const lastMessageRef = useRef();
  const messagesListRef = useRef(); // Referência para o container de mensagens
  const isNearBottomRef = useRef(true);
  const previousScrollHeightRef = useRef(0); // Para manter posição do scroll ao carregar mais
  const initialScrollDoneRef = useRef(false); // Flag para indicar se o scroll inicial já foi feito
  const isRestoringScrollRef = useRef(false);
  const ignoreScrollUntilRef = useRef(0);
  const historyLoadInFlightRef = useRef(false);
  // Gate para evitar fetch com pageNumber antigo logo após troca do filtro de histórico
  const contactFilterResetRef = useRef(false);
  const contactFilterKey = contactId
    ? `${contactHistoryFilter?.ticketId || ""}|${contactHistoryFilter?.whatsappId || ""}|${contactHistoryFilter?.viewAll ? "1" : "0"}`
    : "";
  // Lido pelo handler de socket (efeito com deps estáveis): sem ref ele veria o
  // filtro da montagem.
  const contactHistoryFilterRef = useRef(contactHistoryFilter);
  contactHistoryFilterRef.current = contactHistoryFilter;
  const historyScrollSnapshotRef = useRef({
    conversationRef: null,
    pageNumber: 0,
    scrollTop: 0,
    scrollHeight: 0,
  });
  const fullscreenScrollSnapshotRef = useRef({
    hasSnapshot: false,
    scrollTop: 0,
    scrollHeight: 0,
  });

  const [selectedMessage, setSelectedMessage] = useState(null);
  const [anchorEl, setAnchorEl] = useState(null);
  const [anchorPosition, setAnchorPosition] = useState(null);
  const messageOptionsMenuOpen = Boolean(selectedMessage && (anchorEl || anchorPosition));
  const [reactionAnchorEl, setReactionAnchorEl] = useState(null);
  const [reactionPaletteAnchorEl, setReactionPaletteAnchorEl] = useState(null);
  const reactionPickerRootRef = useRef(null);
  const [reactionMessage, setReactionMessage] = useState(null);
  const [reactionDetailsAnchorEl, setReactionDetailsAnchorEl] = useState(null);
  const [reactionDetailsMessage, setReactionDetailsMessage] = useState(null);
  const [reactionDetailsFilter, setReactionDetailsFilter] = useState("all");
  const currentTicketId = useRef(ticketId);
  // Lido pelo listener de socket (efeito sem `user` nas dependências).
  const currentUserIdRef = useRef(user?.id);
  currentUserIdRef.current = user?.id;
  const fallbackSyncTimerRef = useRef(null);
  const fallbackSyncRunIdRef = useRef(0);
  const [retryingMediaId, setRetryingMediaId] = useState(null);
  const [mediaUrlOverrides, setMediaUrlOverrides] = useState({});
  // Visualizador de mídias aberto: { initialItem, single } (ver openMediaViewer).
  const [mediaViewer, setMediaViewer] = useState(null);
  useEffect(() => {
    setMediaViewer(null);
  }, [ticketId, contactId]);
  const [mediaLoadErrors, setMediaLoadErrors] = useState({});
  // Relógio que faz um "baixando" órfão virar falha na tela sozinho. Um emit
  // perdido é indistinguível de um download lento do lado do cliente, e sem
  // isto o atendente ficaria com o spinner até recarregar a página. Só corre
  // enquanto existe mídia baixando — este componente não tinha nenhum
  // setInterval, e não vale pagar re-render em 100% dos tickets.
  const [, setMediaClockTick] = useState(0);

  const hasDownloadingMedia = useMemo(
    () => displayedMessages.some((m) => isMediaDownloading(m, m?.mediaUrl)),
    [displayedMessages]
  );

  useEffect(() => {
    if (!hasDownloadingMedia) return undefined;
    const id = setInterval(() => setMediaClockTick((t) => t + 1), 15000);
    return () => clearInterval(id);
  }, [hasDownloadingMedia]);
  const [highlightedMessageId, setHighlightedMessageId] = useState(null);
  const [jumpRequestSeq, setJumpRequestSeq] = useState(0);
  const jumpRequestRef = useRef({ id: null, token: null, done: false });
  const jumpNotFoundNotifiedRef = useRef(false);
  const jumpFetchInFlightRef = useRef(false);
  const jumpAroundFetchedRef = useRef(false);
  // Origem do salto quando ele nasce do clique numa citação: guarda o ticket da
  // mensagem citada para que, se ela estiver fora do escopo do histórico exibido
  // (modo 'ticket' com a citada vindo de um atendimento anterior), dê para abrir o
  // ticket de origem em vez de só avisar que não achou.
  const quotedJumpFallbackRef = useRef(null);
  // Cursor keyset do histórico: id da mensagem mais antiga já carregada. É enviado
  // no lugar do OFFSET para que a chegada de mensagens novas durante a rolagem não
  // desloque a janela e faça a paginação pular linhas (grupos ativos sofriam disso).
  const oldestLoadedIdRef = useRef(null);
  const isMountedRef = useRef(false);
  // Aborta, de uma vez, as requisições da conversa exibida (salto /around, sync
  // de fallback, settings) quando ela é trocada ou o componente desmonta. Sem
  // isso elas seguiam ocupando as poucas conexões HTTP/1.1 do navegador e o
  // ticket seguinte ficava na fila.
  const conversationAbortRef = useRef(null);
  const getConversationSignal = useCallback(() => {
    if (!conversationAbortRef.current || conversationAbortRef.current.signal.aborted) {
      conversationAbortRef.current = new AbortController();
    }
    return conversationAbortRef.current.signal;
  }, []);
  const loadedMessageIdsRef = useRef(new Set());
  const loadedOptimisticIdsRef = useRef(new Set());
  const senderFallbackLoggedRef = useRef(new Set());

  const jumpDebugEnabled = (() => {
    try {
      if (typeof window === "undefined") return false;
      const byStorage = window.localStorage?.getItem("jumpDebug") === "1";
      const byQuery = new URLSearchParams(window.location.search).get("jumpDebug") === "1";
      return byStorage || byQuery;
    } catch (_err) {
      return false;
    }
  })();

  const jumpDebugLog = (stage, payload = {}) => {
    if (!jumpDebugEnabled) return;
    console.log("[JumpDebug][MessagesList]", stage, {
      ticketId,
      contactId,
      pageNumber,
      hasMore,
      loading,
      listSize: messagesList.length,
      ...payload,
    });
  };

  useEffect(() => {
    const messageIds = new Set();
    const optimisticIds = new Set();

    messagesList.forEach((msg) => {
      const messageId = getMessageKey(msg?.id);
      const optimisticId = getMessageKey(msg?.optimisticId);

      if (messageId) {
        messageIds.add(messageId);
      }

      if (optimisticId) {
        optimisticIds.add(optimisticId);
      }
    });

    loadedMessageIdsRef.current = messageIds;
    loadedOptimisticIdsRef.current = optimisticIds;

    // Cursor do scroll de histórico: a mensagem mais antiga já carregada (a lista é
    // ordenada por data). Se por algum motivo o id não existir no banco, o backend
    // ignora o cursor e volta ao modo por página — nunca fica sem histórico.
    const oldestLoaded = messagesList.find((msg) => msg?.id);
    oldestLoadedIdRef.current = oldestLoaded?.id ? String(oldestLoaded.id) : null;
  }, [messagesList]);

  const socketManager = useContext(SocketContext);
  // Conexões da empresa com status/cor ao vivo: resolve o selo da bolha pela
  // conexão que ENVIOU a mensagem (`sentByWhatsappId`), não pela do ticket.
  const { whatsApps: companyWhatsApps } = useContext(WhatsAppsContext) || {};

  // Grupo "um chamado por conexão": mensagem enviada por outra conexão da
  // empresa é desenhada como de participante (ver groupConnectionPerspective).
  // Mesmo tamanho e ordem de displayedMessages: os índices continuam valendo.
  const viewerConnectionId = Number(viewerWhatsappId || ticket?.whatsappId) || 0;
  const renderedMessages = useMemo(() => {
    if (!groupSharedHistory || !viewerConnectionId) return displayedMessages;
    return displayedMessages.map((message) =>
      asSeenByConnection(message, viewerConnectionId, companyWhatsApps)
    );
  }, [displayedMessages, groupSharedHistory, viewerConnectionId, companyWhatsApps]);
  const { getPendingForTicket, removePendingMessageByOptimisticId } = useContext(PendingMessagesContext);
  const { getOptimisticMessages, removeOptimisticMessage, scrollTrigger } = useContext(OptimisticMessagesContext);
  const {
    selectedForwardMessages,
    isForwardSelectionMode,
    toggleForwardMessage,
    openForwardModal,
    clearForwardSelection
  } = useContext(ForwardMessageContext);

  const { setInstagramReplyTarget } = useContext(ReplyMessageContext);

  // Menu "Responder" de um comentário do Instagram (escolha público × DM).
  const [igReplyMenuAnchor, setIgReplyMenuAnchor] = useState(null);
  const [igReplyMenuMessage, setIgReplyMenuMessage] = useState(null);
  // Player do reel aberto, por message.id. O iframe só monta no clique: carregar
  // o Instagram para cada reel do histórico pesaria a conversa inteira.
  const [igReelPlayerOpen, setIgReelPlayerOpen] = useState({});

  const parseInstagramMeta = useCallback((message) => {
    try {
      const raw = message?.dataJson;
      if (!raw) return null;
      const data = typeof raw === "string" ? JSON.parse(raw) : raw;
      if (data?.source !== "instagram") return null;
      return data;
    } catch {
      return null;
    }
  }, []);

  const handleOpenIgReplyMenu = (event, message) => {
    event.stopPropagation();
    setIgReplyMenuAnchor(event.currentTarget);
    setIgReplyMenuMessage(message);
  };

  const handleCloseIgReplyMenu = () => {
    setIgReplyMenuAnchor(null);
    setIgReplyMenuMessage(null);
  };

  const handleChooseIgReplyMode = (mode) => {
    const meta = parseInstagramMeta(igReplyMenuMessage);
    const commentId = meta?.commentId || igReplyMenuMessage?.id;
    if (commentId && setInstagramReplyTarget) {
      setInstagramReplyTarget({
        commentId: String(commentId),
        mode,
        permalink: meta?.permalink || null,
        preview: String(igReplyMenuMessage?.body || "").slice(0, 120),
      });
    }
    handleCloseIgReplyMenu();
  };

  // Renderiza o selo "Comentário" + prévia do post + botão "Responder" para
  // mensagens originadas de comentário do Instagram.
  const renderInstagramCommentTag = (message) => {
    const meta = parseInstagramMeta(message);
    if (!meta || meta.channel !== "comment") return null;
    const permalink = meta.permalink;
    const isReceived = !message.fromMe;
    // A capa do post fica no storage do backend (a URL do CDN da Meta expira).
    const postThumbnail = meta.postThumbnail
      ? `${process.env.REACT_APP_BACKEND_URL}/public/${meta.postThumbnail}`
      : null;
    const postTooltip = meta.postCaption
      ? i18n.t("messagesList.instagram.postTooltip", { caption: String(meta.postCaption).slice(0, 180) })
      : i18n.t("messagesList.instagram.openCommentedPost");
    return (
      <div className={classes.igCommentTag}>
        {postThumbnail && (
          <Tooltip title={postTooltip} arrow placement="top">
            <a
              href={permalink || undefined}
              target="_blank"
              rel="noreferrer"
              className={classes.igPostThumb}
              onClick={(e) => {
                e.stopPropagation();
                if (!permalink) e.preventDefault();
              }}
            >
              <img src={postThumbnail} alt={i18n.t("messagesList.instagram.commentedPostAlt")} />
              {meta.postMediaType === "VIDEO" && (
                <PlayCircleFilled className={classes.igPostThumbBadge} />
              )}
            </a>
          </Tooltip>
        )}
        <Chip
          size="small"
          icon={<Comment style={{ fontSize: 13, color: "#fff" }} />}
          label={isReceived ? i18n.t("messagesList.instagram.comment") : i18n.t("messagesList.instagram.publicReply")}
          className={classes.igCommentChip}
        />
        {permalink && (
          <Link
            href={permalink}
            target="_blank"
            rel="noreferrer"
            className={classes.igPostLink}
            onClick={(e) => e.stopPropagation()}
          >
            <OpenInNew style={{ fontSize: 13 }} /> {i18n.t("messagesList.instagram.viewPost")}
          </Link>
        )}
        {isReceived && (
          <Button
            size="small"
            startIcon={<Reply style={{ fontSize: 14 }} />}
            className={classes.igReplyButton}
            onClick={(e) => handleOpenIgReplyMenu(e, message)}
          >
            {i18n.t("messagesList.instagram.reply")}
          </Button>
        )}
      </div>
    );
  };

  // Selo + card dos anexos de DM do Instagram (story, reel, post). A mídia com
  // arquivo continua saindo pelo bloco normal (renderMessageMediaBlock); aqui
  // fica o que o arquivo não diz: o que a mensagem é, a legenda, o player do
  // reel e o "Baixar mídia" do anexo que ficou só como link.
  const renderInstagramDmAttachment = (message) => {
    const attachment = getInstagramDmAttachment(message);
    if (!attachment) return null;
    const { kind, url, title, isPage, embedUrl } = attachment;
    const hasFile = !isPage && Boolean(getMediaUrl(message));
    const isStory = kind === "storyMention" || kind === "storyReply";
    const createdAtMs = message.createdAt ? new Date(message.createdAt).getTime() : NaN;
    const storyExpired =
      isStory && !hasFile && Number.isFinite(createdAtMs) && Date.now() - createdAtMs > IG_STORY_TTL_MS;
    const canDownload = isInteractionEnabled && !hasFile && !isPage && Boolean(url) && !storyExpired;
    const playerOpen = Boolean(igReelPlayerOpen[message.id]);
    const retrying = retryingMediaId === message.id;

    return (
      <div>
        <div className={classes.igCommentTag}>
          <Chip
            size="small"
            icon={<InstagramIcon style={{ fontSize: 13, color: "#fff" }} />}
            label={i18n.t(`messagesList.instagram.dm.${kind}`)}
            className={classes.igCommentChip}
          />
          {isPage && url && (
            <Link
              href={url}
              target="_blank"
              rel="noreferrer"
              className={classes.igPostLink}
              onClick={(e) => e.stopPropagation()}
            >
              <OpenInNew style={{ fontSize: 13 }} /> {i18n.t("messagesList.instagram.dm.openInInstagram")}
            </Link>
          )}
        </div>
        {title && <div className={classes.igDmCaption}>{title}</div>}
        {embedUrl && playerOpen && (
          <div className={classes.igReelFrame}>
            <iframe
              src={embedUrl}
              title={i18n.t("messagesList.instagram.dm.reelPlayerTitle")}
              className={classes.igReelEmbed}
              scrolling="no"
              allow="autoplay; encrypted-media; picture-in-picture"
              allowFullScreen
              loading="lazy"
            />
          </div>
        )}
        {(embedUrl || canDownload) && (
          <div className={classes.igDmActions}>
            {embedUrl && (
              <Button
                size="small"
                startIcon={<PlayCircleFilled style={{ fontSize: 14 }} />}
                className={classes.igReplyButton}
                onClick={(e) => {
                  e.stopPropagation();
                  setIgReelPlayerOpen((prev) => ({ ...prev, [message.id]: !prev[message.id] }));
                }}
              >
                {playerOpen
                  ? i18n.t("messagesList.instagram.dm.closePlayer")
                  : i18n.t("messagesList.instagram.dm.watchHere")}
              </Button>
            )}
            {canDownload && (
              <Button
                size="small"
                startIcon={retrying ? <CircularProgress size={12} /> : <GetApp style={{ fontSize: 14 }} />}
                className={classes.igReplyButton}
                disabled={retrying}
                onClick={(e) => {
                  e.stopPropagation();
                  handleRetryMedia(message);
                }}
              >
                {i18n.t("messagesList.instagram.dm.downloadMedia")}
              </Button>
            )}
          </div>
        )}
        {storyExpired && (
          <div className={classes.igDmNotice}>{i18n.t("messagesList.instagram.dm.storyExpired")}</div>
        )}
      </div>
    );
  };

  const selectedForwardMessageIds = useMemo(
    () => new Set(selectedForwardMessages.map((message) => String(message?.id)).filter(Boolean)),
    [selectedForwardMessages]
  );

  const isMessageForwardSelected = useCallback(
    (message) => selectedForwardMessageIds.has(String(message?.id)),
    [selectedForwardMessageIds]
  );

  const handleToggleForwardSelection = useCallback((event, message) => {
    if (!isInteractionEnabled) {
      return;
    }

    event?.stopPropagation?.();
    event?.preventDefault?.();
    toggleForwardMessage(message);
  }, [isInteractionEnabled, toggleForwardMessage]);

  const renderForwardSelectableBubble = useCallback((message, content) => {
    if (!isInteractionEnabled || !isForwardSelectionMode) {
      return content;
    }

    const isFromMe = Boolean(message?.fromMe);

    return (
      <div
        className={clsx(classes.forwardSelectableRow, {
          [classes.forwardSelectableRowRight]: isFromMe,
        })}
      >
        {isFromMe ? (
          <>
            <div className={clsx(classes.forwardSelectableContent, classes.forwardSelectableContentRight)}>{content}</div>
            <Checkbox
              color="primary"
              checked={isMessageForwardSelected(message)}
              onChange={(event) => handleToggleForwardSelection(event, message)}
            />
          </>
        ) : (
          <>
            <Checkbox
              color="primary"
              checked={isMessageForwardSelected(message)}
              onChange={(event) => handleToggleForwardSelection(event, message)}
            />
            <div className={classes.forwardSelectableContent}>{content}</div>
          </>
        )}
      </div>
    );
  }, [
    classes.forwardSelectableContent,
    classes.forwardSelectableContentRight,
    classes.forwardSelectableRow,
    classes.forwardSelectableRowRight,
    handleToggleForwardSelection,
    isInteractionEnabled,
    isForwardSelectionMode,
    isMessageForwardSelected,
  ]);

  const getActiveConversationRef = useCallback(() => {
    if (contactId) {
      return `contact-${contactId}`;
    }

    return ticketId || ticket?.id || null;
  }, [contactId, ticketId, ticket?.id]);

  const getActiveOptimisticKey = useCallback(() => {
    return ticketId || ticket?.id || null;
  }, [ticketId, ticket?.id]);

  const clearFallbackSyncTimer = useCallback(() => {
    if (fallbackSyncTimerRef.current) {
      clearTimeout(fallbackSyncTimerRef.current);
      fallbackSyncTimerRef.current = null;
    }
  }, []);

  const runFallbackSync = useCallback(async () => {
    const conversationAtStart = getActiveConversationRef();
    const activeOptimisticKey = getActiveOptimisticKey();

    if (!conversationAtStart || (!activeOptimisticKey && !contactId)) {
      return false;
    }

    try {
      let data;

      if (contactId) {
        const response = await api.get(`/messages/contact/${contactId}`, {
          params: { pageNumber: 1 },
          signal: getConversationSignal(),
        });
        data = response.data;
      } else {
        const response = await api.get(`/messages/${ticketId}`, {
          params: { pageNumber: 1 },
          signal: getConversationSignal(),
        });
        data = response.data;
      }

      const stillSameConversation = currentTicketId.current === conversationAtStart;
      if (!stillSameConversation || !isMountedRef.current) {
        return false;
      }

      dispatch({ type: "LOAD_MESSAGES", payload: data.messages });

      const serverOptimisticIds = (data.messages || [])
        .map((message) => message?.optimisticId)
        .filter(Boolean);

      if (serverOptimisticIds.length > 0 && activeOptimisticKey) {
        serverOptimisticIds.forEach((optimisticId) => {
          removeOptimisticMessage(activeOptimisticKey, optimisticId);
        });
      }

      return true;
    } catch (_error) {
      return false;
    }
  }, [contactId, getActiveConversationRef, getActiveOptimisticKey, getConversationSignal, removeOptimisticMessage, ticketId]);

  const scheduleOptimisticFallbackSync = useCallback((attempt = 0) => {
    clearFallbackSyncTimer();

    const activeOptimisticKey = getActiveOptimisticKey();
    if (!activeOptimisticKey) {
      return;
    }

    const pendingOptimistic = getOptimisticMessages(activeOptimisticKey) || [];
    const pendingCount = pendingOptimistic.length;
    if (pendingCount <= 0) {
      return;
    }

    const conversationAtSchedule = currentTicketId.current;
    const runId = ++fallbackSyncRunIdRef.current;
    const baseDelay = OPTIMISTIC_FALLBACK_BASE_DELAY_MS + Math.min(pendingCount, 5) * 300;
    const syncDelay = Math.min(
      Math.round(baseDelay * Math.pow(1.7, attempt)),
      OPTIMISTIC_FALLBACK_MAX_DELAY_MS
    );

    fallbackSyncTimerRef.current = setTimeout(async () => {
      const isCurrentRun = fallbackSyncRunIdRef.current === runId;
      if (!isCurrentRun || !isMountedRef.current) {
        return;
      }

      await runFallbackSync();

      const stillSameConversation = currentTicketId.current === conversationAtSchedule;
      if (!stillSameConversation || !isMountedRef.current) {
        return;
      }

      const stillPending = getOptimisticMessages(activeOptimisticKey) || [];
      if (stillPending.length > 0 && attempt + 1 < OPTIMISTIC_FALLBACK_MAX_RETRIES) {
        scheduleOptimisticFallbackSync(attempt + 1);
      }
    }, syncDelay);
  }, [clearFallbackSyncTimer, getActiveOptimisticKey, getOptimisticMessages, runFallbackSync]);

  // Autor: Claude | Data: 05/08/2026 | Motivo: incidente das 16:26 BRT.
  // `getOptimisticMessages` depende do state `optimisticMessages`, então troca de
  // identidade a cada mensagem otimista adicionada/removida. Isso se propagava
  // para `scheduleOptimisticFallbackSync` e derrubava/remontava o efeito do
  // socket, que a cada execução reemitia "joinChatBox" — uma consulta ao banco
  // por emit. Guardar os callbacks aqui e lê-los na hora da chamada mantém o
  // efeito preso só à identidade da sala (ticket/contato).
  const socketCallbacksRef = useRef({});
  socketCallbacksRef.current = {
    scheduleOptimisticFallbackSync,
    removeOptimisticMessage,
    getActiveOptimisticKey,
    runFallbackSync,
    removePendingMessageByOptimisticId,
    isAdmin: isAdminUser(user),
    ticket
  };

  useEffect(() => {
    currentTicketId.current = getActiveConversationRef();
  }, [getActiveConversationRef]);

  useEffect(() => {
    const getFullscreenElement = () => (
      document.fullscreenElement
      || document.webkitFullscreenElement
      || document.mozFullScreenElement
      || document.msFullscreenElement
    );

    const handleFullscreenChange = () => {
      const container = messagesListRef.current;
      if (!container) {
        return;
      }

      const fullscreenElement = getFullscreenElement();
      const isFullscreenMessageVideo = Boolean(
        fullscreenElement
        && fullscreenElement.tagName === "VIDEO"
        && fullscreenElement.classList
        && fullscreenElement.classList.contains(classes.messageMedia)
      );

      if (isFullscreenMessageVideo) {
        fullscreenScrollSnapshotRef.current = {
          hasSnapshot: true,
          scrollTop: container.scrollTop,
          scrollHeight: container.scrollHeight,
        };
        return;
      }

      const snapshot = fullscreenScrollSnapshotRef.current;
      if (!snapshot.hasSnapshot) {
        return;
      }

      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          const currentContainer = messagesListRef.current;
          if (!currentContainer) {
            return;
          }

          const heightDelta = currentContainer.scrollHeight - snapshot.scrollHeight;
          currentContainer.scrollTop = Math.max(0, snapshot.scrollTop + heightDelta);
        });
      });

      fullscreenScrollSnapshotRef.current = {
        hasSnapshot: false,
        scrollTop: 0,
        scrollHeight: 0,
      };
    };

    document.addEventListener("fullscreenchange", handleFullscreenChange);
    document.addEventListener("webkitfullscreenchange", handleFullscreenChange);
    document.addEventListener("mozfullscreenchange", handleFullscreenChange);
    document.addEventListener("MSFullscreenChange", handleFullscreenChange);

    return () => {
      document.removeEventListener("fullscreenchange", handleFullscreenChange);
      document.removeEventListener("webkitfullscreenchange", handleFullscreenChange);
      document.removeEventListener("mozfullscreenchange", handleFullscreenChange);
      document.removeEventListener("MSFullscreenChange", handleFullscreenChange);
    };
  }, [classes.messageMedia]);

  useEffect(() => {
    isMountedRef.current = true;

    return () => {
      isMountedRef.current = false;
      clearFallbackSyncTimer();
      conversationAbortRef.current?.abort();
    };
  }, [clearFallbackSyncTimer]);

  const getMediaFilenameFromUrl = (mediaUrl) => {
    return extractMediaFilename(mediaUrl);
  };

  const getFileExtension = (filename = "") => {
    const normalized = filename.toLowerCase();
    const dotIndex = normalized.lastIndexOf(".");
    if (dotIndex < 0) return "";
    return normalized.slice(dotIndex + 1);
  };

  const formatFileSize = (rawSize) => {
    const size = Number(rawSize);
    if (!Number.isFinite(size) || size <= 0) return "";
    if (size < 1024) return `${size} B`;

    const units = ["kB", "MB", "GB"];
    let value = size / 1024;
    let unitIndex = 0;

    while (value >= 1024 && unitIndex < units.length - 1) {
      value /= 1024;
      unitIndex += 1;
    }

    const fixed = value >= 10 ? value.toFixed(0) : value.toFixed(1);
    return `${fixed} ${units[unitIndex]}`;
  };

  const getDocumentMessagePayload = (message) => {
    const data = getMessageJsonData(message);
    return (
      data?.message?.documentMessage ||
      data?.message?.documentWithCaptionMessage?.message?.documentMessage ||
      data?.documentMessage ||
      data?.documentWithCaptionMessage?.message?.documentMessage ||
      {}
    );
  };

  const getDocumentDescriptor = (message, mediaUrlOverride) => {
    const mediaUrl = mediaUrlOverride || getMediaUrl(message);
    const documentPayload = getDocumentMessagePayload(message);
    const filename =
      message?.mediaName ||
      documentPayload?.fileName ||
      getMediaFilenameFromUrl(mediaUrl || "") ||
      i18n.t("messagesList.documentCard.label");

    const extension = getFileExtension(filename);
    const mimeType =
      documentPayload?.mimetype ||
      getMessageJsonData(message)?.mimetype ||
      message?.mediaMimeType ||
      "";

    const typeLabel = extension
      ? extension.toUpperCase()
      : (String(mimeType).split("/")[1] || "DOC").toUpperCase();

    const fileSize = formatFileSize(
      documentPayload?.fileLength ||
      documentPayload?.fileSize ||
      message?.mediaSize
    );

    return {
      mediaUrl,
      filename,
      typeLabel,
      fileSize,
    };
  };

  const renderDocumentCard = ({
    mediaUrl,
    filename,
    typeLabel,
    fileSize,
    compact = false,
    showActions = true,
    showOpenAction = true,
    // "downloading" | "failed" | null. Nome, tipo e tamanho vêm do dataJson e
    // já são conhecidos na criação da mensagem, então o card sai COMPLETO e
    // correto durante o download — só a linha de botões troca de conteúdo, o
    // que mantém a altura e elimina o salto de layout.
    status = null,
    onRetry,
    retrying = false,
  }) => {
    const metaParts = [typeLabel, fileSize].filter(Boolean);

    return (
      <div className={clsx(classes.documentCard, { [classes.documentCardCompact]: compact })}>
        <div className={classes.documentCardHeader}>
          <div className={classes.documentCardIcon}>
            <InsertDriveFile />
          </div>
          <div className={classes.documentCardInfo}>
            <span className={classes.documentCardFilename} title={filename}>{filename}</span>
            <span className={classes.documentCardMeta}>{metaParts.join(" · ") || i18n.t("messagesList.documentCard.label")}</span>
          </div>
        </div>

        {status === "downloading" && (
          <div className={classes.documentCardActions}>
            <LinearProgress className={classes.mediaAudioPlaceholderTrack} />
            <span className={classes.documentCardMeta}>
              {i18n.t("messagesList.media.downloadingShort")}
            </span>
          </div>
        )}

        {status === "failed" && showActions && (
          <div className={classes.documentCardActions}>
            <Button
              className={classes.documentCardButton}
              color="primary"
              variant="outlined"
              startIcon={retrying ? <CircularProgress size={16} /> : <Refresh />}
              disabled={retrying}
              onClick={onRetry}
            >
              {i18n.t("messagesList.media.retry")}
            </Button>
          </div>
        )}

        {!status && showActions && (
          <div className={classes.documentCardActions}>
            {showOpenAction && (
              <Button
                className={classes.documentCardButton}
                color="primary"
                variant="contained"
                target="_blank"
                rel="noopener noreferrer"
                href={mediaUrl}
              >
                {i18n.t("messagesList.documentCard.open")}
              </Button>
            )}
            <Button
              className={classes.documentCardButton}
              color="primary"
              variant="contained"
              onClick={(event) => handleForceDownload(event, mediaUrl, filename)}
            >
              {i18n.t("messagesList.documentCard.download")}
            </Button>
          </div>
        )}
      </div>
    );
  };
  const PDF_EXTENSIONS = ["pdf"];
  const TEXT_PREVIEW_EXTENSIONS = ["txt", "log", "csv", "json", "md", "html", "htm"];
  // Autor: GitHub Copilot | Data: 28/03/2026 | Motivo: arquivos OFX devem baixar direto sem abrir nova aba.
  // Data: 06/08/2026 | Motivo: remessa bancaria .rem e ASCII puro e o servidor nao tem mimetype para ela;
  // sem Content-Type o navegador fareja o conteudo e renderiza o arquivo na aba em vez de salvar.
  const DIRECT_DOWNLOAD_EXTENSIONS = ["ofx", "rem"];

  // Wrappers locais dos predicados de utils/mediaState: injetam o getMediaUrl
  // do componente (que respeita mediaUrlOverrides) para que TODO ponto da tela
  // decida com base na mesma URL. Antes, os gates das bolhas olhavam
  // `message.mediaUrl` cru enquanto o checkMessageMedia olhava o override — uma
  // divergência que já existia e que a fatoração elimina.
  const hasEmptyMediaSlot = (message) =>
    isMediaSlotEmpty(message, getMediaUrl(message));
  const isMediaDownloadingFor = (message) =>
    isMediaDownloading(message, getMediaUrl(message));
  const isMediaFailedFor = (message) =>
    isMediaFailed(message, getMediaUrl(message));

  // Espelha a condição do bloco de mídia da bolha. Quando a mensagem tem mídia,
  // a citação sobe para cima dela (igual ao WhatsApp Web) em vez de ficar
  // espremida embaixo, junto da legenda.
  const hasMediaBubble = (message) =>
    (!message.isDeleted || revealedDeleted[message.id]) &&
    // Reel do Instagram nunca tem arquivo (o payload.url é a página); o .bin
    // antigo com o HTML dela desenharia um vídeo quebrado.
    !getInstagramDmAttachment(message)?.isPage &&
    Boolean(
      message.mediaUrl
      || message.mediaType === "locationMessage"
      || message.mediaType === "liveLocationMessage"
      || message.mediaType === "vcard"
      || hasEmptyMediaSlot(message)
    );

  // Envelope de mensagem interativa (botões, lista, template, carrossel, enquete
  // ou resposta a botão), gravado pelo backend em dataJson.interactive.
  // Ver backend/src/utils/interactiveContent.ts.
  const getInteractiveContent = (message) => {
    const interactive = getMessageJsonData(message)?.interactive;
    return interactive && typeof interactive === "object" ? interactive : null;
  };

  const isStickerMessage = (message) => {
    if (!message) return false;

    if (message.mediaType === "sticker" || message.mediaType === "stickerMessage") {
      return true;
    }

    // Instagram não manda figurinha: o post chega em .webp e o farejamento
    // abaixo o desenharia pequeno e sem bolha.
    if (getInstagramData(message)) {
      return false;
    }

    // Interativa nunca é figurinha — o farejamento por string do dataJson abaixo
    // acharia o "image/webp" de um thumbnail e aplicaria o estilo de sticker.
    if (getInteractiveContent(message)) {
      return false;
    }

    const body = String(message.body || "").trim().toLowerCase();
    if (body === "sticker" || body.endsWith(": sticker") || body.endsWith(" sticker")) {
      return true;
    }

    const mediaUrl = getMediaUrl(message);
    const filename = getMediaFilenameFromUrl(mediaUrl || "").toLowerCase();
    if (filename.endsWith(".webp")) {
      return true;
    }

    const dataJsonRaw = message.dataJson;
    const dataJsonText = typeof dataJsonRaw === "string" ? dataJsonRaw : JSON.stringify(dataJsonRaw || {});
    if (
      dataJsonText.includes("stickerMessage") ||
      dataJsonText.includes("image/webp") ||
      dataJsonText.includes("mimetype\":\"image/webp")
    ) {
      return true;
    }

    return false;
  };

  const shouldShowBodyForMessage = (message) => {
    if (message.mediaType === "locationMessage" || message.mediaType === "liveLocationMessage") return false;
    if (isStickerMessage(message)) return false;
    // O InteractiveMessageContent já desenha o texto do envelope — mostrar o body
    // aqui duplicaria a mensagem na bolha.
    if (getInteractiveContent(message)) return false;
    const body = (message.body || "").trim();
    if (!body) return false;
    // Anexo do Instagram sem arquivo: o body é só a URL, e o card a substitui.
    if (getInstagramDmAttachment(message) && !stripInstagramAttachmentUrl(message, body)) return false;
    // Mídia sem arquivo agora guarda a LEGENDA REAL, e ela deve aparecer já
    // durante o download (como no WhatsApp Web). Só continua escondido o
    // placeholder textual que o backend antigo gravava no lugar da legenda —
    // ver LEGACY_MEDIA_PLACEHOLDER_BODY.
    if (hasEmptyMediaSlot(message) && LEGACY_MEDIA_PLACEHOLDER_BODY.test(body)) {
      return false;
    }
    // O nome do arquivo já aparece no card do documento; repetir no corpo
    // duplicaria a informação. Vale também durante o download, quando ainda não
    // há mediaUrl mas o mediaName já é conhecido.
    if (message.mediaUrl || message.mediaName) {
      const mediaName = (message.mediaName || "").trim();
      const filename = message.mediaUrl
        ? getMediaFilenameFromUrl(message.mediaUrl)
        : "";
      if (body === mediaName || (filename && body === filename)) return false;
    }
    return true;
  };

  const getDisplayBody = (message) => {
    if (!shouldShowBodyForMessage(message)) return "";
    if (getInstagramDmAttachment(message)) return stripInstagramAttachmentUrl(message, message.body);
    // Enviada (com assinatura) por outra conexão: mesmo formato da bolha
    // enviada, que é o que o grupo vê no WhatsApp.
    if (message.sentByOtherConnection) {
      return message.signedMessage && hasSenderSign(message)
        ? `*${message.sign}:*\n${message.body}`
        : message.body;
    }
    if (message.signedMessage) {
      return (message.sign == "false" ? "" : message.sign) + message.body;
    }
    return message.body;
  };

  // Só os tipos que desenham um bloco visual de ponta a ponta na bolha: são eles
  // que ficam com o relógio por cima quando não há legenda. Áudio, documento e
  // interativa ficam de fora porque têm predicado próprio para o mesmo
  // tratamento (isAudioBubbleMessage, isDocumentCardMessage,
  // isInteractiveCardMessage).
  const MEDIA_ONLY_CLOCK_TYPES = ["image", "video", "locationMessage", "liveLocationMessage"];

  const isMediaWithoutCaption = (message) =>
    hasMediaBubble(message)
    && MEDIA_ONLY_CLOCK_TYPES.includes(message?.mediaType)
    && !shouldShowBodyForMessage(message)
    // Figurinha já tira o relógio do absoluto pelo timestampSticker.
    && !isStickerMessage(message)
    && !isInteractiveCardMessage(message);

  // Bolhas em que checkMessageMedia desenha o card de documento ou o preview em
  // iframe. Ver messageDocumentCard: nelas o relogio sai do absoluto.
  // Enquete/botões/lista/template/carrossel ocupam a bolha inteira, como o card de
  // documento. A resposta de botão é um chip curto e fica no fluxo normal.
  const isInteractiveCardMessage = (message) => {
    const interactive = getInteractiveContent(message);
    return Boolean(interactive) && interactive.kind !== "reply";
  };

  // Aceita também o slot vazio (mídia baixando ou falhada). NÃO é cosmético:
  // esta função decide a classe messageDocumentCard, que tira o relógio do
  // position:absolute. Sem ela valer durante o download, a bolha cresceria e o
  // relógio saltaria no exato instante em que o arquivo chega.
  const isDocumentCardMessage = (message) => {
    const isDocType = ["document", "application"].includes(
      String(message?.mediaType || "").toLowerCase()
    );
    return isDocType && (Boolean(getMediaUrl(message)) || hasEmptyMediaSlot(message));
  };

  // Ver messageAudioCard: o relogio sai do absoluto para nao cair sobre a
  // transcricao nem sobre o player.
  // Mesmo motivo do isDocumentCardMessage: manter a classe estável entre o
  // placeholder e o player evita o salto de layout na troca.
  const isAudioBubbleMessage = (message) => {
    const isAudio = String(message?.mediaType || "").toLowerCase() === "audio";
    return isAudio && (Boolean(getMediaUrl(message)) || hasEmptyMediaSlot(message));
  };

  // Mensagens de email não passam pelo pipeline de markdown do WhatsApp:
  // corpos grandes/HTML estouram a pilha do parser (too much recursion).
  // Detecção por mensagem (dataJson.provider) cobre o ContactChat, cujo
  // pseudo-ticket não tem whatsapp.type; o fallback por ticket cobre a
  // página de Ticket (ShowTicketService inclui whatsapp.type).
  const isEmailMessage = (message) => {
    try {
      const data = typeof message?.dataJson === "string"
        ? JSON.parse(message.dataJson)
        : message?.dataJson;
      if (data?.provider === "email") return true;
    } catch (err) {
      // dataJson inválido — segue para o fallback por ticket
    }
    return ticket?.whatsapp?.type === "email";
  };

  const EMAIL_URL_REGEX = /(https?:\/\/[^\s<>"')\]]+)/g;

  const getEmailDataJson = (message) => {
    try {
      return typeof message?.dataJson === "string"
        ? JSON.parse(message.dataJson)
        : message?.dataJson;
    } catch (err) {
      return null;
    }
  };

  const renderEmailBody = (bodyText, message = null) => {
    // Render fiel: HTML original do email, sanitizado (emails novos gravam
    // emailHtml; antigos/sem parte HTML caem no texto plano abaixo).
    const emailHtml = message?.emailHtml;
    if (emailHtml && emailHtml.length <= MAX_EMAIL_HTML_LENGTH) {
      const subject = getEmailDataJson(message)?.subject;
      return <EmailHtmlBody html={emailHtml} subject={subject} />;
    }

    const parts = String(bodyText || "").split(EMAIL_URL_REGEX);
    return (
      <span
        translate="no"
        style={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}
      >
        {parts.map((part, i) =>
          /^https?:\/\//.test(part) ? (
            <a key={i} href={part} target="_blank" rel="noopener noreferrer">
              {part}
            </a>
          ) : (
            part
          )
        )}
      </span>
    );
  };

  const isLidLikeValue = (value) => {
    const rawValue = String(value || "").trim();
    if (!rawValue) return false;

    const normalized = rawValue.toLowerCase();
    if (normalized.includes("@lid")) return true;

    const localPart = normalized.split("@")[0].trim();
    return /^\d{14,}(?::\d+)?$/.test(localPart);
  };

  const getMentionDisplayName = (participant) => {
    const nameCandidates = [
      participant?.displayName,
      participant?.participantName,
      participant?.name,
      participant?.pushName,
    ];

    for (const candidate of nameCandidates) {
      const cleanCandidate = String(candidate || "").trim();
      if (!cleanCandidate) continue;
      if (isLidLikeValue(cleanCandidate)) continue;
      return cleanCandidate;
    }

    return i18n.t("messagesList.contactFallback");
  };

  const buildMentionSegments = (bodyText) => {
    if (!isGroup || !bodyText || !Array.isArray(groupParticipants) || groupParticipants.length === 0) {
      return [{ type: "text", value: bodyText || "" }];
    }

    const candidates = [];
    groupParticipants.forEach((participant) => {
      const displayName = String(participant?.displayName || "").trim();
      const participantNumber = String(participant?.participantNumber || "").replace(/\D/g, "");
      const participantJidLocal = String(participant?.participantJid || "").split("@")[0].split(":")[0].trim();

      if (displayName) {
        candidates.push({
          label: `@${displayName}`,
          participant,
        });
      }

      if (participantNumber) {
        candidates.push({
          label: `@${participantNumber}`,
          participant,
        });
      }

      if (participantJidLocal) {
        candidates.push({
          label: `@${participantJidLocal}`,
          participant,
        });
      }
    });

    if (candidates.length === 0) {
      return [{ type: "text", value: bodyText }];
    }

    candidates.sort((a, b) => b.label.length - a.label.length);

    const textLower = bodyText.toLowerCase();
    const segments = [];
    let cursor = 0;

    while (cursor < bodyText.length) {
      let nextMatch = null;

      for (const candidate of candidates) {
        const idx = textLower.indexOf(candidate.label.toLowerCase(), cursor);
        if (idx === -1) continue;

        if (!nextMatch || idx < nextMatch.index) {
          nextMatch = {
            index: idx,
            label: candidate.label,
            participant: candidate.participant,
          };
        }
      }

      if (!nextMatch) {
        segments.push({ type: "text", value: bodyText.slice(cursor) });
        break;
      }

      if (nextMatch.index > cursor) {
        segments.push({ type: "text", value: bodyText.slice(cursor, nextMatch.index) });
      }

      segments.push({
        type: "mention",
        value: `@${getMentionDisplayName(nextMatch.participant)}`,
        participant: nextMatch.participant,
      });

      cursor = nextMatch.index + nextMatch.label.length;
    }

    return segments;
  };

  const handleMentionClick = (participant) => {
    if (!isInteractionEnabled) return;
    if (!participant) return;
    setSelectedMentionParticipant(participant);
    setOpenMentionDialog(true);
  };

  const handleCloseMentionDialog = () => {
    setOpenMentionDialog(false);
    setSelectedMentionParticipant(null);
  };

  const handleStartDirectConversationFromMention = async () => {
    if (!isInteractionEnabled) return;
    const mention = selectedMentionParticipant;
    if (!mention) return;

    const mentionName = getMentionDisplayName(mention);
    const mentionNumber = String(mention.participantNumber || "").replace(/\D/g, "");

    if (!mentionNumber || mentionNumber.length < 8) {
      toastError(i18n.t("messagesList.mention.noNumber"));
      return;
    }

    try {
      let contact = null;

      try {
        const { data } = await api.get(`/contacts/bynumber/${mentionNumber}`);
        if (data?.contact) {
          contact = data.contact;
        }
      } catch (err) {
        if (err?.response?.status !== 404) {
          throw err;
        }
      }

      if (!contact) {
        const normalizedNumber = mentionNumber.replace(/^\d{4}(9)(\d{8})$/, (m, p1, p2) => m.replace(p1, ""));
        if (normalizedNumber && normalizedNumber !== mentionNumber) {
          try {
            const { data } = await api.get(`/contacts/bynumber/${normalizedNumber}`);
            if (data?.contact) {
              contact = data.contact;
            }
          } catch (err) {
            if (err?.response?.status !== 404) {
              throw err;
            }
          }
        }
      }

      if (!contact) {
        const { data: newContact } = await api.post("/contacts", {
          name: mentionName,
          number: mentionNumber,
        });
        contact = newContact;
      }

      const queueId = ticket?.queueId;
      // Chamado de conexão excluída: a conexão dele não serve mais de destino, o
      // backend escolhe a conexão do novo chamado.
      const whatsappId = isInactiveTicket(ticket) ? undefined : ticket?.whatsappId;

      const { data: newTicket } = await api.post("/tickets", {
        contactId: contact.id,
        queueId,
        userId: user.id,
        status: "open",
        whatsappId,
      });

      if (newTicket?.uuid) {
        history.push(`/tickets/${newTicket.uuid}`);
      }

      handleCloseMentionDialog();
    } catch (err) {
      const code = err?.response?.data?.error;

      if (code === "ERR_OTHER_OPEN_TICKET") {
        let ticketData = null;
        try {
          ticketData = JSON.parse(err?.response?.data?.details || "{}");
        } catch (_parseErr) {
          ticketData = null;
        }

        setExistingTicketData(ticketData);
        setOpenExistingTicketDialog(true);
        handleCloseMentionDialog();
        return;
      }

      toastError(err);
    }
  };

  const renderBodyWithMentions = (bodyText, message = null) => {
    if (message && isEmailMessage(message)) {
      return renderEmailBody(bodyText, message);
    }
    const segments = buildMentionSegments(bodyText);
    const hasMentions = segments.some((segment) => segment.type === "mention");

    if (!hasMentions) {
      return <MarkdownWrapper codeBlocks>{bodyText}</MarkdownWrapper>;
    }

    const renderTextSegment = (value, segmentIndex) => {
      const lines = String(value || "").split("\n");

      return lines.map((line, lineIndex) => (
        <React.Fragment key={`text-${segmentIndex}-line-${lineIndex}`}>
          {line ? <MarkdownWrapper>{line}</MarkdownWrapper> : null}
          {lineIndex < lines.length - 1 ? <br /> : null}
        </React.Fragment>
      ));
    };

    return (
      <span style={{ whiteSpace: "pre-wrap" }}>
        {segments.map((segment, index) => {
          if (segment.type !== "mention") {
            return <React.Fragment key={`text-${index}`}>{renderTextSegment(segment.value, index)}</React.Fragment>;
          }

          return (
            <span
              key={`mention-${index}`}
              className={isInteractionEnabled ? classes.mentionClickable : undefined}
              onClick={isInteractionEnabled ? () => handleMentionClick(segment.participant) : undefined}
            >
              {segment.value}
            </span>
          );
        })}
      </span>
    );
  };

  const getMediaUrl = (message) => normalizeMediaUrl(mediaUrlOverrides[message.id] || message.mediaUrl);

  const removeNinthDigitIfNeeded = (number) => {
    const cleaned = String(number || "").replace(/\D/g, "");
    // Variante sem o nono dígito só faz sentido para número brasileiro
    if (!isBrazilianNumber(cleaned)) return cleaned;
    return cleaned.replace(/^(\d{2})(\d{2})(9)(\d{4})(\d{4})$/, "$1$2$4$5");
  };

  const extractNameFromVcard = (vcard) => {
    const rawVcard = String(vcard || "");
    const match = rawVcard.match(/FN[^:]*:(.+)/i);
    return match?.[1]?.trim() || "";
  };

  const normalizeSharedContactName = (name) => {
    const normalized = String(name || "").trim();
    return normalized || i18n.t("messagesList.sharedContact.unnamed");
  };

  const normalizeSharedContactNumber = (number) => {
    return String(number || "").replace(/\D/g, "");
  };

  const extractTelNumbersFromVcard = (vcard) => {
    const rawVcard = String(vcard || "").replace(/\r?\n[ \t]/g, "");
    const matches = [...rawVcard.matchAll(/TEL[^:]*:([^\n\r]+)/gi)];

    return matches
      .map((match) => {
        const telValue = String(match?.[1] || "").trim();

        const waidFromTel = telValue.match(/waid=(\d+)/i);
        if (waidFromTel?.[1]) {
          return waidFromTel[1];
        }

        return telValue.replace(/^tel:/i, "");
      })
      .map((value) => normalizeSharedContactNumber(value))
      .filter(Boolean);
  };

  const extractNumberFromVcard = (vcard) => {
    const rawVcard = String(vcard || "");
    const waidMatch = rawVcard.match(/waid=(\d+)/i);
    if (waidMatch?.[1]) {
      return waidMatch[1];
    }

    const telNumbers = extractTelNumbersFromVcard(rawVcard);
    if (telNumbers.length > 0) {
      return telNumbers[0];
    }

    return "";
  };

  const getSharedContactsFromMessage = (message) => {
    const mediaType = String(message?.mediaType || "");
    if (!message || !["contactMessage", "contactsArrayMessage", "vcard"].includes(mediaType)) {
      return [];
    }

    let parsedData = null;

    try {
      parsedData = message.dataJson ? JSON.parse(message.dataJson) : null;
    } catch (_err) {
      parsedData = null;
    }

    const messagePayload = parsedData?.message || {};
    const sourceContacts = [];

    if (messagePayload.contactMessage) {
      sourceContacts.push(messagePayload.contactMessage);
    }

    if (Array.isArray(messagePayload?.contactsArrayMessage?.contacts)) {
      sourceContacts.push(...messagePayload.contactsArrayMessage.contacts);
    }

    // Linhas da Cloud API gravadas antes do backend montar dataJson.message
    // (ex.: ticket #98338): o contato só existe no payload bruto da Meta,
    // com o número em phones[].wa_id.
    if (!sourceContacts.length && Array.isArray(parsedData?.rawMessage?.contacts)) {
      parsedData.rawMessage.contacts.forEach((rawContact) => {
        const phone = Array.isArray(rawContact?.phones) ? rawContact.phones[0] : null;
        sourceContacts.push({
          displayName:
            rawContact?.name?.formatted_name
            || [rawContact?.name?.first_name, rawContact?.name?.last_name].filter(Boolean).join(" "),
          number: phone?.wa_id || String(phone?.phone || "").replace(/\D/g, ""),
        });
      });
    }

    if (!sourceContacts.length && ["contactMessage", "vcard"].includes(mediaType)) {
      sourceContacts.push({
        displayName: extractNameFromVcard(message.body),
        vcard: message.body,
      });
    }

    const normalized = sourceContacts
      .map((contact) => {
        const displayName = String(contact?.displayName || "").trim();
        const vcard = String(contact?.vcard || "");
        const parsedName = extractNameFromVcard(vcard);
        const fallbackNumber = contact?.number || contact?.phoneNumber || contact?.waid;
        const name = normalizeSharedContactName(displayName || parsedName);
        const number = normalizeSharedContactNumber(extractNumberFromVcard(vcard) || fallbackNumber);

        return {
          name,
          number,
          vcard,
        };
      })
      .filter((contact) => contact.name || contact.number);

    const seen = new Set();
    return normalized.filter((contact) => {
      const key = contact.number ? `number::${contact.number}` : `name::${contact.name}`;
      if (seen.has(key)) {
        return false;
      }
      seen.add(key);
      return true;
    });
  };

  const findContactByNumber = async (number) => {
    const cleanedNumber = normalizeSharedContactNumber(number);
    if (!cleanedNumber) {
      return null;
    }

    try {
      const { data } = await api.get(`/contacts/bynumber/${cleanedNumber}`);
      if (data?.contact) {
        return data.contact;
      }
    } catch (err) {
      if (err?.response?.status !== 404) {
        throw err;
      }
    }

    const normalizedNumber = removeNinthDigitIfNeeded(cleanedNumber);
    if (normalizedNumber && normalizedNumber !== cleanedNumber) {
      try {
        const { data } = await api.get(`/contacts/bynumber/${normalizedNumber}`);
        if (data?.contact) {
          return data.contact;
        }
      } catch (err) {
        if (err?.response?.status !== 404) {
          throw err;
        }
      }
    }

    return null;
  };

  const createSharedContactFlowError = (code, message, cause) => {
    const error = new Error(message);
    error.code = code;
    if (cause) {
      error.cause = cause;
    }
    return error;
  };

  const ensureSharedContactPersisted = async (sharedContact) => {
    const contactName = normalizeSharedContactName(sharedContact?.name);
    const contactNumber = normalizeSharedContactNumber(sharedContact?.number);

    if (!contactNumber) {
      throw createSharedContactFlowError(
        "SHARED_CONTACT_MISSING_NUMBER",
        "Contato compartilhado sem numero valido"
      );
    }

    const existingContact = await findContactByNumber(contactNumber);
    if (existingContact) {
      return { contact: existingContact, created: false };
    }

    try {
      const { data: newContact } = await api.post("/contacts", {
        name: contactName,
        number: contactNumber,
      });

      return { contact: newContact, created: true };
    } catch (err) {
      throw createSharedContactFlowError(
        "SHARED_CONTACT_PERSIST_FAILED",
        "Falha ao salvar contato compartilhado",
        err
      );
    }
  };

  const handleStartConversationWithSharedContact = async (sharedContact) => {
    if (!isInteractionEnabled) return;
    try {
      const { contact } = await ensureSharedContactPersisted(sharedContact);

      const queueId = ticket?.queueId;
      // Chamado de conexão excluída: a conexão dele não serve mais de destino, o
      // backend escolhe a conexão do novo chamado.
      const whatsappId = isInactiveTicket(ticket) ? undefined : ticket?.whatsappId;

      const { data: newTicket } = await api.post("/tickets", {
        contactId: contact.id,
        queueId,
        userId: user.id,
        status: "open",
        whatsappId,
      });

      if (newTicket?.uuid) {
        history.push(`/tickets/${newTicket.uuid}`);
      }
    } catch (err) {
      if (err?.response?.data?.error === "ERR_OTHER_OPEN_TICKET") {
        let ticketData = null;
        try {
          ticketData = JSON.parse(err?.response?.data?.details || "{}");
        } catch (_parseErr) {
          ticketData = null;
        }

        setExistingTicketData(ticketData);
        setOpenExistingTicketDialog(true);
        return;
      }

      if (err?.code === "SHARED_CONTACT_MISSING_NUMBER") {
        toast.error(i18n.t("messagesList.sharedContact.startMissingNumber"));
        return;
      }

      if (err?.code === "SHARED_CONTACT_PERSIST_FAILED") {
        toast.error(i18n.t("messagesList.sharedContact.startSaveFailed"));
        return;
      }

      toastError(err);
    }
  };

  const handleSaveSharedContact = async (sharedContact) => {
    if (!isInteractionEnabled) return;
    try {
      const { created } = await ensureSharedContactPersisted(sharedContact);

      if (!created) {
        toast.info(i18n.t("messagesList.sharedContact.alreadyExists"));
        return;
      }

      toast.success(i18n.t("messagesList.sharedContact.saved"));
    } catch (err) {
      if (err?.code === "SHARED_CONTACT_MISSING_NUMBER") {
        toast.error(i18n.t("messagesList.sharedContact.saveMissingNumber"));
        return;
      }

      if (err?.code === "SHARED_CONTACT_PERSIST_FAILED") {
        toast.error(i18n.t("messagesList.sharedContact.saveFailed"));
        return;
      }

      toastError(err);
    }
  };

  const handleOpenSharedContacts = (contacts = []) => {
    if (!isInteractionEnabled) return;
    setSelectedSharedContacts(contacts);
    setOpenSharedContactsDialog(true);
  };

  const handleCloseSharedContacts = () => {
    setOpenSharedContactsDialog(false);
    setSelectedSharedContacts([]);
  };

  const handleRetryMedia = async (message) => {
    if (!isInteractionEnabled) return;
    if (!message?.id || retryingMediaId === message.id) return;

    try {
      setRetryingMediaId(message.id);
      setMediaLoadErrors((prev) => ({ ...prev, [message.id]: false }));
      const { data } = await api.post(`/messages/${message.id}/redownload`);

      if (data?.success && data?.newMediaUrl) {
        setMediaUrlOverrides((prev) => ({
          ...prev,
          [message.id]: data.newMediaUrl
        }));
        setMediaLoadErrors((prev) => ({ ...prev, [message.id]: false }));
      }
    } catch (err) {
      toastError(err);
    } finally {
      setRetryingMediaId(null);
    }
  };

  // Otimista: marca "processing" antes do POST para que o resultado que chega
  // pelo socket (done/failed) nunca seja sobrescrito por esta marcação.
  const handleRetryTranscription = async (message) => {
    if (!isInteractionEnabled) return;
    if (!message?.id) return;

    dispatch({
      type: "UPDATE_MESSAGE",
      payload: { ...message, transcriptionStatus: "processing" }
    });
    try {
      await api.post(`/messages/${message.id}/retranscribe`);
    } catch (err) {
      dispatch({ type: "UPDATE_MESSAGE", payload: message });
      toastError(err);
    }
  };

  const markMediaLoadError = (messageId) => {
    if (!messageId) return;
    setMediaLoadErrors((prev) => {
      if (prev[messageId]) return prev;
      return { ...prev, [messageId]: true };
    });
  };

  const clearMediaLoadError = (messageId) => {
    if (!messageId) return;
    setMediaLoadErrors((prev) => {
      if (!prev[messageId]) return prev;
      return { ...prev, [messageId]: false };
    });
  };

  const handleForceDownload = async (event, url, filename = "arquivo") => {
    if (!isInteractionEnabled) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();

    await downloadMediaFile(url, filename);
  };

  const getMessageDomId = (messageId) => `message-${messageId}`;

  const scrollMessageIntoContainer = (element, behavior = "smooth") => {
    const container = document.getElementById("messagesList");
    if (!container || !element) return false;

    const containerRect = container.getBoundingClientRect();
    const elementRect = element.getBoundingClientRect();
    const targetScrollTop =
      container.scrollTop +
      (elementRect.top - containerRect.top) -
      Math.max(0, (container.clientHeight / 2) - (element.clientHeight / 2));

    container.scrollTo({
      top: Math.max(0, targetScrollTop),
      behavior,
    });

    return true;
  };

  // Verifica se a mensagem-alvo está suficientemente visível dentro do container.
  // Usado para decidir se é preciso reposicionar depois que o layout muda (mídia
  // carregando, restauração de histórico ou scroll-to-bottom inicial concorrente).
  const isMessageReasonablyVisible = (element) => {
    const container = document.getElementById("messagesList");
    if (!container || !element) return true;

    const containerRect = container.getBoundingClientRect();
    const elementRect = element.getBoundingClientRect();
    const visibleTop = Math.max(containerRect.top, elementRect.top);
    const visibleBottom = Math.min(containerRect.bottom, elementRect.bottom);
    const visibleHeight = visibleBottom - visibleTop;

    if (visibleHeight <= 0) return false;

    const referenceHeight = Math.min(elementRect.height, containerRect.height) || 1;
    return visibleHeight >= referenceHeight * 0.6;
  };

  // Centraliza a mensagem-alvo e reafirma a posição em fases. Imagens/áudios/vídeos
  // acima da mensagem podem carregar depois e empurrá-la para fora da área visível;
  // a restauração de histórico e o scroll inicial também podem competir. Só
  // reposiciona enquanto o jump continuar ativo e a mensagem não estiver visível,
  // para não atrapalhar um scroll manual do usuário.
  const centerTargetMessage = (targetMessageId) => {
    const targetId = String(targetMessageId);
    const element = document.getElementById(getMessageDomId(targetId));
    if (!element) return false;

    // Suprime o auto-loadMore disparado pelo handleScroll enquanto o jump rola e se
    // reafirma. Sem isso, a rolagem suave a partir do topo passa pela zona de gatilho
    // (scrollTop < 50) e dispara carregamento de histórico em loop ("scroll infinito").
    ignoreScrollUntilRef.current = Date.now() + 1200;

    scrollMessageIntoContainer(element, "smooth");
    setHighlightedMessageId(targetId);

    setTimeout(() => {
      setHighlightedMessageId((prev) => (String(prev) === targetId ? null : prev));
    }, 2600);

    const reassert = () => {
      if (String(jumpRequestRef.current?.id) !== targetId) return;
      const currentElement = document.getElementById(getMessageDomId(targetId));
      if (!currentElement) return;
      if (isMessageReasonablyVisible(currentElement)) return;
      scrollMessageIntoContainer(currentElement, "auto");
    };

    requestAnimationFrame(reassert);
    [150, 400, 800].forEach((delay) => setTimeout(reassert, delay));

    return true;
  };

  const tryScrollToTargetMessage = (targetMessageId) => {
    if (!targetMessageId) return false;

    const existsInList = messagesList.some((message) => String(message?.id) === String(targetMessageId));
    if (!existsInList) {
      jumpDebugLog("target-not-in-list", { targetMessageId });
      return false;
    }

    const element = document.getElementById(getMessageDomId(targetMessageId));
    if (!element) {
      jumpDebugLog("target-in-list-but-dom-missing", { targetMessageId });
      requestAnimationFrame(() => {
        if (!document.getElementById(getMessageDomId(targetMessageId))) return;
        centerTargetMessage(targetMessageId);
        jumpRequestRef.current = {
          ...jumpRequestRef.current,
          done: true,
        };
        jumpDebugLog("target-found-on-next-frame", { targetMessageId });
      });
      return false;
    }

    centerTargetMessage(targetMessageId);
    jumpDebugLog("jump-success", { targetMessageId });

    return true;
  };

  useEffect(() => {
    let isMounted = true;

    if (["contact", "ticket", "whatsapp"].includes(String(messageHistoryModeOverride || ""))) {
      setMessageHistoryMode(String(messageHistoryModeOverride));
      return () => {
        isMounted = false;
      };
    }

    const settingsAbortController = new AbortController();
    const loadMessageHistoryMode = async () => {
      try {
        const { data: settings } = await api.get("/settings", { signal: settingsAbortController.signal });
        const messageHistorySetting = settings?.find((s) => s.key === "messageHistory");
        const groupUnifySetting = settings?.find((s) => s.key === "groupTicketUnify");
        const settingValue = messageHistorySetting?.value;
        let resolvedMode = "contact";

        if (settingValue === "contact" || settingValue === "ticket" || settingValue === "whatsapp") {
          resolvedMode = settingValue;
        } else if (settingValue === "disabled") {
          resolvedMode = "ticket";
        }

        if (resolvedMode === "ticket" && hasFullHistoryPermission) {
          resolvedMode = "contact";
        }

        if (isMounted) {
          setMessageHistoryMode(resolvedMode);
          setGroupTicketUnify(groupUnifySetting?.value === "disabled" ? "disabled" : "enabled");
        }
      } catch (err) {
        if (isMounted && !isRequestCanceled(err)) {
          setMessageHistoryMode("contact");
        }
      }
    };

    loadMessageHistoryMode();

    return () => {
      isMounted = false;
      settingsAbortController.abort();
    };
  }, [messageHistoryModeOverride, hasFullHistoryPermission]);

  useEffect(() => {
    const nextConversationRef = getActiveConversationRef();
    if (!nextConversationRef) {
      return;
    }

    if (currentTicketId.current === nextConversationRef) {
      return;
    }

    // Atualiza o marcador da conversa antes do reset para evitar corridas com sync atrasado.
    const isConversationSwitch = currentTicketId.current != null;
    currentTicketId.current = nextConversationRef;
    clearFallbackSyncTimer();
    fallbackSyncRunIdRef.current += 1;
    if (isConversationSwitch) {
      conversationAbortRef.current?.abort();
      conversationAbortRef.current = null;
    }

    dispatch({ type: "RESET" });
    setPageNumber(1);
    setHasMore(false);
    setLoading(false);
    setIsNearBottom(true);
    setHasUnreadWhileAwayFromBottom(false);
    initialScrollDoneRef.current = false;
    isNearBottomRef.current = true;
    previousScrollHeightRef.current = 0;
    isRestoringScrollRef.current = false;
    ignoreScrollUntilRef.current = 0;
    historyLoadInFlightRef.current = false;
    historyScrollSnapshotRef.current = {
      conversationRef: null,
      pageNumber: 0,
      scrollTop: 0,
      scrollHeight: 0,
    };
    setHighlightedMessageId(null);
    jumpRequestRef.current = { id: null, token: null, done: false };
    jumpNotFoundNotifiedRef.current = false;
    jumpFetchInFlightRef.current = false;
    jumpAroundFetchedRef.current = false;
    quotedJumpFallbackRef.current = null;
    oldestLoadedIdRef.current = null;
  }, [contactId, ticketId, ticket?.id, getActiveConversationRef, clearFallbackSyncTimer]);

  // Ao trocar o filtro do histórico da aba de contato (ticket específico,
  // conexão específica ou "ver todas"), reinicia a paginação e recarrega
  useEffect(() => {
    if (!contactId) return;

    if (pageNumber !== 1) {
      contactFilterResetRef.current = true;
    }

    dispatch({ type: "RESET" });
    setPageNumber(1);
    setHasMore(false);
    initialScrollDoneRef.current = false;
    previousScrollHeightRef.current = 0;
    historyLoadInFlightRef.current = false;
    oldestLoadedIdRef.current = null;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contactFilterKey, contactId]);

  // Ponto único de disparo do salto: usado tanto pelo pedido externo (props vindas
  // da pesquisa, do relatório, da ficha do contato) quanto pelo clique na citação.
  // `fallbackTicketId` só é preenchido no caso da citação — ver quotedJumpFallbackRef.
  const startJumpToMessage = (messageId, { token, fallbackTicketId } = {}) => {
    if (!messageId) return;

    jumpRequestRef.current = {
      id: String(messageId),
      token: String(token || Date.now()),
      done: false,
    };
    jumpNotFoundNotifiedRef.current = false;
    jumpFetchInFlightRef.current = false;
    jumpAroundFetchedRef.current = false;
    quotedJumpFallbackRef.current = fallbackTicketId
      ? { id: String(messageId), ticketId: fallbackTicketId }
      : null;
    setJumpRequestSeq((prev) => prev + 1);
  };

  useEffect(() => {
    if (!jumpToMessageId) return;

    jumpDebugLog("jump-request-received", {
      jumpToMessageId,
      jumpToMessageTs,
    });

    startJumpToMessage(jumpToMessageId, { token: jumpToMessageTs });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jumpToMessageId, jumpToMessageTs]);

  // A citação só é clicável quando o salto realmente faz sentido: no modo
  // somente-leitura a lista é filtrada por allowedMessageIds (a original pode nem
  // estar lá) e, no modo de encaminhamento, o clique pertence à seleção.
  const isQuotedJumpEnabled = (message) =>
    isInteractionEnabled
    && !isForwardSelectionMode
    && Boolean(message?.quotedMsg?.id)
    // Resposta a status/story: a citada é uma mensagem sintética "status@broadcast",
    // que não existe no histórico do ticket — não há para onde saltar.
    && message?.quotedMsg?.remoteJid !== "status@broadcast";

  const handleQuotedMessageJump = (event, message) => {
    if (!isQuotedJumpEnabled(message)) return;

    // A citação pode embutir player de áudio/vídeo, preview de PDF em <iframe>,
    // botão de Download e a miniatura do ModalImageCors (que abre o lightbox — um
    // comportamento que já existia). O clique nesses elementos pertence a eles.
    // O container é o próprio alvo do closest quando tem role="button", daí a
    // comparação com currentTarget.
    const interactive = event.target?.closest?.(
      'audio, video, iframe, button, a, input, img, [role="button"]'
    );
    if (interactive && interactive !== event.currentTarget) return;

    event.stopPropagation();

    const quoted = message.quotedMsg;
    jumpDebugLog("quoted-jump-click", {
      targetMessageId: quoted.id,
      quotedTicketId: quoted.ticketId,
    });

    startJumpToMessage(quoted.id, { fallbackTicketId: quoted.ticketId || null });
  };

  // Carrega, numa única requisição, o histórico ancorado na mensagem-alvo: o
  // contexto imediatamente anterior a ela e tudo que veio depois, até o fim da
  // conversa. Substitui a antiga varredura página a página (até 100 requisições),
  // que em grupos estourava o limite e ainda pulava mensagens por causa do OFFSET.
  const fetchAroundMessage = useCallback(async (jump) => {
    const activeTicketId = ticketId || ticket?.id;
    const conversationRef = contactId ? `contact-${contactId}` : activeTicketId;
    const endpoint = contactId
      ? `/messages/contact/${contactId}/around/${encodeURIComponent(jump.id)}`
      : `/messages/${activeTicketId}/around/${encodeURIComponent(jump.id)}`;

    const isStale = () =>
      !isMountedRef.current ||
      currentTicketId.current !== conversationRef ||
      String(jumpRequestRef.current?.id) !== String(jump.id);

    // Salto nascido do clique numa citação cuja mensagem original está fora do
    // escopo do histórico exibido (tipicamente modo 'ticket', com a citada vinda de
    // um atendimento anterior do mesmo contato): abre o ticket de origem já
    // posicionado nela, em vez de só avisar que não foi encontrada.
    const tryNavigateToQuotedTicket = async () => {
      const fallback = quotedJumpFallbackRef.current;
      if (!fallback || String(fallback.id) !== String(jump.id)) return false;
      // Na aba Contatos o escopo já é o contato inteiro, então um 404 ali significa
      // inalcançável de verdade — navegar não mudaria nada.
      if (contactId) return false;
      if (!fallback.ticketId) return false;
      if (String(fallback.ticketId) === String(activeTicketId)) return false;

      try {
        const { data } = await api.get(`/tickets/${fallback.ticketId}`);
        if (!data?.uuid || !isMountedRef.current) return false;

        quotedJumpFallbackRef.current = null;
        jumpRequestRef.current = { ...jumpRequestRef.current, done: true };
        jumpDebugLog("quoted-jump-navigate", {
          targetMessageId: jump.id,
          targetTicketId: fallback.ticketId,
        });

        history.push(`/tickets/${data.uuid}`, {
          jumpToMessageId: String(jump.id),
          jumpToMessageTs: Date.now(),
        });
        return true;
      } catch (_err) {
        return false;
      }
    };

    const failJump = async (translationKey, { allowNavigate = false } = {}) => {
      if (allowNavigate && (await tryNavigateToQuotedTicket())) return;

      if (!jumpNotFoundNotifiedRef.current) {
        jumpNotFoundNotifiedRef.current = true;
        toast.warning(i18n.t(translationKey));
      }
      jumpRequestRef.current = { ...jumpRequestRef.current, done: true };
      scrollToBottomInstant();
    };

    try {
      const { data } = await api.get(endpoint, { signal: getConversationSignal() });

      if (isStale()) return;

      // Mensagem antiga demais para o salto direto: avisa em vez de tentar
      // carregar o histórico inteiro do contato.
      if (data?.beyondLimit) {
        jumpDebugLog("jump-beyond-limit", {
          targetMessageId: jump.id,
          newerCount: data?.newerCount,
        });
        // Aqui a mensagem ESTÁ no escopo, só está longe demais do fim: abrir outro
        // ticket não resolveria, então só avisa.
        await failJump("messagesList.jumpTooOld");
        return;
      }

      const loadedMessages = Array.isArray(data?.messages) ? data.messages : [];

      if (!loadedMessages.length) {
        await failJump("messagesList.jumpNotFound", { allowNavigate: true });
        return;
      }

      // O trecho carregado vai da mensagem-alvo até a mensagem mais recente, então
      // a lista continua sendo a cauda viva da conversa — o append por socket e o
      // scroll infinito para cima seguem valendo sem nenhuma ressalva.
      ignoreScrollUntilRef.current = Date.now() + 1200;
      dispatch({ type: "LOAD_MESSAGES", payload: loadedMessages });
      setHasMore(Boolean(data?.hasMore));
      setLoading(false);
      initialScrollDoneRef.current = true;

      jumpDebugLog("around-loaded", {
        targetMessageId: jump.id,
        loadedCount: loadedMessages.length,
        hasMore: Boolean(data?.hasMore),
      });
    } catch (err) {
      // Cancelada pela troca de conversa: o salto pertencia à tela anterior.
      if (isStale() || isRequestCanceled(err)) return;
      // 404: a mensagem não existe mais ou está fora do histórico deste ticket
      // (ERR_MESSAGE_NOT_IN_SCOPE). No segundo caso, se o salto veio de uma citação,
      // ainda dá para abrir o ticket de origem.
      await failJump("messagesList.jumpNotFound", { allowNavigate: true });
    } finally {
      jumpFetchInFlightRef.current = false;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contactId, ticketId, ticket?.id]);

  useEffect(() => {
    const currentJump = jumpRequestRef.current;
    if (!currentJump?.id || currentJump.done) return;

    const found = tryScrollToTargetMessage(currentJump.id);
    if (found) {
      jumpRequestRef.current = { ...currentJump, done: true };
      return;
    }

    const targetAlreadyLoaded = messagesList.some(
      (message) => String(message?.id) === String(currentJump.id)
    );

    if (targetAlreadyLoaded) {
      jumpDebugLog("target-already-loaded-awaiting-dom", {
        targetMessageId: currentJump.id,
      });
      return;
    }

    if (jumpFetchInFlightRef.current) return;

    // O trecho ancorado já foi carregado e a mensagem continua fora da lista:
    // não há mais o que buscar (não paginamos atrás dela como antes).
    if (jumpAroundFetchedRef.current) {
      if (!jumpNotFoundNotifiedRef.current) {
        jumpNotFoundNotifiedRef.current = true;
        jumpDebugLog("jump-not-found", { targetMessageId: currentJump.id });
        toast.warning(i18n.t("messagesList.jumpNotFound"));
      }
      jumpRequestRef.current = { ...currentJump, done: true };
      // Como pulamos o scroll inicial enquanto o jump estava pendente, volta para o
      // final da conversa para não deixar a visualização presa no topo.
      scrollToBottomInstant();
      return;
    }

    jumpFetchInFlightRef.current = true;
    jumpAroundFetchedRef.current = true;
    jumpDebugLog("request-around", { targetMessageId: currentJump.id });
    fetchAroundMessage(currentJump);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [messagesList, jumpRequestSeq, fetchAroundMessage]);

  useEffect(() => {
    if (!isReadOnlyMode || !allowedMessageIdSet || allowedMessageIdSet.size === 0) {
      return;
    }

    if (loading || !hasMore) {
      return;
    }

    const missingMarkedMessages = Array.from(allowedMessageIdSet).some((allowedId) => {
      return !messagesList.some((message) => String(message?.id || "") === allowedId);
    });

    if (missingMarkedMessages) {
      setPageNumber((prevPageNumber) => prevPageNumber + 1);
    }
  }, [
    allowedMessageIdSet,
    hasMore,
    isReadOnlyMode,
    loading,
    messagesList,
  ]);

  useEffect(() => {
    let cancelled = false;
    const abortController = new AbortController();

    const loadGroupParticipants = async () => {
      const groupContactId = ticket?.contactId || contactId;

      if (!isGroup || !groupContactId) {
        if (!cancelled && isMountedRef.current) {
          setGroupParticipants([]);
        }
        return;
      }

      try {
        const { data } = await api.get(`/contacts/${groupContactId}/participants`, {
          signal: abortController.signal,
        });
        if (!cancelled && isMountedRef.current) {
          setGroupParticipants(Array.isArray(data?.participants) ? data.participants : []);
        }
      } catch (_err) {
        if (!cancelled && isMountedRef.current) {
          setGroupParticipants([]);
        }
      }
    };

    loadGroupParticipants();

    return () => {
      cancelled = true;
      abortController.abort();
    };
  }, [isGroup, ticket?.contactId, contactId]);

  // Scroll automático quando mensagens otimistas são adicionadas
  useEffect(() => {
    if (scrollTrigger > 0) {
      const timer = setTimeout(() => {
        if (isNearBottomRef.current) {
          scrollToBottom();
        } else {
          setHasUnreadWhileAwayFromBottom(true);
        }
      }, 100);

      return () => clearTimeout(timer);
    }

    return undefined;
  }, [scrollTrigger]);

  // Fallback: quando houver mensagem otimista pendente, sincroniza via API.
  // Isso cobre cenários em que o socket não entrega corretamente o create/update.
  useEffect(() => {
    if (scrollTrigger <= 0) {
      return;
    }

    scheduleOptimisticFallbackSync(0);

    return () => {
      clearFallbackSyncTimer();
      fallbackSyncRunIdRef.current += 1;
    };
  }, [scrollTrigger, scheduleOptimisticFallbackSync, clearFallbackSyncTimer]);

  useEffect(() => {
    let cancelled = false;
    const activeTicketId = ticketId || ticket?.id;
    const activeConversationRef = contactId ? `contact-${contactId}` : activeTicketId;
    const isHistoryPage = pageNumber > 1;
    let acquiredHistoryLoadLock = false;

    // Não tenta carregar se não tiver ticketId ou contactId válido
    if (!activeTicketId && !contactId) {
      if (isMountedRef.current) {
        setLoading(false);
      }
      return;
    }

    // Filtro do histórico recém-trocado: aguarda a paginação voltar à página 1
    // antes de buscar (evita fetch com pageNumber antigo do filtro anterior)
    if (contactFilterResetRef.current) {
      if (pageNumber !== 1) {
        return;
      }
      contactFilterResetRef.current = false;
    }

    if (isHistoryPage) {
      if (historyLoadInFlightRef.current) {
        return;
      }

      historyLoadInFlightRef.current = true;
      acquiredHistoryLoadLock = true;
    }

    // PERF: na primeira página (abertura do ticket) não usar debounce — vai direto.
    // Para páginas seguintes (scroll-up de histórico) mantém um pequeno debounce
    // para coalescer disparos duplicados do efeito.
    const debounceMs = pageNumber === 1 ? 0 : 120;

    // PERF: só mostra a bolinha se a busca demorar >180ms (evita flicker em
    // respostas rápidas — o cache/SSD do banco geralmente devolve em <100ms).
    let loadingTimer = null;
    if (isMountedRef.current) {
      if (pageNumber === 1) {
        loadingTimer = setTimeout(() => {
          if (!cancelled && isMountedRef.current) {
            setLoading(true);
          }
        }, 180);
      } else {
        setLoading(true);
      }
    }

    // Salva a altura do scroll antes de carregar mais mensagens (para manter posição)
    const messagesContainer = document.getElementById("messagesList");
    if (messagesContainer && pageNumber > 1) {
      previousScrollHeightRef.current = messagesContainer.scrollHeight;
      historyScrollSnapshotRef.current = {
        conversationRef: activeConversationRef,
        pageNumber,
        scrollTop: messagesContainer.scrollTop,
        scrollHeight: messagesContainer.scrollHeight,
      };
    }

    const pageAbortController = new AbortController();
    const delayDebounceFn = setTimeout(() => {
      const fetchMessages = async () => {
        try {
          let data;

          // A partir da 2ª página usamos cursor keyset (a mensagem mais antiga já
          // carregada) em vez de OFFSET: assim mensagens novas chegando durante a
          // rolagem não deslocam a janela nem fazem o histórico pular linhas.
          const params = { pageNumber };
          if (pageNumber > 1 && oldestLoadedIdRef.current) {
            params.beforeId = oldestLoadedIdRef.current;
          }

          // Se é chat de contato, busca por contactId
          if (contactId) {
            // Filtros do seletor de histórico (modo ticket/conexão)
            if (contactHistoryFilter?.ticketId) {
              params.ticketId = contactHistoryFilter.ticketId;
            }
            if (contactHistoryFilter?.whatsappId) {
              params.whatsappId = contactHistoryFilter.whatsappId;
            }
            if (contactHistoryFilter?.viewAll) {
              params.viewAll = "true";
            }
            const response = await api.get(`/messages/contact/${contactId}`, {
              params,
              signal: pageAbortController.signal,
            });
            data = response.data;
          } else {
            // Busca normal por ticketId
            const response = await api.get("/messages/" + activeTicketId, {
              params,
              signal: pageAbortController.signal,
            });
            data = response.data;
          }

          // Converte ambos para o mesmo tipo para comparação
          if (
            currentTicketId.current === activeConversationRef ||
            parseInt(currentTicketId.current) === parseInt(activeTicketId)
          ) {
            if (cancelled || !isMountedRef.current) {
              return;
            }

            if (jumpRequestRef.current?.id) {
              jumpDebugLog("fetch-page-complete", {
                targetMessageId: jumpRequestRef.current.id,
                fetchedPage: pageNumber,
                fetchedCount: Array.isArray(data.messages) ? data.messages.length : 0,
              });
            }
            dispatch({ type: "LOAD_MESSAGES", payload: data.messages });

            // Reconcilia mensagens otimistas/pendentes restauradas do localStorage
            // (após um reload) com as mensagens reais já confirmadas no banco,
            // evitando que fiquem "presas" ou dupliquem na tela.
            const serverOptimisticIds = (data.messages || [])
              .map((message) => message?.optimisticId)
              .filter(Boolean);

            if (serverOptimisticIds.length > 0) {
              const optimisticKey = getActiveOptimisticKey();
              if (optimisticKey) {
                serverOptimisticIds.forEach((optimisticId) => {
                  removeOptimisticMessage(optimisticKey, optimisticId);
                  removePendingMessageByOptimisticId(optimisticId);
                });
              }
            }

            setHasMore(data.hasMore);
            if (loadingTimer) clearTimeout(loadingTimer);
            setLoading(false);
            
            // Scroll para o final apenas na primeira página (instantâneo, sem animação).
            // Se houver um "jump" pendente (abrir a conversa já posicionando em uma
            // mensagem específica), NÃO rola para o final — isso sobrescreveria o
            // posicionamento na mensagem-alvo. O fluxo de jump cuida do scroll.
            if (pageNumber === 1 && data.messages.length > 0) {
              const hasPendingJump = Boolean(jumpRequestRef.current?.id) && !jumpRequestRef.current?.done;

              // Com um jump pendente a lista permanece em scrollTop = 0 até o
              // centerTargetMessage reposicionar. Suprime o auto-loadMore por
              // handleScroll nesse intervalo para não disparar histórico em loop.
              if (hasPendingJump) {
                ignoreScrollUntilRef.current = Date.now() + 1200;
              }

              requestAnimationFrame(() => {
                if (!hasPendingJump) {
                  // Faz scroll em fases para cobrir reflow tardio de layout/mídia
                  scrollToBottomInstant();
                  requestAnimationFrame(() => {
                    scrollToBottomInstant();
                  });

                  setTimeout(() => {
                    if (
                      currentTicketId.current === activeConversationRef &&
                      isMountedRef.current &&
                      !(jumpRequestRef.current?.id && !jumpRequestRef.current?.done)
                    ) {
                      scrollToBottomInstant();
                    }
                  }, 120);
                }

                // Marcar que o scroll inicial foi feito
                initialScrollDoneRef.current = true;
              });
            } else if (pageNumber > 1) {
              // Ao carregar mensagens antigas, mantém a posição do scroll
              setTimeout(() => {
                if (cancelled || !isMountedRef.current) {
                  return;
                }

                if (currentTicketId.current !== activeConversationRef) {
                  return;
                }

                const container = document.getElementById("messagesList");
                const snapshot = historyScrollSnapshotRef.current;
                const hasValidSnapshot =
                  snapshot?.conversationRef === activeConversationRef &&
                  snapshot?.pageNumber === pageNumber &&
                  Number.isFinite(snapshot?.scrollHeight) &&
                  snapshot.scrollHeight > 0;

                if (container && hasValidSnapshot) {
                  const newScrollHeight = container.scrollHeight;
                  const scrollDelta = newScrollHeight - snapshot.scrollHeight;
                  const maxScrollTop = Math.max(0, newScrollHeight - container.clientHeight);
                  const desiredTop = snapshot.scrollTop + scrollDelta;
                  const clampedTop = Math.min(Math.max(1, desiredTop), maxScrollTop);

                  isRestoringScrollRef.current = true;
                  ignoreScrollUntilRef.current = Date.now() + 250;
                  container.scrollTop = Number.isFinite(clampedTop) ? clampedTop : Math.max(1, scrollDelta);

                  requestAnimationFrame(() => {
                    requestAnimationFrame(() => {
                      if (
                        currentTicketId.current === activeConversationRef &&
                        isMountedRef.current
                      ) {
                        isRestoringScrollRef.current = false;
                      }
                    });
                  });
                }
              }, 100);
            }
          }
        } catch (err) {
          if (!cancelled && isMountedRef.current) {
            if (loadingTimer) clearTimeout(loadingTimer);
            setLoading(false);
            toastError(err);
          }
        } finally {
          if (acquiredHistoryLoadLock) {
            historyLoadInFlightRef.current = false;
          }
        }
      };
      fetchMessages();
    }, debounceMs);

    return () => {
      cancelled = true;
      if (loadingTimer) clearTimeout(loadingTimer);
      clearTimeout(delayDebounceFn);
      pageAbortController.abort();
      if (acquiredHistoryLoadLock) {
        historyLoadInFlightRef.current = false;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageNumber, ticketId, ticket?.id, contactId, contactFilterKey]);

  useEffect(() => {
    const companyId = localStorage.getItem("companyId");
    const socket = socketManager.getSocket(companyId);

    // Delegam para a versão mais recente via ref (ver socketCallbacksRef acima).
    // Sombreiam os nomes do escopo externo, então os usos abaixo continuam iguais
    // e o efeito deixa de depender da identidade desses callbacks.
    const scheduleOptimisticFallbackSync = (...args) =>
      socketCallbacksRef.current.scheduleOptimisticFallbackSync(...args);
    const removeOptimisticMessage = (...args) =>
      socketCallbacksRef.current.removeOptimisticMessage(...args);
    const getActiveOptimisticKey = (...args) =>
      socketCallbacksRef.current.getActiveOptimisticKey(...args);
    const runFallbackSync = (...args) =>
      socketCallbacksRef.current.runFallbackSync(...args);
    const removePendingMessageByOptimisticId = (...args) =>
      socketCallbacksRef.current.removePendingMessageByOptimisticId(...args);

    const handleAppMessage = async (data) => {
      // [AckDebug] Log de TODOS os eventos appMessage chegando neste socket
      console.log("[AckDebug] appMessage received", {
        action: data?.action,
        messageId: data?.message?.id,
        ack: data?.message?.ack,
        ticketId: data?.message?.ticketId,
      });

      if (!data || (data.action !== "create" && data.action !== "update")) {
        return;
      }

      const incomingMessage = data?.message;
      if (!incomingMessage) {
        scheduleOptimisticFallbackSync(0);
        return;
      }

      const incomingMessageId = getMessageKey(incomingMessage?.id);
      const incomingOptimisticId = getMessageKey(incomingMessage?.optimisticId);
      const alreadyLoadedMessage =
        (incomingMessageId && loadedMessageIdsRef.current.has(incomingMessageId)) ||
        (incomingOptimisticId && loadedOptimisticIdsRef.current.has(incomingOptimisticId));

      const currentConversationRef = currentTicketId.current;
      const currentTicketNumericId = parseInt(String(ticketId || ticket?.id || ""), 10);
      const currentContactNumericId = parseInt(String(ticket?.contactId || contactId || ""), 10);
      const messageTicketId = parseInt(String(incomingMessage?.ticketId || data?.ticket?.id || ""), 10);
      const messageContactId = parseInt(
        String(
          incomingMessage?.contactId ||
          data?.contact?.id ||
          incomingMessage?.contact?.id ||
          data?.ticket?.contactId ||
          ""
        ),
        10
      );

      const activeTicketRef = currentConversationRef?.toString().startsWith("contact-")
        ? NaN
        : parseInt(String(currentConversationRef), 10);

      const isMatchingTicket = Number.isFinite(messageTicketId) && (
        messageTicketId === currentTicketNumericId ||
        messageTicketId === activeTicketRef
      );
      const isMatchingContact = Number.isFinite(messageContactId) &&
        Number.isFinite(currentContactNumericId) &&
        messageContactId === currentContactNumericId;
      // Aba Contatos com um chamado (modo "Por ticket") ou uma conexão (modo "Por
      // conexão") escolhido no seletor de histórico: ao vivo só entra o que é
      // dele. Antes, a mensagem de um chamado novo do contato aparecia colada no
      // chamado antigo aberto na tela e sumia no F5 (a API respeita o filtro).
      // `alreadyLoadedMessage` cobre a bolha otimista do que o próprio usuário enviou.
      const historyFilter = contactId ? contactHistoryFilterRef.current : null;
      const filterTicketId = parseInt(String(historyFilter?.ticketId || ""), 10);
      const filterWhatsappId = parseInt(String(historyFilter?.whatsappId || ""), 10);
      const messageWhatsappId = parseInt(String(data?.ticket?.whatsappId || ""), 10);
      const outsideHistoryFilter =
        (Number.isFinite(filterTicketId) && messageTicketId !== filterTicketId) ||
        (Number.isFinite(filterWhatsappId) &&
          Number.isFinite(messageWhatsappId) &&
          messageWhatsappId !== filterWhatsappId);

      const shouldProcess =
        (!outsideHistoryFilter || alreadyLoadedMessage) && (
          isMatchingTicket ||
          ((messageHistoryMode === "contact" || groupSharedHistory) && isMatchingContact) ||
          (data.action === "update" && alreadyLoadedMessage)
        );

      if (
        !shouldProcess &&
        (isMatchingTicket || isMatchingContact) &&
        (!outsideHistoryFilter || incomingMessage.fromMe)
      ) {
        scheduleOptimisticFallbackSync(0);
      }

      if (data.action === "create" && shouldProcess) {
        // Duas chaves de propósito: no chat de contato a bolha otimista nasce sob
        // o pseudo-ticket ("contact-<id>") e só depois migra para o ticket real —
        // se a mensagem do servidor chegar antes dessa migração, remover só pelo
        // ticketId dela deixaria a bolha pendente para trás.
        const optimisticTicketKeys = dedupeOptimisticKeys([
          incomingMessage.ticketId,
          getActiveOptimisticKey()
        ]);

        // Se a mensagem tem optimisticId, remover a mensagem otimista
        if (incomingMessage.optimisticId && optimisticTicketKeys.length > 0) {
          optimisticTicketKeys.forEach((key) =>
            removeOptimisticMessage(key, incomingMessage.optimisticId)
          );
          removePendingMessageByOptimisticId(incomingMessage.optimisticId);
        }

        dispatch({ type: "ADD_MESSAGE", payload: incomingMessage });
        if (isNearBottomRef.current) {
          // Use setTimeout to ensure DOM is updated before scrolling
          setTimeout(() => {
            scrollToBottom();
          }, 100);
        } else {
          setHasUnreadWhileAwayFromBottom(true);
        }
        
        // Se a mensagem NÃO foi enviada por mim (fromMe: false), marcar como lida
        // Isso envia o read receipt para o WhatsApp (ticks azuis)
        if (!isReadOnlyMode && !incomingMessage.fromMe && ticketId) {
          try {
            // Chama endpoint dedicado para marcar mensagens como lidas
            await api.post(`/messages/${ticketId}/read`);
          } catch (err) {
            console.warn("Erro ao marcar mensagem como lida:", err);
          }
        }
      } 

      if (data.action === "update" && shouldProcess) {
        // Mesma razão do bloco de "create": limpar nas duas chaves possíveis.
        const optimisticTicketKeys = dedupeOptimisticKeys([
          incomingMessage.ticketId,
          getActiveOptimisticKey()
        ]);
        if (incomingMessage.optimisticId && optimisticTicketKeys.length > 0) {
          optimisticTicketKeys.forEach((key) =>
            removeOptimisticMessage(key, incomingMessage.optimisticId)
          );
          removePendingMessageByOptimisticId(incomingMessage.optimisticId);
        }

        // Quando a URL definitiva chega pelo socket, o override local do retry
        // perde a validade. Sem esta limpeza, um retry manual que aconteceu em
        // paralelo à fila deixaria a URL MAIS VELHA vencendo para sempre —
        // getMediaUrl dá precedência ao override.
        if (incomingMessage?.mediaUrl) {
          setMediaUrlOverrides((prev) => {
            if (!prev[incomingMessage.id]) return prev;
            const next = { ...prev };
            delete next[incomingMessage.id];
            return next;
          });
        }

        // `isPending`, `pendingAt` e `pendingUserId` são COLUNAS reais: num
        // toJSON de instância Sequelize vêm como false/null, nunca undefined.
        // Ou seja, esta condição sempre foi verdadeira e o efeito real era
        // "todo update sem mediaUrl dispara um refetch da página 1". O dano era
        // contido porque runFallbackSync tem guarda própria, mas com o download
        // diferido ela passaria a disparar em cada atualização de mídia — que é
        // justamente quando o socket já traz tudo o que precisamos.
        const mediaStatus = String(
          incomingMessage?.mediaDownloadStatus || ""
        ).toLowerCase();

        const shouldForceSyncMissingMedia =
          !incomingMessage?.mediaUrl &&
          MEDIA_SLOT_TYPES.includes(incomingMessage?.mediaType) &&
          !["pending", "downloading", "failed"].includes(mediaStatus);

        if (shouldForceSyncMissingMedia) {
          // Sem await: LOAD_MESSAGES e UPDATE_MESSAGE fazem merge/upsert, então
          // a ordem entre os dois não importa — e o ACK deixa de esperar por um
          // GET completo da conversa.
          void runFallbackSync();
        }

        // [AckDebug] Log para depurar atualização de status (✓✓) em tempo real
        console.log("[AckDebug] UPDATE_MESSAGE dispatch", {
          messageId: incomingMessage?.id,
          ack: incomingMessage?.ack,
          fromMe: incomingMessage?.fromMe,
          ticketId: incomingMessage?.ticketId,
          inLoadedSet: incomingMessageId
            ? loadedMessageIdsRef.current.has(incomingMessageId)
            : null,
        });

        dispatch({ type: "UPDATE_MESSAGE", payload: incomingMessage });
      } else if (data.action === "update") {
        // [AckDebug] Atualização recebida porém shouldProcess=false; ajuda a
        // identificar mismatch entre o ticket aberto e o ticketId da mensagem.
        console.warn("[AckDebug] UPDATE recebido mas shouldProcess=false", {
          messageId: incomingMessage?.id,
          ack: incomingMessage?.ack,
          messageTicketId: incomingMessage?.ticketId,
          currentTicketNumericId,
          isMatchingTicket,
          isMatchingContact,
          alreadyLoadedMessage,
          messageHistoryMode,
        });
      }
    };

    const handleMessageQueueFailed = (data) => {
      const failedTicketId = parseInt(data?.ticketId);
      const currentId = parseInt(currentTicketId.current);
      const isCurrentTicket = failedTicketId === currentId;
      const isMine =
        !!data?.userId && Number(data.userId) === Number(currentUserIdRef.current);

      // Antes a bolha só sumia e o atendente não sabia que a mensagem não
      // saiu. Avisa quem enviou (mesmo em outro chamado); sem userId (evento
      // antigo), avisa quem está com o chamado aberto. toastId evita repetir.
      if (isMine || (isCurrentTicket && !data?.userId)) {
        const reason =
          data?.error && i18n.exists(`backendErrors.${data.error}`)
            ? i18n.t(`backendErrors.${data.error}`)
            : i18n.t("messagesList.queueFailed.genericReason");
        toast.error(
          i18n.t("messagesList.queueFailed.message", {
            preview: data?.bodyPreview || "",
            reason,
          }),
          {
            toastId: `queue-failed-${data?.optimisticId || failedTicketId}`,
            autoClose: false,
          }
        );
      }

      // Quem enviou tira a bolha mesmo estando em outro chamado (senão o
      // relógio ficava lá ao voltar).
      if (!isCurrentTicket && !isMine) {
        return;
      }

      if (data?.optimisticId) {
        removeOptimisticMessage(failedTicketId, data.optimisticId);
        removePendingMessageByOptimisticId(data.optimisticId);
      }
    };

    // Função para se inscrever nas salas
    const joinRooms = () => {
      const activeTicketId = ticketId || ticket?.id;
      if (activeTicketId) {
        socket.emit("joinChatBox", `${activeTicketId}`);
      }
      // Também inscrever na sala do contato apenas quando o histórico é por contato
      const ticketContactId = ticket?.contactId || contactId;
      if (ticketContactId && (messageHistoryMode === "contact" || groupSharedHistory)) {
        socket.emit("joinChatBox", `contact-${ticketContactId}`);
      }
    };

    // Inscrever nas salas quando o socket estiver pronto. O ManagedSocket
    // dispara na hora se o servidor já deu "ready" nesta conexão; senão espera
    // o "ready" (que só vem depois de o backend registrar os handlers — um
    // emit antes disso é descartado). Por isso NÃO há mais o atalho
    // `if (socket.CONNECTED) joinRooms()`: conectado ≠ pronto, e o join
    // duplicado era deduplicado no wrapper justamente quando o primeiro se
    // perdia (21/09/2026).
    socket.on("ready", joinRooms);

    // Conexão excluída: quem não é admin perde na hora as mensagens dos chamados
    // dela que já estavam carregadas (o backend já não as devolve).
    const handleTicketEvent = (data) => {
      if (data?.action !== "connectionInactivated" || !data.whatsappId) return;
      if (socketCallbacksRef.current.isAdmin) return;

      const currentTicket = socketCallbacksRef.current.ticket;
      const isCurrentTicketFromConnection =
        Number(currentTicket?.whatsappId) === Number(data.whatsappId);

      dispatch({
        type: "REMOVE_MESSAGES_BY_WHATSAPP",
        payload: {
          whatsappId: data.whatsappId,
          ticketId: isCurrentTicketFromConnection ? currentTicket?.id : null,
        },
      });
    };

    socket.on(`company-${companyId}-appMessage`, handleAppMessage);
    socket.on(`company-${companyId}-messageQueueFailed`, handleMessageQueueFailed);
    socket.on(`company-${companyId}-ticket`, handleTicketEvent);

    return () => {
      // ✅ ManagedSocket.disconnect() remove APENAS os listeners e joins DESTE socket gerenciado
      // Não desconecta o socket global real — apenas faz cleanup dos callbacks registrados
      socket.disconnect();
    };
    // Callbacks fora das deps de propósito: chegam por socketCallbacksRef, sempre
    // na versão mais recente. O efeito só remonta quando a sala realmente muda.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ticketId, ticket?.id, ticket?.contactId, socketManager, contactId, messageHistoryMode, groupSharedHistory, isReadOnlyMode]);

  const loadMore = () => {
    if (!hasMore || loading) {
      return;
    }

    if (isRestoringScrollRef.current || historyLoadInFlightRef.current) {
      return;
    }

    setPageNumber((prevPageNumber) => prevPageNumber + 1);
  };

  const getIsNearBottom = useCallback((container) => {
    const listContainer = container || messagesListRef.current || document.getElementById("messagesList");
    if (!listContainer) {
      return true;
    }

    const bottomGap = listContainer.scrollHeight - (listContainer.scrollTop + listContainer.clientHeight);
    return bottomGap <= 80;
  }, []);

  const syncNearBottomState = useCallback((container) => {
    const nearBottom = getIsNearBottom(container);

    isNearBottomRef.current = nearBottom;
    setIsNearBottom((prev) => (prev === nearBottom ? prev : nearBottom));

    if (nearBottom) {
      setHasUnreadWhileAwayFromBottom(false);
    }

    return nearBottom;
  }, [getIsNearBottom]);

  // Scroll instantâneo para o final (sem animação) - usado na carga inicial
  const scrollToBottomInstant = () => {
    const container = messagesListRef.current || document.getElementById("messagesList");
    if (container) {
      if (lastMessageRef.current?.isConnected) {
        lastMessageRef.current.scrollIntoView({ behavior: "auto", block: "end" });
      }
      container.scrollTop = container.scrollHeight;
      syncNearBottomState(container);
    }
  };

  // Scroll suave para o final - usado quando recebe nova mensagem
  const scrollToBottom = () => {
    const container = messagesListRef.current || document.getElementById("messagesList");

    if (lastMessageRef.current) {
      try {
        if (lastMessageRef.current.isConnected) {
          lastMessageRef.current.scrollIntoView({ behavior: "smooth" });
        }
      } catch (error) {
        console.warn("Error scrolling to bottom:", error);
      }
    }

    if (container) {
      requestAnimationFrame(() => {
        syncNearBottomState(container);
      });
    }
  };

  const handleGoToBottom = () => {
    setHasUnreadWhileAwayFromBottom(false);
    scrollToBottom();
  };

  const handleScroll = (e) => {
    const container = e.currentTarget;
    syncNearBottomState(container);

    // Ignorar scroll até que o scroll inicial seja feito
    if (!initialScrollDoneRef.current) return;
    if (isRestoringScrollRef.current) return;
    if (Date.now() < ignoreScrollUntilRef.current) return;
    const { scrollTop } = container;

    if (scrollTop === 0) {
      container.scrollTop = 1;
    }

    if (loading || !hasMore) {
      return;
    }

    if (scrollTop < 50) {
      loadMore();
    }
  };

  const handleOpenMessageOptionsMenu = (e, message) => {
    if (!isInteractionEnabled) {
      return;
    }

    if (isForwardSelectionMode) {
      handleToggleForwardSelection(e, message);
      return;
    }

    const rect = e.currentTarget?.getBoundingClientRect?.();
    if (rect) {
      setAnchorPosition({
        top: Math.round(rect.bottom),
        left: Math.round(rect.right),
      });
    }
    setAnchorEl(e.currentTarget);
    setSelectedMessage(message);
  };

  const handleMessageContextMenu = (e, message) => {
    if (!isInteractionEnabled) {
      return;
    }

    if (isForwardSelectionMode) {
      handleToggleForwardSelection(e, message);
      return;
    }

    e.preventDefault();
    setAnchorEl(null);
    setAnchorPosition({
      top: e.clientY,
      left: e.clientX,
    });
    setSelectedMessage(message);
  };

  const handleCloseMessageOptionsMenu = (options = {}) => {
    setAnchorEl(null);
    setAnchorPosition(null);
    if (!options?.preserveSelectedMessage) {
      setSelectedMessage(null);
    }
  };

  const canReactMessage = (message) => {
    if (!isInteractionEnabled) {
      return false;
    }

    // Instagram não suporta reagir a mensagens recebidas — desabilita o gatilho.
    const igMeta = parseInstagramMeta(message);
    if (igMeta && !message?.fromMe) {
      return false;
    }

    return ticket?.status !== "pending" && !message?.isDeleted && message?.mediaType !== "call_log";
  };

  const handleOpenReactionPicker = (e, message) => {
    if (!isInteractionEnabled) {
      return;
    }

    e.stopPropagation();
    setReactionAnchorEl(e.currentTarget);
    setReactionMessage(message);
  };

  const handleCloseReactionPicker = () => {
    setReactionAnchorEl(null);
    setReactionPaletteAnchorEl(null);
    setReactionMessage(null);
  };

  const handleOpenReactionPalette = (e) => {
    e.stopPropagation();
    setReactionPaletteAnchorEl(e.currentTarget);
    setReactionAnchorEl(null);
  };

  const handleCloseReactionPalette = () => {
    setReactionPaletteAnchorEl(null);
  };

  useEffect(() => {
    if (!reactionPaletteAnchorEl || !reactionPickerRootRef.current) {
      return undefined;
    }

    const root = reactionPickerRootRef.current;

    const injectHeartsIntoSymbols = () => {
      const categories = root.querySelectorAll(".emoji-mart-category");
      if (!categories || !categories.length) {
        return;
      }

      categories.forEach((categoryEl) => {
        const labelSpan = categoryEl.querySelector(".emoji-mart-category-label span");
        if (!labelSpan) {
          return;
        }

        const labelText = (labelSpan.textContent || "").trim().toLowerCase();
        const symbolsLabel = i18n.t("messagesList.reactions.categories.symbols").trim().toLowerCase();
        if (labelText !== symbolsLabel && labelText !== "símbolos" && labelText !== "simbolos") {
          return;
        }

        const listEl = categoryEl.querySelector(".emoji-mart-category-list");
        if (!listEl) {
          return;
        }

        const existingRow = listEl.querySelector(".custom-symbol-hearts-row");
        if (existingRow) {
          return;
        }

        const rowEl = document.createElement("div");
        rowEl.className = "custom-symbol-hearts-row";
        rowEl.style.display = "flex";
        rowEl.style.alignItems = "center";
        rowEl.style.flexWrap = "wrap";
        rowEl.style.gap = "4px";
        rowEl.style.padding = "4px 8px 6px";

        SUPPLEMENTAL_HEART_REACTIONS.forEach((emoji) => {
          const buttonEl = document.createElement("button");
          buttonEl.type = "button";
          buttonEl.textContent = emoji;
          buttonEl.style.width = "24px";
          buttonEl.style.height = "24px";
          buttonEl.style.border = "none";
          buttonEl.style.background = "transparent";
          buttonEl.style.cursor = "pointer";
          buttonEl.style.padding = "0";
          buttonEl.style.fontSize = "18px";
          buttonEl.style.lineHeight = "1";

          buttonEl.addEventListener("click", (event) => {
            event.stopPropagation();
            handleSendReaction(emoji);
          });

          rowEl.appendChild(buttonEl);
        });

        listEl.prepend(rowEl);
      });
    };

    injectHeartsIntoSymbols();

    const observer = new MutationObserver(() => {
      injectHeartsIntoSymbols();
    });

    observer.observe(root, {
      childList: true,
      subtree: true,
    });

    return () => observer.disconnect();
  }, [reactionPaletteAnchorEl, reactionMessage]);

  const handleSelectReactionFromPicker = (emojiData) => {
    if (!emojiData?.native) {
      return;
    }

    handleSendReaction(emojiData.native);
  };

  const handleSendReaction = async (emoji) => {
    if (!isInteractionEnabled) return;
    if (!reactionMessage?.id) return;

    try {
      const selectedEmoji = (emoji || "").trim();
      const currentEmoji = getCurrentUserReactionEmoji(reactionMessage);
      const emojiToSend = selectedEmoji && selectedEmoji === currentEmoji ? "" : selectedEmoji;

      await api.post(`/messages/${reactionMessage.id}/reaction`, { emoji: emojiToSend });
    } catch (err) {
      toastError(err);
    } finally {
      handleCloseReactionPicker();
    }
  };

  // Proporção da mídia final, para o placeholder já nascer do tamanho certo.
  // Vem do dataJson (presente desde a criação da mensagem); sem ela, o
  // paddingTop percentual cai num 4:3 neutro.
  const getMediaAspectRatio = (message) => {
    const data = getMessageJsonData(message);
    const inner =
      data?.message?.imageMessage ||
      data?.message?.videoMessage ||
      data?.message?.ephemeralMessage?.message?.imageMessage ||
      data?.message?.viewOnceMessageV2?.message?.imageMessage ||
      data?.imageMessage ||
      data?.videoMessage ||
      null;

    const width = Number(inner?.width) || 0;
    const height = Number(inner?.height) || 0;
    if (width > 0 && height > 0) return height / width;
    return null;
  };

  // Bloco exibido enquanto o arquivo baixa em background.
  const renderMediaDownloadingBlock = (message) => {
    const mediaType = String(message.mediaType || "").toLowerCase();
    const thumbUrl = normalizeMediaUrl(getMediaThumbUrl(message));

    if (mediaType === "audio") {
      const seconds = Number(
        getMessageJsonData(message)?.message?.audioMessage?.seconds || 0
      );
      const duration = seconds
        ? `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, "0")}`
        : "";

      return (
        <div className={classes.mediaAudioPlaceholder}>
          <PlayCircleFilled fontSize="small" />
          <LinearProgress className={classes.mediaAudioPlaceholderTrack} />
          <span>{duration}</span>
        </div>
      );
    }

    if (["document", "application"].includes(mediaType)) {
      const descriptor = getDocumentDescriptor(message);
      return renderDocumentCard({ ...descriptor, status: "downloading" });
    }

    // Imagem/vídeo/figurinha: quando o WhatsApp mandou a miniatura embutida,
    // exibimos ela desfocada — é o que o WhatsApp Web faz, e resolve de uma vez
    // o "quadrado cinza" e o salto de layout, porque a proporção já é a certa.
    if (thumbUrl) {
      const ratio = getMediaAspectRatio(message);
      return (
        <div
          className={classes.mediaThumbFrame}
          style={ratio ? { width: 250, paddingTop: `${ratio * 100}%` } : { width: 250, height: 190 }}
        >
          <img
            className={classes.mediaThumbBlurred}
            style={{ position: "absolute", top: 0, left: 0 }}
            loading="lazy"
            src={thumbUrl}
            alt=""
          />
          <div className={classes.mediaThumbScrim}>
            <CircularProgress size={32} style={{ color: "#fff" }} />
            <span className={classes.mediaThumbLabel}>
              {i18n.t("messagesList.media.downloadingShort")}
            </span>
          </div>
        </div>
      );
    }

    // Sem miniatura (áudio já tratado acima, documento idem, e imagem/vídeo em
    // que o WhatsApp não mandou thumb — é sempre o caso do canal Cloud API).
    return (
      <div className={classes.mediaPlaceholderBox}>
        <CircularProgress size={28} />
        <span>{i18n.t("messagesList.media.downloading")}</span>
      </div>
    );
  };

  // Bloco de falha definitiva. O botão é o mesmo que já existia; o que muda é
  // que agora vem acompanhado do motivo amigável gravado pelo backend, e que
  // documento mostra o card com nome e tamanho em vez de um botão solto — tudo
  // isso é derivado do dataJson e continua disponível mesmo sem o arquivo.
  const renderMediaFailedBlock = (message) => {
    const mediaType = String(message.mediaType || "").toLowerCase();

    if (["document", "application"].includes(mediaType)) {
      const descriptor = getDocumentDescriptor(message);
      return (
        <>
          {renderDocumentCard({
            ...descriptor,
            status: "failed",
            retrying: retryingMediaId === message.id,
            onRetry: () => handleRetryMedia(message),
          })}
          {message.mediaDownloadError && (
            <div className={classes.mediaPlaceholderError}>
              {message.mediaDownloadError}
            </div>
          )}
        </>
      );
    }

    return (
      <>
        <div className={classes.downloadMedia}>
          <Button
            startIcon={retryingMediaId === message.id ? <CircularProgress size={16} /> : <Refresh />}
            color="primary"
            variant="outlined"
            disabled={retryingMediaId === message.id}
            onClick={() => handleRetryMedia(message)}
          >
            {i18n.t("messagesList.media.retry")}
          </Button>
        </div>
        {message.mediaDownloadError && (
          <div className={classes.mediaPlaceholderError}>
            {message.mediaDownloadError}
          </div>
        )}
        <Divider />
      </>
    );
  };

  // Visualizador de mídias da conversa (anterior/próxima). A lista navegável vem
  // do servidor com o MESMO escopo do histórico desta tela — o usuário nunca
  // navega até o que a lista esconde. No modal de Solicitação (allowedMessageIds)
  // a navegação fica nas mensagens marcadas, já carregadas.
  const isMediaViewerOpen = Boolean(mediaViewer);
  const contactFilterTicketId = contactHistoryFilter?.ticketId;
  const contactFilterWhatsappId = contactHistoryFilter?.whatsappId;
  const contactFilterViewAll = contactHistoryFilter?.viewAll;
  const mediaGallerySource = useMemo(() => {
    if (contactId) {
      return {
        kind: "contact",
        contactId,
        filter: {
          ticketId: contactFilterTicketId,
          whatsappId: contactFilterWhatsappId,
          viewAll: contactFilterViewAll,
        },
      };
    }
    const activeTicketId = ticketId || ticket?.id;
    return activeTicketId ? { kind: "ticket", ticketId: activeTicketId } : null;
  }, [contactId, contactFilterTicketId, contactFilterWhatsappId, contactFilterViewAll, ticketId, ticket?.id]);

  // Mesmo critério da galeria do backend: foto/vídeo com arquivo, não apagada.
  const isGalleryMessage = (message) =>
    ["image", "video"].includes(message?.mediaType)
    && !message.isDeleted
    && !isStickerMessage(message)
    && Boolean(getMediaUrl(message))
    && !isMediaDownloadingFor(message);

  const toViewerItem = (message) =>
    toMediaGalleryItem(message, {
      url: getMediaUrl(message),
      thumbUrl: getMediaThumbUrl(message),
    });

  const localGalleryItems = useMemo(() => {
    if (!allowedMessageIdSet || !isMediaViewerOpen) return undefined;
    return displayedMessages.filter(isGalleryMessage).map(toViewerItem);
    // isGalleryMessage/toViewerItem leem mediaUrlOverrides e o estado de download.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allowedMessageIdSet, isMediaViewerOpen, displayedMessages, mediaUrlOverrides]);

  // `single`: item fora da conversa (status citado) ou que a galeria não lista
  // (apagada revelada) — abre sozinho, sem navegação.
  const openMediaViewer = (message, { single = false, url, mediaType } = {}) => {
    if (!message?.id) return;
    setMediaViewer({
      initialItem: toMediaGalleryItem(message, {
        url: url || getMediaUrl(message),
        thumbUrl: getMediaThumbUrl(message),
        mediaType,
      }),
      single: single || !isGalleryMessage(message),
    });
  };

  const closeMediaViewer = useCallback(() => setMediaViewer(null), []);

  const handleMediaViewerGoToMessage = (item) => {
    startJumpToMessage(item.id, { fallbackTicketId: item.ticketId || null });
  };

  const checkMessageMedia = (message) => {
    const mediaUrl = getMediaUrl(message);
    const mediaFilename = message.mediaName || getMediaFilenameFromUrl(mediaUrl);
    const downloadFileName = mediaFilename || "arquivo";
    const fileExtension = getFileExtension(mediaFilename);
    const isPdfFile = PDF_EXTENSIONS.includes(fileExtension);
    const isTextFile = TEXT_PREVIEW_EXTENSIONS.includes(fileExtension);
    const isDirectDownloadFile = DIRECT_DOWNLOAD_EXTENSIONS.includes(fileExtension);
    const isDocumentMedia = ["document", "application"].includes(String(message.mediaType || "").toLowerCase());
    const shouldShowRetryButton = isInteractionEnabled && (!mediaUrl || Boolean(mediaLoadErrors[message.id]));

    // Mídia ainda baixando em background. Vem ANTES do guard de readOnly de
    // propósito: o placeholder é informação, não ação — mesmo numa visualização
    // somente-leitura o atendente precisa saber que há um arquivo a caminho.
    if (isMediaDownloadingFor(message)) {
      return renderMediaDownloadingBlock(message);
    }

    // Autor: GitHub Copilot | Data: 28/03/2026 | Motivo: locationMessage nao depende de mediaUrl;
    // sem este filtro a UI mostrava apenas "Tentar novamente" para localizacao.
    if (!mediaUrl && message.mediaType && message.mediaType !== "chat" && message.mediaType !== "locationMessage" && message.mediaType !== "liveLocationMessage") {
      if (isReadOnlyMode) {
        return null;
      }

      return renderMediaFailedBlock(message);
    }

    if ((message.mediaType === "locationMessage" || message.mediaType === "liveLocationMessage") && typeof message.body === "string" && message.body.split('|').length >= 2) {
      let locationParts = message.body.split('|')
      let imageLocation = locationParts[0]
      let linkLocation = locationParts[1]

      let descriptionLocation = null

      if (locationParts.length > 2)
        descriptionLocation = message.body.split('|')[2]

      return <LocationPreview image={imageLocation} link={linkLocation} description={descriptionLocation} />
    }
    /* else if (message.mediaType === "vcard") {
      let array = message.body.split("\n");
      let obj = [];
      let contact = "";
      for (let index = 0; index < array.length; index++) {
        const v = array[index];
        let values = v.split(":");
        for (let ind = 0; ind < values.length; ind++) {
          if (values[ind].indexOf("+") !== -1) {
            obj.push({ number: values[ind] });
          }
          if (values[ind].indexOf("FN") !== -1) {
            contact = values[ind + 1];
          }
        }
      }
      return <VcardPreview contact={contact} numbers={obj[0].number} />
    } */
    /*else if (message.mediaType === "multi_vcard") {
      console.log("multi_vcard")
      console.log(message)
    	
      if(message.body !== null && message.body !== "") {
        let newBody = JSON.parse(message.body)
        return (
          <>
            {
            newBody.map(v => (
              <VcardPreview contact={v.name} numbers={v.number} />
            ))
            }
          </>
        )
      } else return (<></>)
    }*/
    else if (message.mediaType === "image" || message.mediaType === "sticker" || message.mediaType === "stickerMessage") {
      return (
        <ModalImageCors
          imageUrl={mediaUrl}
          messageId={message.id}
          isSticker={isStickerMessage(message)}
          // Reusa a miniatura para não voltar à caixa cinza enquanto o blob da
          // imagem real carrega.
          placeholderSrc={normalizeMediaUrl(getMediaThumbUrl(message)) || undefined}
          onOpen={() => openMediaViewer(message)}
        />
      );
    } else if (message.mediaType === "audio") {
      return (
        <>
          {/* preload="metadata": só o cabeçalho do arquivo na abertura (o erro de
              mídia ainda é detectado); o resto baixa ao dar play. */}
          <audio controls preload="metadata" onError={() => markMediaLoadError(message.id)} onLoadedData={() => clearMediaLoadError(message.id)}>
            <source src={mediaUrl} type="audio/ogg"></source>
          </audio>
          {message.transcriptionStatus === "processing" ? (
            <div className={classes.audioTranscriptionError}>
              {i18n.t("messagesList.transcription.processing")}
            </div>
          ) : message.transcription && (
            message.transcriptionStatus === "failed" ? (
              <>
                <div className={classes.audioTranscriptionError}>
                  {message.transcription}
                </div>
                {isInteractionEnabled && (
                  <Button
                    size="small"
                    className={classes.audioTranscriptionRetry}
                    startIcon={<Refresh fontSize="small" />}
                    onClick={() => handleRetryTranscription(message)}
                  >
                    {i18n.t("messagesList.transcription.retry")}
                  </Button>
                )}
              </>
            ) : (
              <div className={classes.audioTranscription}>
                <span className={classes.audioTranscriptionLabel}>
                  {i18n.t("messagesList.transcription.label")}
                </span>
                {message.transcription}
              </div>
            )
          )}
          {shouldShowRetryButton && (
            <div className={classes.downloadMedia}>
              <Button
                startIcon={retryingMediaId === message.id ? <CircularProgress size={16} /> : <Refresh />}
                color="primary"
                variant="outlined"
                disabled={retryingMediaId === message.id}
                onClick={() => handleRetryMedia(message)}
              >
                {i18n.t("messagesList.media.retry")}
              </Button>
            </div>
          )}
        </>
      );
    } else if (message.mediaType === "video") {
      return (
        <>
          <div className={classes.videoMediaFrame}>
            <video
              className={classes.messageMedia}
              src={mediaUrl}
              // A miniatura embutida também serve de poster: sem ela, um <video>
              // com width/height automáticos colapsa para os 300x150 padrão do
              // navegador até os metadados carregarem — um salto que já existia.
              poster={normalizeMediaUrl(getMediaThumbUrl(message)) || undefined}
              controls
              preload="metadata"
              onError={() => markMediaLoadError(message.id)}
              onLoadedData={() => clearMediaLoadError(message.id)}
            />
            <Tooltip title={i18n.t("messagesList.mediaViewer.open")}>
              <IconButton
                size="small"
                className={classes.videoViewerButton}
                aria-label={i18n.t("messagesList.mediaViewer.open")}
                onClick={(event) => {
                  // O clique não pode subir para a bolha (citação, menu).
                  event.stopPropagation();
                  openMediaViewer(message);
                }}
              >
                <ZoomOutMap fontSize="small" />
              </IconButton>
            </Tooltip>
          </div>
          {shouldShowRetryButton && (
            <div className={classes.downloadMedia}>
              <Button
                startIcon={retryingMediaId === message.id ? <CircularProgress size={16} /> : <Refresh />}
                color="primary"
                variant="outlined"
                disabled={retryingMediaId === message.id}
                onClick={() => handleRetryMedia(message)}
              >
                {i18n.t("messagesList.media.retry")}
              </Button>
            </div>
          )}
        </>
      );
    } else if (isDocumentMedia) {
      return (
        <>
          {(isPdfFile || isTextFile) ? (
            <>
              <iframe
                loading="lazy"
                title={`preview-${message.id}`}
                className={classes.documentPreviewFrame}
                src={mediaUrl}
              />
              {isInteractionEnabled && (
                <div className={classes.downloadMedia}>
                  <Button
                    startIcon={<GetApp />}
                    color="primary"
                    variant="outlined"
                    onClick={(event) => handleForceDownload(event, mediaUrl, downloadFileName)}
                  >
                    {i18n.t("messagesList.media.download")}
                  </Button>
                </div>
              )}
            </>
          ) : (
            renderDocumentCard({
              ...getDocumentDescriptor(message, mediaUrl),
              showActions: isInteractionEnabled,
              showOpenAction: isInteractionEnabled && !isDirectDownloadFile,
            })
          )}
          <Divider />
        </>
      );
    } else {
      if (!isInteractionEnabled) {
        return null;
      }

      return (
        <>
          <div className={classes.downloadMedia}>
            <Button
              startIcon={<GetApp />}
              color="primary"
              variant="outlined"
              onClick={(event) => handleForceDownload(event, mediaUrl, downloadFileName)}
            >
              {i18n.t("messagesList.media.download")}
            </Button>
          </div>
          <Divider />
        </>
      );
    }
  };

  // Numa resposta a bolha fica com a largura da citação, quase sempre maior que
  // a figurinha/imagem enviada — sem centralizar, a mídia encosta na esquerda e
  // sobra um vão branco do lado direito.
  const shouldCenterMedia = (message) =>
    Boolean(message?.quotedMsg)
    && (isStickerMessage(message) || ["image", "video"].includes(message?.mediaType));

  const renderMessageMediaBlock = (message) => {
    const media = isStickerMessage(message)
      ? checkMessageMedia(message)
      : <div className={classes.messageMediaBleed}>{checkMessageMedia(message)}</div>;

    if (!shouldCenterMedia(message)) {
      return media;
    }

    return <div className={classes.centeredMediaWrapper}>{media}</div>;
  };

  const [openErrorTooltip, setOpenErrorTooltip] = useState(null);

  const getDeliveryError = (message) => {
    try {
      if (message.dataJson) {
        const data = typeof message.dataJson === 'string' ? JSON.parse(message.dataJson) : message.dataJson;
        if (data.deliveryError) {
          return data.deliveryErrorCode
            ? i18n.t("messagesList.delivery.failedWithCode", { error: data.deliveryError, code: data.deliveryErrorCode })
            : i18n.t("messagesList.delivery.failedWithError", { error: data.deliveryError });
        }
      }
    } catch (e) {
      // ignore parse errors
    }
    return i18n.t("messagesList.delivery.failed");
  };

  const isPendingAckStale = (message) => {
    if (message?.ack !== 1 || !message?.createdAt) {
      return false;
    }

    try {
      const createdAt = parseISO(message.createdAt);
      if (Number.isNaN(createdAt.getTime())) {
        return false;
      }

      const now = new Date();
      const diffMs = now.getTime() - createdAt.getTime();
      return diffMs >= ACK_PENDING_FALLBACK_MINUTES * 60 * 1000;
    } catch (error) {
      return false;
    }
  };

  const getPendingAckMessage = (message) => {
    let metaMessageId = null;
    let pendingStatusTimeout = false;
    let pendingStatusTimeoutMinutes = null;
    let pendingStatusTimeoutReason = null;
    let pendingStatusTimeoutHandled = false;

    try {
      if (message?.dataJson) {
        const data = typeof message.dataJson === "string" ? JSON.parse(message.dataJson) : message.dataJson;
        metaMessageId = data?.metaMessageId || null;
        pendingStatusTimeout = Boolean(data?.pendingStatusTimeout);
        pendingStatusTimeoutMinutes = data?.pendingStatusTimeoutMinutes || null;
        pendingStatusTimeoutReason = data?.pendingStatusTimeoutReason || null;
        pendingStatusTimeoutHandled = data?.pendingStatusTimeoutHandled === true || data?.pendingStatusTimeoutHandled === "true";
      }
    } catch (error) {
      metaMessageId = null;
    }

    if (pendingStatusTimeoutHandled) {
      return i18n.t("messagesList.pendingAck.timeoutHandled");
    }

    if (pendingStatusTimeout) {
      const reason = pendingStatusTimeoutReason || i18n.t("messagesList.pendingAck.defaultReason");
      return pendingStatusTimeoutMinutes
        ? i18n.t("messagesList.pendingAck.timeoutMinutes", { minutes: pendingStatusTimeoutMinutes, reason })
        : i18n.t("messagesList.pendingAck.timeoutLong", { reason });
    }

    if (metaMessageId) {
      return i18n.t("messagesList.pendingAck.awaitingMeta");
    }

    return i18n.t("messagesList.pendingAck.processing");
  };

  const getMessageJsonData = (message) => {
    try {
      if (!message?.dataJson) {
        return {};
      }
      return typeof message.dataJson === "string" ? JSON.parse(message.dataJson) : message.dataJson;
    } catch (error) {
      return {};
    }
  };

  // Marcação de encaminhada que o WhatsApp já manda e fica gravada no dataJson.
  // Cloud API: rawMessage.context.{forwarded,frequently_forwarded}.
  // Baileys: <tipo>.contextInfo.{isForwarded,forwardingScore}, às vezes dentro
  // de um envelope (ephemeral/viewOnce/documentWithCaption) — daí a descida
  // por .message, sem listar tipo por tipo.
  // Envio pela Cloud API com o botão Encaminhar: a Meta não marca, então o
  // backend grava isForwarded no dataJson e a marca é só interna.
  const getForwardInfo = (message) => {
    const data = getMessageJsonData(message);

    if (data?.isForwarded === true) {
      return { frequently: false };
    }

    const cloudContext = data?.rawMessage?.context;
    if (cloudContext?.forwarded || cloudContext?.frequently_forwarded) {
      return { frequently: !!cloudContext.frequently_forwarded };
    }

    const findContextInfo = (node, depth) => {
      if (!node || typeof node !== "object" || depth > 3) return null;
      for (const value of Object.values(node)) {
        if (!value || typeof value !== "object") continue;
        if (value.contextInfo) return value.contextInfo;
        const nested = findContextInfo(value.message, depth + 1);
        if (nested) return nested;
      }
      return null;
    };

    const contextInfo = findContextInfo(data?.message, 0);
    if (contextInfo?.isForwarded) {
      return { frequently: Number(contextInfo.forwardingScore) >= 5 };
    }

    return null;
  };

  const getReactionActorKey = (reaction) => {
    if (reaction?.fromMe) {
      return "__from_me__";
    }

    const actorId = String(reaction?.actorId || "").trim();
    if (actorId && actorId !== "__from_me__") {
      return `id:${actorId}`;
    }

    const actorName = String(reaction?.actorName || "").trim().toLowerCase();
    if (actorName) {
      return `name:${actorName}`;
    }

    return `source:${reaction?.source || "unknown"}:unknown`;
  };

  const getMessageReactions = (message) => {
    const data = getMessageJsonData(message);
    const reactions = Array.isArray(data?.reactions) ? data.reactions : [];

    if (!reactions.length) {
      return [];
    }

    const dedupedByActor = new Map();

    reactions.forEach((reaction) => {
      if (!reaction || !reaction.emoji) {
        return;
      }

      const actorKey = getReactionActorKey(reaction);
      dedupedByActor.set(actorKey, reaction);
    });

    return Array.from(dedupedByActor.values());
  };

  const getCurrentUserReactionEmoji = (message) => {
    const reactions = getMessageReactions(message);
    const currentUserReaction = reactions.find((reaction) => {
      if (!reaction) return false;
      if (reaction.fromMe === true) return true;

      const actorId = String(reaction.actorId || "").trim();
      return actorId === `agent:${user.id}` || actorId === "__from_me__";
    });

    return String(currentUserReaction?.emoji || "").trim();
  };

  const getGroupedReactions = (message) => {
    const reactions = getMessageReactions(message);
    if (!reactions.length) {
      return [];
    }

    const grouped = reactions.reduce((acc, reaction) => {
      const emoji = reaction?.emoji;
      if (!emoji) {
        return acc;
      }

      if (!acc[emoji]) {
        acc[emoji] = 0;
      }

      acc[emoji] += 1;
      return acc;
    }, {});

    return Object.entries(grouped).map(([emoji, count]) => ({ emoji, count }));
  };

  const hasMessageReactions = (message) => getGroupedReactions(message).length > 0;

  const getReactionActorName = (reaction) => {
    if (reaction?.fromMe) {
      return i18n.t("messagesList.you");
    }

    return reaction?.actorName || reaction?.actorId || i18n.t("messagesList.contactFallback");
  };

  const getInitials = (value) => {
    if (!value) return "?";
    const words = String(value).trim().split(/\s+/).filter(Boolean);
    if (!words.length) return "?";
    if (words.length === 1) return words[0].charAt(0).toUpperCase();
    return `${words[0].charAt(0)}${words[words.length - 1].charAt(0)}`.toUpperCase();
  };

  const getReactionTabs = (message) => {
    const groupedReactions = getGroupedReactions(message);
    const total = groupedReactions.reduce((acc, item) => acc + item.count, 0);

    return [
      {
        key: "all",
        label: i18n.t("messagesList.reactions.allTab", { total }),
      },
      ...groupedReactions.map(({ emoji, count }) => ({
        key: emoji,
        label: `${emoji} ${count}`,
      })),
    ];
  };

  const getFilteredReactions = (message, filter) => {
    const reactions = getMessageReactions(message);
    if (filter === "all") {
      return reactions;
    }

    return reactions.filter((reaction) => reaction?.emoji === filter);
  };

  const handleOpenReactionDetails = (event, message, initialFilter = "all") => {
    if (!isInteractionEnabled) {
      return;
    }

    event.stopPropagation();
    setReactionDetailsAnchorEl(event.currentTarget);
    setReactionDetailsMessage(message);
    setReactionDetailsFilter(initialFilter);
  };

  const handleReactionChipClick = async (event, message, emoji) => {
    if (!isInteractionEnabled) {
      return;
    }

    event.stopPropagation();

    const selectedEmoji = String(emoji || "").trim();
    const currentEmoji = getCurrentUserReactionEmoji(message);

    if (selectedEmoji && selectedEmoji === currentEmoji) {
      try {
        await api.post(`/messages/${message.id}/reaction`, { emoji: "" });
      } catch (err) {
        toastError(err);
      }
      return;
    }

    handleOpenReactionDetails(event, message, emoji);
  };

  const handleCloseReactionDetails = () => {
    setReactionDetailsAnchorEl(null);
    setReactionDetailsMessage(null);
    setReactionDetailsFilter("all");
  };

  const renderMessageReactions = (message) => {
    const groupedReactions = getGroupedReactions(message);
    if (!groupedReactions.length) {
      return null;
    }

    return (
      <div
        className={clsx(classes.reactionsBar, {
          [classes.reactionsBarLeft]: !message.fromMe,
          [classes.reactionsBarRight]: message.fromMe,
        })}
      >
        {groupedReactions.map(({ emoji, count }) => (
          <button
            type="button"
            key={`${message.id}-${emoji}`}
            className={classes.reactionChip}
            onClick={isInteractionEnabled ? (e) => handleReactionChipClick(e, message, emoji) : undefined}
          >
            {emoji}{count > 1 ? ` ${count}` : ""}
          </button>
        ))}
      </div>
    );
  };

  const renderReactionTrigger = (message) => {
    if (!canReactMessage(message)) {
      return null;
    }

    return (
      <IconButton
        size="small"
        className={clsx(classes.reactionTriggerButton, {
          [classes.reactionTriggerButtonLeft]: !message.fromMe,
          [classes.reactionTriggerButtonRight]: message.fromMe,
        })}
        onClick={(e) => handleOpenReactionPicker(e, message)}
      >
        <Mood fontSize="small" />
      </IconButton>
    );
  };

  const renderMessageOptionsButton = (message) => {
    if (!isInteractionEnabled) {
      return null;
    }

    return (
      <IconButton
        variant="contained"
        size="small"
        id="messageActionsButton"
        disabled={message.isDeleted}
        className={classes.messageActionsButton}
        onClick={(e) => handleOpenMessageOptionsMenu(e, message)}
      >
        <ExpandMore />
      </IconButton>
    );
  };

  const renderPendingAckTooltipContent = (message) => {
    const data = getMessageJsonData(message);
    const timeoutHandled = data?.pendingStatusTimeoutHandled === true || data?.pendingStatusTimeoutHandled === "true";

    return (
      <div>
        <div>{getPendingAckMessage(message)}</div>
        {timeoutHandled && (
          <div style={{ marginTop: 8, fontSize: 12, opacity: 0.9 }}>
            {i18n.t("messagesList.pendingAck.timeoutHandledShort")}
          </div>
        )}
      </div>
    );
  };

  const renderMessageAck = (message) => {
    const isMediaMessage = Boolean(
      message?.mediaUrl ||
      ["image", "video", "audio", "ptt", "document", "sticker"].includes(String(message?.mediaType || "").toLowerCase())
    );

    if (message.ack === -1) {
      const errorText = getDeliveryError(message);
      return (
        <ClickAwayListener onClickAway={() => setOpenErrorTooltip(null)}>
          <Tooltip
            classes={{ tooltip: classes.ackErrorTooltip }}
            onClose={() => setOpenErrorTooltip(null)}
            open={openErrorTooltip === message.id}
            disableFocusListener
            disableHoverListener
            disableTouchListener
            title={errorText}
            arrow
          >
            <span
              onClick={(e) => {
                e.stopPropagation();
                setOpenErrorTooltip(openErrorTooltip === message.id ? null : message.id);
              }}
              style={{ display: "inline-flex", alignItems: "center" }}
            >
              <ErrorOutline fontSize="small" className={classes.ackErrorIcon} />
            </span>
          </Tooltip>
        </ClickAwayListener>
      );
    }
    if (message.ack === 0 || message.ack === 1) {
      if (message.ack === 1 && isMediaMessage) {
        return <Done fontSize="small" className={classes.ackIcons} />;
      }

      if (isPendingAckStale(message)) {
        return (
          <ClickAwayListener onClickAway={() => setOpenErrorTooltip(null)}>
            <Tooltip
              classes={{ tooltip: classes.ackPendingTooltip }}
              onClose={() => setOpenErrorTooltip(null)}
              open={openErrorTooltip === message.id}
              disableFocusListener
              disableHoverListener
              disableTouchListener
              title={renderPendingAckTooltipContent(message)}
              arrow
            >
              <span
                onClick={(e) => {
                  e.stopPropagation();
                  setOpenErrorTooltip(openErrorTooltip === message.id ? null : message.id);
                }}
                style={{ display: "inline-flex", alignItems: "center" }}
              >
                <AccessTime fontSize="small" className={clsx(classes.ackIcons, classes.ackPendingStaleIcon)} />
              </span>
            </Tooltip>
          </ClickAwayListener>
        );
      }

      return <AccessTime fontSize="small" className={classes.ackIcons} />;
    }
    if (message.ack === 2) {
      return <Done fontSize="small" className={classes.ackIcons} />;
    }
    if (message.ack === 3) {
      return <DoneAll fontSize="small" className={classes.ackIcons} />;
    }
    if (message.ack === 4 || message.ack === 5) {
      return <DoneAll fontSize="small" className={classes.ackDoneAllIcon} />;
    }
  };

  // Chip da conexão ao lado do horário da mensagem (modo "por contato"), com o
  // mesmo visual da lista de chamados: quadradinho na cor da conexão + ícone do
  // canal. Tooltip mostra o nome da conexão e o número, quando disponíveis.
  const renderConnectionBadge = (message) => {
    // Também no histórico compartilhado do grupo (um chamado por conexão):
    // mensagens de várias conexões na mesma tela precisam do selo.
    if (messageHistoryMode !== "contact" && !groupSharedHistory) return null;

    // Enviada por OUTRA conexão da empresa (ex.: celular de outro número no
    // grupo): o backend resolve `sentByWhatsappId` pelo participant; sem ele,
    // a conexão do ticket. O campo só vem em mensagem fromMe — vale também
    // quando a tela a desenha como de participante (sentByOtherConnection).
    const sentById = Number(message?.sentByWhatsappId) || 0;
    const sentByConnection = sentById
      ? (companyWhatsApps || []).find((w) => Number(w.id) === sentById)
      : null;
    const connection = sentByConnection || message?.ticket?.whatsapp;
    const connectionId = connection?.id || message?.ticket?.whatsappId;
    if (!connectionId) return null;

    const tooltipParts = [];
    if (connection?.name) tooltipParts.push(connection.name);
    if (connection?.connectedNumber) tooltipParts.push(connection.connectedNumber);
    const tooltipTitle = tooltipParts.length
      ? i18n.t("messagesList.connectionBadge.withDetails", { details: tooltipParts.join(" • ") })
      : i18n.t("messagesList.connectionBadge.withId", { id: connectionId });

    const connectionIcon = (
      <ChannelIcon type={connection?.type} style={{ fontSize: 11 }} />
    );

    return (
      <Tooltip title={tooltipTitle} arrow placement="top">
        <span
          className={classes.connectionBadge}
          style={{ backgroundColor: connection?.color || "#7c7c7c" }}
        >
          {connectionIcon}
        </span>
      </Tooltip>
    );
  };

  const renderDailyTimestamps = (message, index) => {
    if (index === 0) {
      // Verifica se a primeira mensagem é de um ticket diferente (histórico)
      const isHistoricalMessage = message.ticketId !== ticketId;
      
      return (
        <React.Fragment>
          {isHistoricalMessage && (
            <div className={classes.ticketNunberOpen}>
              {i18n.t("messagesList.historyLoaded")}
            </div>
          )}
          <span
            className={classes.dailyTimestamp}
            key={`timestamp-${message.id}`}
          >
            <div className={classes.dailyTimestampText}>
              {safeFormatDate(displayedMessages[index].createdAt, "dd/MM/yyyy")}
            </div>
          </span>
        </React.Fragment>
      );
    }
    if (index < displayedMessages.length - 1) {
      let messageDay = parseISO(displayedMessages[index].createdAt);
      let previousMessageDay = parseISO(displayedMessages[index - 1].createdAt);

      if (!isSameDay(messageDay, previousMessageDay)) {
        return (
          <span
            className={classes.dailyTimestamp}
            key={`timestamp-${message.id}`}
          >
            <div className={classes.dailyTimestampText}>
                {safeFormatDate(displayedMessages[index].createdAt, "dd/MM/yyyy")}
            </div>
          </span>
        );
      }
    }
    if (index === displayedMessages.length - 1) {
      return (
        <div
          key={`ref-${message.createdAt}`}
          ref={lastMessageRef}
          style={{ float: "left", clear: "both" }}
        />
      );
    }
  };

  // Mesma condição do divisor de data acima (inclusive o corte na última
  // mensagem, que troca o divisor pela âncora de scroll). Precisa ser idêntica:
  // se um divisor entra no meio, os chips não são vizinhos e não podem colar.
  const hasDayDividerBefore = (index) =>
    index > 0 &&
    index < displayedMessages.length - 1 &&
    !isSameDay(
      parseISO(displayedMessages[index].createdAt),
      parseISO(displayedMessages[index - 1].createdAt)
    );

  // Chip de sistema encostado no chip de sistema anterior: recebe os
  // modificadores que zeram a margem de topo.
  const isStackedOnSystem = (index) =>
    index > 0 &&
    isSystemMessage(displayedMessages[index - 1]) &&
    !hasDayDividerBefore(index);

  const renderNumberTicket = (message, index) => {
    if (index < displayedMessages.length && index > 0) {

      let messageTicket = message.ticketId;
      let ticketUserName = message.ticket?.user?.name;
      const previousMessage = displayedMessages[index - 1];

      // Linha sem chamado (campanha / agendamento degradado): não é abertura de
      // chamado. Uma sequência delas (a mesma campanha disparada em dias
      // seguidos) ganha um único chip, no começo.
      if (hasNoTicket(message)) {
        if (hasNoTicket(previousMessage)) return null;

        const formattedDate = formatChipDateTime(message.createdAt);
        const noTicketLabel = getNoTicketChipLabel(message, formattedDate);
        const stacked = isStackedOnSystem(index);

        return (
          <div
            key={`ticket-${message.id}`}
            className={clsx(classes.systemMessageRow, {
              [classes.systemMessageRowStacked]: stacked,
            })}
          >
            <div
              className={clsx(classes.ticketNunberOpen, {
                [classes.systemChipStacked]: stacked,
              })}
            >
              {noTicketLabel}
            </div>
          </div>
        );
      }

      // Compara com o último chamado visto, pulando as linhas sem chamado, para
      // não repetir o "Chamado aberto" quando o chamado volta depois da campanha.
      const previousMessageTicket = findPreviousTicketIdIn(displayedMessages, index);

      if (messageTicket !== previousMessageTicket) {
        const formattedDate = formatChipDateTime(message.createdAt);
        const hasAssignedUser = Boolean(String(ticketUserName || "").trim());
        // openedBy vem do backend; tickets antigos não têm o campo e caem na inferência pelo atendente.
        const openedByAgent = message.ticket?.openedBy === "agent";
        // `ticket: null` explícito é o LEFT JOIN do backend sem linha em Tickets
        // (chamado apagado — Messages não tem FK). Chave ausente (undefined) é só
        // um update parcial sem o include, e segue no rótulo genérico.
        const ticketMissing = message.ticket === null;
        const chipParams = { ticket: messageTicket, date: formattedDate, user: ticketUserName };
        // Chamado finalizado criado pela importação do histórico do celular
        // (coexistência da Cloud API): ninguém "abriu" esse chamado.
        const openedByImport = message.ticket?.openedBy === "import";
        const ticketLabel = ticketMissing
          ? i18n.t("messagesList.chips.ticketMissing", chipParams)
          : openedByImport
          ? i18n.t("messagesList.chips.openedByImport", chipParams)
          : openedByAgent
            ? i18n.t("messagesList.chips.openedExternal", chipParams)
            : hasAssignedUser
              ? i18n.t("messagesList.chips.openedByAgent", chipParams)
              : i18n.t("messagesList.chips.openedByCustomer", chipParams);

        // Caso mais comum de chip colado em chip: "Atendimento finalizado por X"
        // (mensagem de sistema do ticket anterior) seguido deste separador.
        const stacked = isStackedOnSystem(index);

        return (
          <div
            key={`ticket-${message.id}`}
            className={clsx(classes.systemMessageRow, {
              [classes.systemMessageRowStacked]: stacked,
            })}
          >
            <div
              className={clsx(classes.ticketNunberOpen, {
                [classes.systemChipStacked]: stacked,
              })}
            >
              {ticketLabel}
            </div>
          </div>
        );
      }
    }
  };

  const renderMessageDivider = (message, index) => {
    if (index < displayedMessages.length && index > 0) {
      let messageUser = renderedMessages[index].fromMe;
      let previousMessageUser = renderedMessages[index - 1].fromMe;

      if (messageUser !== previousMessageUser) {
        return (
          <span className={classes.messageDivider} key={`divider-${message.id}`}></span>
        );
      }
    }
  };

  const getParticipantNumberFromJid = (participantJid) => {
    const localPart = String(participantJid || "").split("@")[0].split(":")[0].trim();
    return localPart || "";
  };

  const isGroupRemoteJid = (remoteJidValue) => {
    return String(remoteJidValue || "").trim().endsWith("@g.us");
  };

  const isGroupMessageContext = (messageLike, options = {}) => {
    const { parentRemoteJid } = options;
    return Boolean(
      isGroup ||
      isGroupRemoteJid(messageLike?.remoteJid) ||
      isGroupRemoteJid(parentRemoteJid)
    );
  };

  const getGroupSenderName = (messageLike, options = {}) => {
    const { parentMessageId, parentRemoteJid } = options;

    if (!isGroupMessageContext(messageLike, { parentRemoteJid }) || !messageLike || messageLike.fromMe) {
      return messageLike?.contact?.name;
    }

    // Cadeia compartilhada com a busca de mensagens (helpers/groupSenderName),
    // para que histórico e resultado da busca nunca divirjam no nome do autor.
    const resolvedName = resolveGroupSenderName(messageLike, {
      onNumberFallback: () => {
        if (process.env.NODE_ENV !== "development") return;
        const messageIdForLog = String(messageLike?.id || parentMessageId || "unknown");
        if (senderFallbackLoggedRef.current.has(messageIdForLog)) return;
        senderFallbackLoggedRef.current.add(messageIdForLog);
        console.debug("[MessagesList][GroupSenderFallback] fallback para numero do participante", {
          messageId: messageIdForLog,
          participant: messageLike?.participant,
          remoteJid: messageLike?.remoteJid,
        });
      },
    });

    return resolvedName || messageLike?.contact?.name;
  };

  const getSenderName = (message) => getGroupSenderName(message);

  // Cor consistente por participante nos grupos (estilo WhatsApp):
  // o mesmo remetente recebe sempre a mesma cor da paleta.
  const getSenderColorKey = (messageLike) => {
    const jid = String(messageLike?.participant || "").trim();
    if (jid) {
      const number = getParticipantNumberFromJid(jid);
      return number || jid.toLowerCase();
    }
    return String(getGroupSenderName(messageLike) || "").trim().toLowerCase();
  };

  const getSenderColor = (messageLike, options = {}) => {
    const { parentRemoteJid } = options;
    if (!isGroupMessageContext(messageLike, { parentRemoteJid }) || !messageLike || messageLike.fromMe) {
      return undefined;
    }

    const key = getSenderColorKey(messageLike);
    if (!key) {
      return undefined;
    }

    let hash = 0;
    for (let i = 0; i < key.length; i += 1) {
      hash = (hash * 31 + key.charCodeAt(i)) | 0;
    }
    return SENDER_COLOR_PALETTE[Math.abs(hash) % SENDER_COLOR_PALETTE.length];
  };

  // O nome do usuário logado só vale para a mensagem otimista/pendente; linha
  // persistida sem assinatura é campanha/agendamento/envio externo (ver helper).
  const getSentMessageUserName = (message) =>
    resolveSentMessageUserName(message, user?.name);

  const formatSentMessageDateTime = (value) => {
    try {
      const parsed = parseISO(String(value || ""));
      if (Number.isNaN(parsed.getTime())) return "";
      return format(parsed, "dd/MM/yyyy HH:mm");
    } catch (_err) {
      return "";
    }
  };

  const renderSentMessageMeta = (messageLike) => {
    const userName = getSentMessageUserName(messageLike);
    const dateTime = formatSentMessageDateTime(messageLike?.createdAt);
    return (
      <span className={classes.sentMessageMeta}>
        {userName}{dateTime ? ` - ${dateTime}` : ""}
      </span>
    );
  };

  const renderForwardedLabel = (message) => {
    // Apagada e não revelada: só o aviso de apagada, como no WhatsApp.
    if (message.isDeleted && !revealedDeleted[message.id]) return null;
    const forwardInfo = getForwardInfo(message);
    if (!forwardInfo) return null;
    const Icon = forwardInfo.frequently ? ReplyAll : Reply;
    return (
      <div className={classes.forwardedLabel}>
        <Icon className={classes.forwardedIcon} />
        {i18n.t(forwardInfo.frequently ? "messagesList.forwarded.frequently" : "messagesList.forwarded.label")}
      </div>
    );
  };

  // Aviso de mensagem apagada + botão para revelar/ocultar o conteúdo original.
  const renderDeletedNotice = (message) => {
    if (!message.isDeleted) return null;
    const noticeText = message.fromMe
      ? i18n.t("messagesList.deleted.byAgent")
      : i18n.t("messagesList.deleted.byContact");
    const hasOriginal = Boolean(
      message.body ||
      message.mediaUrl ||
      message.quotedMsg ||
      MEDIA_SLOT_TYPES.includes(message.mediaType)
    );
    const isRevealed = Boolean(revealedDeleted[message.id]);
    return (
      <div className="message-deleted-notice">
        <span className={classes.deletedNoticeText}>
          <Block
            color="disabled"
            fontSize="small"
            className={classes.deletedIcon}
          />
          {noticeText}
          {hasOriginal && (
            <span
              className={classes.revealDeletedButton}
              onClick={() => toggleRevealDeleted(message.id)}
              role="button"
              tabIndex={0}
            >
              {isRevealed ? i18n.t("messagesList.deleted.hide") : i18n.t("messagesList.deleted.show")}
            </span>
          )}
        </span>
      </div>
    );
  };

  // Botão/linha de lista recebido: o clique manda o título como mensagem comum.
  // Quem envia é o MessageInput, que ouve este evento — assim o texto passa pelo
  // mesmo fluxo do que é digitado (termos proibidos, janela de 24h, assumir o
  // chamado, bolha otimista).
  const handleInteractiveReply = (text) => {
    window.dispatchEvent(
      new CustomEvent("onspace:send-quick-text", {
        cancelable: true,
        detail: { text, ticketId, contactId },
      })
    );
  };

  // Voto em enquete: o backend cifra, envia e devolve a enquete atualizada pelo
  // evento `appMessage`, que é quem marca a opção na bolha.
  const handlePollVote = async (message, selectedOptions) => {
    if (pollVoteInFlightRef.current) return;

    pollVoteInFlightRef.current = true;
    setPollVotingId(message.id);
    try {
      await api.post(`/messages/${message.id}/poll-vote`, { selectedOptions });
    } catch (err) {
      toastError(err);
    } finally {
      pollVoteInFlightRef.current = false;
      setPollVotingId(null);
    }
  };

  // Botões/listas/templates/enquetes recebidos ou enviados: a estrutura vem pronta
  // do backend. Só responde ao clique o que veio do contato (botões/listas) e a
  // enquete; mensagem apagada e conversa somente leitura ficam só de exibição.
  const renderInteractiveContent = (message) => {
    const interactive = getInteractiveContent(message);
    if (!interactive) return null;

    const canInteract = !readOnly && !message.isDeleted;

    return (
      <InteractiveMessageContent
        interactive={interactive}
        onReply={canInteract && !message.fromMe ? handleInteractiveReply : undefined}
        onVote={
          canInteract && interactive.kind === "poll"
            ? (selectedOptions) => handlePollVote(message, selectedOptions)
            : undefined
        }
        busy={pollVotingId === message.id}
      />
    );
  };

  const renderQuotedMessage = (message) => {
    const quotedMediaUrl = message.quotedMsg?.mediaUrl;
    const quotedMediaType = String(message.quotedMsg?.mediaType || "").toLowerCase();
    const quotedIsDocument = ["document", "application"].includes(String(message.quotedMsg?.mediaType || "").toLowerCase());
    const quotedFilename = message.quotedMsg?.mediaName || getMediaFilenameFromUrl(quotedMediaUrl);
    const quotedExtension = getFileExtension(quotedFilename || "");
    const quotedIsPdfFile = PDF_EXTENSIONS.includes(quotedExtension);
    const quotedIsTextFile = TEXT_PREVIEW_EXTENSIONS.includes(quotedExtension);
    const quotedSharedContacts = getSharedContactsFromMessage(message.quotedMsg || {});
    // Autor: GitHub Copilot | Data: 28/03/2026 | Motivo: exibir citacao de contato de forma legivel.
    const quotedContactPreviewText = (() => {
      if (!quotedSharedContacts.length) return i18n.t("messagesList.sharedContact.shared");
      const firstContact = quotedSharedContacts[0];
      if (quotedSharedContacts.length === 1) {
        return firstContact.number
          ? i18n.t("messagesList.sharedContact.quotedWithNumber", { name: firstContact.name, number: firstContact.number })
          : i18n.t("messagesList.sharedContact.quotedName", { name: firstContact.name });
      }
      return i18n.t("messagesList.sharedContact.quotedMultiple", { count: quotedSharedContacts.length });
    })();

    // Resposta a um status (story). O backend materializa o status citado como uma
    // mensagem sintética com remoteJid "status@broadcast" e a miniatura em mediaUrl.
    // Retorna cedo: o corpo generico abaixo renderizaria a miniatura de um status de
    // video dentro de uma tag <video>.
    if (message.quotedMsg?.remoteJid === "status@broadcast") {
      const statusAuthor = message.quotedMsg?.fromMe
        ? i18n.t("messagesList.you")
        : getGroupSenderName(message.quotedMsg, { parentMessageId: message?.id, parentRemoteJid: message?.remoteJid }) || i18n.t("messagesList.contactFallback");

      return (
        <div
          className={clsx(classes.quotedContainerLeft, {
            [classes.quotedContainerRight]: message.fromMe,
          })}
        >
          <span
            className={clsx(classes.quotedSideColorLeft, {
              [classes.quotedSideColorRight]: message.quotedMsg?.fromMe,
            })}
          ></span>
          <div
            className={clsx(classes.quotedMsg, {
              [classes.quotedMsgRight]: message.fromMe,
            })}
          >
            <span className={classes.messageContactName}>
              {i18n.t("messagesList.quoted.statusAuthor", { author: statusAuthor })}
            </span>

            {quotedMediaUrl && (
              <div className={classes.quotedStatusPreview}>
                <ModalImageCors
                  imageUrl={quotedMediaUrl}
                  messageId={message.quotedMsg?.id}
                  onOpen={() => openMediaViewer(message.quotedMsg, {
                    single: true,
                    url: quotedMediaUrl,
                    mediaType: quotedMediaType,
                  })}
                />
                {quotedMediaType === "video" && (
                  <PlayCircleFilled className={classes.quotedStatusPlayIcon} />
                )}
              </div>
            )}

            {message.quotedMsg?.body}
          </div>
        </div>
      );
    }

    const canJumpToQuoted = isQuotedJumpEnabled(message);

    return (
      <div
        className={clsx(classes.quotedContainerLeft, {
          [classes.quotedContainerRight]: message.fromMe,
          [classes.quotedClickable]: canJumpToQuoted,
        })}
        {...(canJumpToQuoted && {
          role: "button",
          tabIndex: 0,
          "aria-label": i18n.t("messagesList.jumpToQuoted"),
          title: i18n.t("messagesList.jumpToQuoted"),
          onClick: (event) => handleQuotedMessageJump(event, message),
          onKeyDown: (event) => {
            if (event.key === "Enter" || event.key === " ") {
              event.preventDefault();
              handleQuotedMessageJump(event, message);
            }
          },
        })}
      >
        <span
          className={clsx(classes.quotedSideColorLeft, {
            [classes.quotedSideColorRight]: message.quotedMsg?.fromMe,
          })}
        ></span>
        <div
          className={clsx(classes.quotedMsg, {
            [classes.quotedMsgRight]: message.fromMe,
          })}
        >
          {!message.quotedMsg?.fromMe && (
            <span
              className={classes.messageContactName}
              style={{ color: getSenderColor(message.quotedMsg, { parentRemoteJid: message?.remoteJid }) }}
            >
              {getGroupSenderName(message.quotedMsg, { parentMessageId: message?.id, parentRemoteJid: message?.remoteJid })}
            </span>
          )}

          {quotedMediaType === "audio"
            && (
              <div className={classes.downloadMedia}>
                <audio controls preload="metadata">
                  <source src={message.quotedMsg.mediaUrl} type="audio/ogg"></source>
                </audio>
              </div>
            )
          }
          {quotedMediaType === "video"
            && (
              <video
                className={classes.messageMedia}
                src={message.quotedMsg.mediaUrl}
                controls
                preload="metadata"
              />
            )
          }
          {(quotedMediaType === "document" || quotedMediaType === "application") && (quotedIsPdfFile || quotedIsTextFile)
            && (
              <>
                <iframe
                  loading="lazy"
                  title={`quoted-preview-${message.quotedMsg?.id}`}
                  className={classes.documentPreviewFrame}
                  src={quotedMediaUrl}
                />
                {isInteractionEnabled && (
                  <div className={classes.downloadMedia}>
                    <Button
                      startIcon={<GetApp />}
                      color="primary"
                      variant="outlined"
                      onClick={(event) => handleForceDownload(event, quotedMediaUrl, quotedFilename || "arquivo")}
                    >
                      {i18n.t("messagesList.media.download")}
                    </Button>
                  </div>
                )}
              </>
            )
          }
          {(quotedMediaType === "document" || quotedMediaType === "application") && !(quotedIsPdfFile || quotedIsTextFile)
            && (
              renderDocumentCard({
                ...getDocumentDescriptor(message.quotedMsg, quotedMediaUrl),
                compact: true,
                showActions: false,
              })
            )
          }

          {quotedMediaType === "image"
            && (
              <ModalImageCors
                imageUrl={message.quotedMsg.mediaUrl}
                messageId={message.quotedMsg?.id}
                onOpen={() => openMediaViewer(message.quotedMsg)}
              />)
            || (quotedMediaType === "vcard" || quotedMediaType === "contactmessage" || quotedMediaType === "contactsarraymessage"
              ? quotedContactPreviewText
              : message.quotedMsg?.body)}

        </div>
      </div>
    );
  };

  const renderMessages = () => {
    if (renderedMessages.length > 0) {
      const viewMessagesList = renderedMessages.map((message, index) => {
        // Safety check for message object
        if (!message || !message.id) {
          console.warn("Invalid message object:", message);
          return null;
        }

        // Verificar se é uma mensagem de sistema (fechamento de ticket, etc.)
        // Debug: verificar se o body contém "Atendimento finalizado"
        if (message.body && message.body.includes("Atendimento finalizado")) {
          // console.log("🔒 Mensagem de fechamento encontrada:", message.id, message.body, "dataJson:", message.dataJson);
        }
        
        // Renderiza mensagens de sistema (fechamento, transferência, início de chamado)
        if (isSystemMessage(message)) {
          const stacked = isStackedOnSystem(index);

          return (
            <React.Fragment key={message.id}>
              {renderDailyTimestamps(message, index)}
              <div
                id={getMessageDomId(message.id)}
                className={clsx(classes.systemMessageRow, {
                  [classes.systemMessageRowStacked]: stacked,
                })}
              >
                <div
                  className={clsx(classes.ticketNunberClosed, {
                    [classes.systemChipStacked]: stacked,
                  })}
                  translate="no"
                  style={{
                    whiteSpace: "pre-wrap",
                    textAlign: "center",
                    boxShadow: String(highlightedMessageId) === String(message.id)
                      ? "0 0 0 2px #1976d2"
                      : undefined,
                  }}
                >
                  {isRatingSystemMessage(message)
                    ? <RatingSystemText message={message} />
                    : translateSystemMessage(message.body)}
                </div>
              </div>
            </React.Fragment>
          );
        }

        if (["contactMessage", "contactsArrayMessage", "vcard"].includes(message.mediaType)) {
          const sharedContacts = getSharedContactsFromMessage(message);

          if (!sharedContacts.length) {
            return null;
          }

          const isMultipleContacts = sharedContacts.length > 1;
          const firstContact = sharedContacts[0];
          const othersCount = Math.max(sharedContacts.length - 1, 0);
          const summaryText = isMultipleContacts
            ? i18n.t("messagesList.sharedContact.summaryOthers", { name: firstContact.name, count: othersCount })
            : firstContact.name;

          return (
            <React.Fragment key={message.id}>
              {renderDailyTimestamps(message, index)}
              {renderNumberTicket(message, index)}
              {renderMessageDivider(message, index)}
              {renderForwardSelectableBubble(message, (
                <div
                  id={getMessageDomId(message.id)}
                  className={clsx(message.fromMe ? classes.messageRight : classes.messageLeft, {
                    [classes.messageJumpHighlight]: String(highlightedMessageId) === String(message.id),
                  })}
                  onContextMenu={(e) => handleMessageContextMenu(e, message)}
                >
                  {renderReactionTrigger(message)}
                  {renderMessageOptionsButton(message)}

                  <div className={classes.contactCard}>
                    {message.fromMe && renderSentMessageMeta(message)}
                    {renderForwardedLabel(message)}
                    <div className={classes.contactCardHeader}>
                      <div className={classes.contactCardAvatar}>
                        {getInitials(firstContact.name)}
                      </div>
                      <div className={classes.contactCardInfo}>
                        <span className={classes.contactCardName}>{summaryText}</span>
                        <span className={classes.contactCardNumber}>
                          {isMultipleContacts
                            ? i18n.t("messagesList.sharedContact.count", { count: sharedContacts.length })
                            : formatPhoneNumber(firstContact.number)}
                        </span>
                      </div>
                    </div>

                    {isInteractionEnabled && (
                      <>
                        <div className={classes.contactCardDivider} />

                        <div className={classes.contactCardActions}>
                          {isMultipleContacts ? (
                            <Button
                              className={`${classes.contactCardButton} ${classes.contactCardButtonSecondary} ${classes.contactCardSingleAction}`}
                              onClick={() => handleOpenSharedContacts(sharedContacts)}
                            >
                              {i18n.t("messagesList.sharedContact.viewAll")}
                            </Button>
                          ) : (
                            <>
                              <Button
                                className={`${classes.contactCardButton} ${classes.contactCardButtonPrimary}`}
                                onClick={() => handleStartConversationWithSharedContact(firstContact)}
                                disabled={!firstContact.number}
                              >
                                {i18n.t("messagesList.sharedContact.chat")}
                              </Button>
                              <Button
                                className={`${classes.contactCardButton} ${classes.contactCardButtonSecondary}`}
                                onClick={() => handleSaveSharedContact(firstContact)}
                                disabled={!firstContact.number}
                              >
                                {i18n.t("common.save")}
                              </Button>
                            </>
                          )}
                        </div>
                      </>
                    )}

                    <div className={classes.contactCardTimestamp}>
                      {renderConnectionBadge(message)}
                      {safeFormatDate(message.createdAt, "HH:mm")}
                      {message.fromMe && renderMessageAck(message)}
                    </div>
                  </div>
                </div>
              ))}
            </React.Fragment>
          );
        }

        if (message.mediaType === "call_log") {
          let callLabel = i18n.t("messagesList.callLog.call");
          let statusLabel = i18n.t("messagesList.callLog.started");

          try {
            const data = message?.dataJson
              ? (typeof message.dataJson === "string" ? JSON.parse(message.dataJson) : message.dataJson)
              : null;

            if (data?.call?.isVideo === true) {
              callLabel = i18n.t("messagesList.callLog.video");
            } else {
              callLabel = i18n.t("messagesList.callLog.voice");
            }

            if (data?.call?.statusLabel) {
              statusLabel = data.call.statusLabel;
            }
          } catch (error) {
            // fallback para formato legivel mesmo com dataJson invalido
          }

          const callBody = String(message.body || "").trim();
          const fallbackText = i18n.t("messagesList.callLog.fallback", {
            call: callLabel,
            status: statusLabel.toLowerCase(),
            date: safeFormatDate(message.createdAt, "dd/MM/yyyy HH:mm", i18n.t("messagesList.chips.unknownDate")),
          });

          return (
            <React.Fragment key={message.id}>
              {renderDailyTimestamps(message, index)}
              {renderNumberTicket(message, index)}
              {renderMessageDivider(message, index)}
              {renderForwardSelectableBubble(message, (
                <div
                  id={getMessageDomId(message.id)}
                  className={classes.messageCenter}
                  onContextMenu={(e) => handleMessageContextMenu(e, message)}
                >
                  {renderReactionTrigger(message)}
                  {renderMessageOptionsButton(message)}
                  {isGroupMessageContext(message) && (
                    <div>
                      <span
                        className={classes.messageContactName}
                        style={{ color: getSenderColor(message) }}
                      >
                        {getSenderName(message)}:
                      </span>
                    </div>
                  )}
                  <div className={classes.ticketNunberClosed} style={{ whiteSpace: "pre-wrap", textAlign: "left" }}>
                    {callBody || fallbackText}
                  </div>
                </div>
              ))}
            </React.Fragment>
          );
        }

        if (!message.fromMe) {
          return (
            <React.Fragment key={message.id}>
              {renderDailyTimestamps(message, index)}
              {renderNumberTicket(message, index)}
              {renderMessageDivider(message, index)}
              {renderForwardSelectableBubble(message, (
                <div
                  id={getMessageDomId(message.id)}
                  translate="no"
                  className={clsx(classes.messageLeft, {
                    [classes.messageWithReactions]: hasMessageReactions(message),
                    // Figurinha em resposta mantém a bolha: sem ela a citação
                    // flutua solta e não se lê como parte da mesma mensagem.
                    [classes.messageSticker]: isStickerMessage(message) && !message.quotedMsg,
                    [classes.messageEmail]: !!message.emailHtml,
                    [classes.messageAudioCard]: isAudioBubbleMessage(message),
                    [classes.messageDocumentCard]: isDocumentCardMessage(message),
                    [classes.messageInteractiveCard]: isInteractiveCardMessage(message),
                    [classes.messageMediaOnly]: isMediaWithoutCaption(message),
                    [classes.messageJumpHighlight]: String(highlightedMessageId) === String(message.id),
                  })}
                  onContextMenu={(e) => handleMessageContextMenu(e, message)}
                >
                  {renderReactionTrigger(message)}
                  {renderMessageOptionsButton(message)}
                  {isGroupMessageContext(message) && (
                    <div>
                      <span
                        className={classes.messageContactName}
                        style={{ color: getSenderColor(message) }}
                      >
                        {getSenderName(message)}:
                      </span>
                    </div>
                  )}
                  {renderForwardedLabel(message)}

                  {message.quotedMsg && hasMediaBubble(message) && (
                    <div className={classes.quotedAboveMediaWrapper}>
                      {renderQuotedMessage(message)}
                    </div>
                  )}
                  {renderInstagramDmAttachment(message)}
                  {/* Mesma condição de hasMediaBubble — antes duplicada literalmente aqui
                      e na bolha oposta, o que garantia que uma divergisse da outra. */}
                  {hasMediaBubble(message) && renderMessageMediaBlock(message)}
                  <div className={clsx(classes.textContentItem, {
                    [classes.textContentItemDeleted]: message.isDeleted && !revealedDeleted[message.id],
                    // Reserva os 120px do "Editada HH:mm" também na bolha
                    // recebida — sem isto o selo cai sobre mensagens curtas.
                    [classes.textContentItemEdited]: message.isEdited,
                    [classes.textContentItemSticker]: isStickerMessage(message),
                  })}>
                    {/* aviso de mensagem apagada pelo contato + botão ver original */}
                    {renderDeletedNotice(message)}
                    {renderInstagramCommentTag(message)}
                    {(!message.isDeleted || revealedDeleted[message.id]) && message.quotedMsg && !hasMediaBubble(message) && renderQuotedMessage(message)}
                    {(!message.isDeleted || revealedDeleted[message.id]) && renderInteractiveContent(message)}
                    {(!message.isDeleted || revealedDeleted[message.id]) && shouldShowBodyForMessage(message) && (
                      renderBodyWithMentions(getDisplayBody(message), message)
                    )}
                    {renderMessageReactions(message)}
                    <span className={clsx(classes.timestamp, {
                      [classes.timestampSticker]: isStickerMessage(message),
                    })}>
				      {message.isEdited && (
                        <span
                          title={message.originalBody ? i18n.t("messagesList.edited.original", { body: message.originalBody }) : undefined}
                          style={{ cursor: message.originalBody ? "help" : "default" }}
                        >
                          {i18n.t("messagesList.edited.label")}{" "}
                        </span>
                      )}
                      {renderConnectionBadge(message)}
                      {safeFormatDate(message.createdAt, "HH:mm")}
                    </span>
                  </div>
                </div>
              ))}
            </React.Fragment>
          );
        } else {
          return (
            <React.Fragment key={message.id}>
              {renderDailyTimestamps(message, index)}
              {renderNumberTicket(message, index)}
              {renderMessageDivider(message, index)}
              {renderForwardSelectableBubble(message, (
                <div
                  id={getMessageDomId(message.id)}
                  translate="no"
                  className={clsx(classes.messageRight, {
                    [classes.messageWithReactions]: hasMessageReactions(message),
                    // Figurinha em resposta mantém a bolha: sem ela a citação
                    // flutua solta e não se lê como parte da mesma mensagem.
                    [classes.messageSticker]: isStickerMessage(message) && !message.quotedMsg,
                    [classes.messageEmail]: !!message.emailHtml,
                    [classes.messageAudioCard]: isAudioBubbleMessage(message),
                    [classes.messageDocumentCard]: isDocumentCardMessage(message),
                    [classes.messageInteractiveCard]: isInteractiveCardMessage(message),
                    [classes.messageMediaOnly]: isMediaWithoutCaption(message),
                    [classes.messageJumpHighlight]: String(highlightedMessageId) === String(message.id),
                  })}
                  onContextMenu={(e) => handleMessageContextMenu(e, message)}
                >
                  {renderReactionTrigger(message)}
                  {renderMessageOptionsButton(message)}
                  {renderSentMessageMeta(message)}
                  {renderForwardedLabel(message)}
                  {message.quotedMsg && hasMediaBubble(message) && (
                    <div className={classes.quotedAboveMediaWrapper}>
                      {renderQuotedMessage(message)}
                    </div>
                  )}
                  {renderInstagramDmAttachment(message)}
                  {/* Mesma condição de hasMediaBubble — antes duplicada literalmente aqui
                      e na bolha oposta, o que garantia que uma divergisse da outra. */}
                  {hasMediaBubble(message) && renderMessageMediaBlock(message)}
                  <div
                    className={clsx(classes.textContentItem, {
                      [classes.textContentItemDeleted]: message.isDeleted && !revealedDeleted[message.id],
					  [classes.textContentItemEdited]: message.isEdited,
                      [classes.textContentItemSticker]: isStickerMessage(message),
                    })}
                  >
                    {renderDeletedNotice(message)}
                    {renderInstagramCommentTag(message)}
                    {(!message.isDeleted || revealedDeleted[message.id]) && message.quotedMsg && !hasMediaBubble(message) && renderQuotedMessage(message)}
                    {(!message.isDeleted || revealedDeleted[message.id]) && renderInteractiveContent(message)}
                    {(!message.isDeleted || revealedDeleted[message.id]) && shouldShowBodyForMessage(message) && (
                      renderBodyWithMentions(
                        (message.mediaType === "locationMessage" || message.mediaType === "liveLocationMessage")
                          ? null
                          : (message.signedMessage && message.sign && message.sign !== 'false'
                            ? `*${message.sign}:*\n${message.body}`
                            : message.body),
                        message
                      )
                    )}
                    {renderMessageReactions(message)}
                    <span className={clsx(classes.timestamp, {
                      [classes.timestampSticker]: isStickerMessage(message),
                    })}>
				      {message.isEdited && (
                        <span
                          title={message.originalBody ? i18n.t("messagesList.edited.original", { body: message.originalBody }) : undefined}
                          style={{ cursor: message.originalBody ? "help" : "default" }}
                        >
                          {i18n.t("messagesList.edited.label")}{" "}
                        </span>
                      )}
                      {renderConnectionBadge(message)}
                      {safeFormatDate(message.createdAt, "HH:mm")}
                      {renderMessageAck(message)}
                    </span>
                  </div>
                </div>
              ))}
            </React.Fragment>
          );
        }
      });
      // Append pending queued messages (if any)
      const pending = getPendingForTicket(ticketId || (ticket && ticket.id));
      if (!isReadOnlyMode && pending && pending.length > 0) {
        pending.forEach((pm) => {
          try {
            viewMessagesList.push(
              <React.Fragment key={pm.tempId}>
                {/* Daily timestamp placeholder for queued messages - keep simple */}
                <div className={classes.optimisticRow}>
                  <div className={classes.messageRight} style={{ opacity: 0.9 }}>
                    <div className={classes.textContentItem}>
                      {renderSentMessageMeta(pm)}
                      <MarkdownWrapper>
                        {pm.signMessage && pm.userName ? `*${pm.userName}*:\n${pm.body}` : pm.body}
                      </MarkdownWrapper>
                      <div className={classes.timestamp}>
                        <span style={{ display: 'inline-flex', alignItems: 'center' }}>
                          <CircularProgress size={14} style={{ marginLeft: 6 }} />
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </React.Fragment>
            );
          } catch (e) {
            console.warn('Erro ao renderizar pending message', e, pm);
          }
        });
      }

      // Append optimistic messages (mensagens otimistas)
      const optimisticMsgs = getOptimisticMessages(ticketId || (ticket && ticket.id));
      if (!isReadOnlyMode && optimisticMsgs && optimisticMsgs.length > 0) {
        const persistedIds = new Set(
          displayedMessages
            .map((message) => getMessageKey(message?.id))
            .filter(Boolean)
        );
        const persistedOptimisticIds = new Set(
          displayedMessages
            .map((message) => getMessageKey(message?.optimisticId))
            .filter(Boolean)
        );

        optimisticMsgs.forEach((optMsg, idx) => {
          const optId = getMessageKey(optMsg?.optimisticId);
          if (!optId) {
            return;
          }

          if (persistedOptimisticIds.has(optId) || persistedIds.has(optId)) {
            return;
          }

          try {
            viewMessagesList.push(
              <React.Fragment key={optMsg.optimisticId}>
                <div className={classes.optimisticRow}>
                  <div className={classes.messageRight}>
                    {optMsg.quotedMsg && renderQuotedMessage(optMsg)}
                    <div className={classes.textContentItem}>
                      {renderSentMessageMeta(optMsg)}
                      <MarkdownWrapper>
                        {optMsg.signMessage && optMsg.userName ? `*${optMsg.userName}*:\n${optMsg.body}` : optMsg.body}
                      </MarkdownWrapper>
                      <span className={classes.timestamp}>
                        {format(parseISO(optMsg.createdAt), "HH:mm")}
                        <AccessTime className={classes.ackIcons} />
                      </span>
                    </div>
                  </div>
                </div>
              </React.Fragment>
            );
          } catch (e) {
            console.warn('Erro ao renderizar optimistic message', e, optMsg);
          }
        });
      }

      // Adicionar ref no final para scroll (após todas as mensagens, incluindo otimistas)
      viewMessagesList.push(
        <div
          key="scroll-anchor"
          ref={lastMessageRef}
          style={{ float: "left", clear: "both" }}
        />
      );

      // Filter out null elements that might have been returned due to invalid messages
      return viewMessagesList.filter(Boolean);
    } else {
      return <div>{i18n.t("messagesList.emptyChat")}</div>;
    }
  };

  // Função para fechar o diálogo de ticket existente
  const handleCloseExistingTicketDialog = () => {
    setOpenExistingTicketDialog(false);
    setExistingTicketData(null);
  };

  // Função para abrir a conversa existente
  const handleOpenExistingTicket = () => {
    console.log('handleOpenExistingTicket chamado. Dados do ticket:', existingTicketData);
    if (existingTicketData && existingTicketData.uuid) {
      console.log('Redirecionando para:', `/tickets/${existingTicketData.uuid}`);
      history.push(`/tickets/${existingTicketData.uuid}`);
    } else {
      console.error('Dados do ticket inválidos ou uuid não encontrado:', existingTicketData);
      toastError(i18n.t("messagesList.openTicketFailed"));
    }
    handleCloseExistingTicketDialog();
  };

  return (
    <div className={classes.messagesListWrapper}>
      {!isReadOnlyMode && isForwardSelectionMode && (
        <div className={classes.forwardSelectionBar}>
          <Typography variant="body2">
            {selectedForwardMessages.length === 1
              ? i18n.t("messagesList.forwardSelection.selectedSingle", { count: selectedForwardMessages.length })
              : i18n.t("messagesList.forwardSelection.selectedMultiple", { count: selectedForwardMessages.length })}
          </Typography>

          <div className={classes.forwardSelectionActions}>
            <Button
              size="small"
              variant="contained"
              color="primary"
              onClick={openForwardModal}
              disabled={selectedForwardMessages.length === 0}
            >
              {i18n.t("messagesList.forwardSelection.forwardMessages")}
            </Button>
            <Button
              size="small"
              variant="outlined"
              onClick={clearForwardSelection}
            >
              {i18n.t("messagesList.forwardSelection.cancelSelection")}
            </Button>
          </div>
        </div>
      )}

      {!isReadOnlyMode && (
        <MessageOptionsMenu
          message={selectedMessage}
          anchorEl={anchorEl}
          anchorPosition={anchorPosition}
          menuOpen={messageOptionsMenuOpen}
          handleClose={handleCloseMessageOptionsMenu}
          ticket={ticket}
        />
      )}

      <Menu
        anchorEl={igReplyMenuAnchor}
        open={Boolean(igReplyMenuAnchor)}
        onClose={handleCloseIgReplyMenu}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
      >
        <MenuItem onClick={() => handleChooseIgReplyMode("public")}>
          <Comment fontSize="small" style={{ marginRight: 8, color: "#E1306C" }} />
          {i18n.t("messagesList.instagram.replyPublic")}
        </MenuItem>
        <MenuItem onClick={() => handleChooseIgReplyMode("private")}>
          <ChatBubbleOutline fontSize="small" style={{ marginRight: 8, color: "#833AB4" }} />
          {i18n.t("messagesList.instagram.replyDm")}
        </MenuItem>
      </Menu>

      {!isReadOnlyMode && (
        <Popover
          open={Boolean(reactionAnchorEl)}
          anchorEl={reactionAnchorEl}
          onClose={handleCloseReactionPicker}
          anchorOrigin={{
            vertical: "top",
            horizontal: reactionMessage?.fromMe ? "left" : "right",
          }}
          transformOrigin={{
            vertical: "bottom",
            horizontal: reactionMessage?.fromMe ? "right" : "left",
          }}
          classes={{
            paper: classes.reactionPopoverPaper,
          }}
        >
          <div className={classes.reactionQuickBar}>
            {QUICK_REACTIONS.map((emoji) => (
              <Button
                key={`quick-reaction-${emoji}`}
                className={classes.reactionQuickButton}
                onClick={() => handleSendReaction(emoji)}
              >
                {emoji}
              </Button>
            ))}
            <IconButton
              size="small"
              className={classes.reactionPlusButton}
              onClick={handleOpenReactionPalette}
            >
              <Add fontSize="small" />
            </IconButton>
          </div>
        </Popover>
      )}

      {!isReadOnlyMode && (
        <Popover
          open={Boolean(reactionPaletteAnchorEl)}
          anchorEl={reactionPaletteAnchorEl}
          onClose={handleCloseReactionPalette}
          anchorOrigin={{
            vertical: "top",
            horizontal: reactionMessage?.fromMe ? "left" : "right",
          }}
          transformOrigin={{
            vertical: "bottom",
            horizontal: reactionMessage?.fromMe ? "right" : "left",
          }}
          classes={{
            paper: classes.reactionPalettePaper,
          }}
        >
          <div className={classes.reactionPickerWrapper} ref={reactionPickerRootRef}>
            <Picker
              onSelect={handleSelectReactionFromPicker}
              showPreview={false}
              showSkinTones={false}
              showCategoryNames={true}
              title={i18n.t("messagesList.reactions.react")}
              emoji="speech_balloon"
              perLine={9}
              set="facebook"
              include={[
                "recent",
                "people",
                "nature",
                "foods",
                "activity",
                "places",
                "objects",
                "symbols",
                "flags",
              ]}
              i18n={{
                search: i18n.t("messagesList.reactions.search"),
                notfound: i18n.t("messagesList.reactions.notFound"),
                categories: {
                  search: i18n.t("messagesList.reactions.categories.search"),
                  recent: i18n.t("messagesList.reactions.categories.recent"),
                  people: i18n.t("messagesList.reactions.categories.people"),
                  nature: i18n.t("messagesList.reactions.categories.nature"),
                  foods: i18n.t("messagesList.reactions.categories.foods"),
                  activity: i18n.t("messagesList.reactions.categories.activity"),
                  places: i18n.t("messagesList.reactions.categories.places"),
                  objects: i18n.t("messagesList.reactions.categories.objects"),
                  symbols: i18n.t("messagesList.reactions.categories.symbols"),
                  flags: i18n.t("messagesList.reactions.categories.flags"),
                  custom: i18n.t("messagesList.reactions.categories.custom"),
                },
              }}
            />
          </div>
        </Popover>
      )}

      {!isReadOnlyMode && (
        <Popover
          open={Boolean(reactionDetailsAnchorEl)}
          anchorEl={reactionDetailsAnchorEl}
          onClose={handleCloseReactionDetails}
          anchorOrigin={{
            vertical: "top",
            horizontal: "center",
          }}
          transformOrigin={{
            vertical: "bottom",
            horizontal: "center",
          }}
          classes={{
            paper: classes.reactionDetailsPaper,
          }}
        >
          {reactionDetailsMessage && (
            <>
            <Tabs
              value={reactionDetailsFilter}
              onChange={(_e, value) => setReactionDetailsFilter(value)}
              className={classes.reactionDetailsTabs}
              indicatorColor="primary"
              textColor="primary"
              variant="scrollable"
              scrollButtons="auto"
            >
              {getReactionTabs(reactionDetailsMessage).map((tab) => (
                <Tab
                  key={`reaction-tab-${tab.key}`}
                  value={tab.key}
                  label={tab.label}
                  className={classes.reactionDetailsTab}
                />
              ))}
            </Tabs>

            <List className={classes.reactionDetailsList}>
              {getFilteredReactions(reactionDetailsMessage, reactionDetailsFilter).length === 0 && (
                <Typography className={classes.reactionDetailsEmpty}>
                  {i18n.t("messagesList.reactions.empty")}
                </Typography>
              )}

              {getFilteredReactions(reactionDetailsMessage, reactionDetailsFilter).map((reaction, index) => {
                const actorName = getReactionActorName(reaction);
                const key = `${reaction?.actorId || "unknown"}-${reaction?.emoji || "none"}-${index}`;

                return (
                  <ListItem key={key} className={classes.reactionDetailsItem}>
                    <ListItemAvatar>
                      <Avatar className={classes.reactionDetailsAvatar}>
                        {getInitials(actorName)}
                      </Avatar>
                    </ListItemAvatar>

                    <ListItemText
                      primary={actorName}
                      secondary={reaction?.fromMe ? i18n.t("messagesList.reactions.clickToRemove") : ""}
                      classes={{
                        primary: classes.reactionDetailsPrimary,
                        secondary: classes.reactionDetailsSecondary,
                      }}
                    />

                    <span className={classes.reactionDetailsEmoji}>{reaction?.emoji || ""}</span>
                  </ListItem>
                );
              })}
            </List>
            </>
          )}
        </Popover>
      )}
      
      {/* Dialog para informar sobre ticket existente */}
      {!isReadOnlyMode && (
      <Dialog
        open={openExistingTicketDialog}
        onClose={handleCloseExistingTicketDialog}
        aria-labelledby="existing-ticket-dialog-title"
        aria-describedby="existing-ticket-dialog-description"
      >
        <DialogTitle id="existing-ticket-dialog-title">
          {i18n.t("messagesList.existingTicket.title")}
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="existing-ticket-dialog-description">
            {i18n.t("messagesList.existingTicket.text")}
            {existingTicketData && existingTicketData.user && (
              <>
                <br /><br />
                <strong>{i18n.t("messagesList.existingTicket.agent")}</strong> {existingTicketData.user.name}
                {existingTicketData.queue && (
                  <>
                    <br />
                    <strong>{i18n.t("messagesList.existingTicket.queue")}</strong> {existingTicketData.queue.name}
                  </>
                )}
              </>
            )}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseExistingTicketDialog} color="default">
            {i18n.t("common.cancel")}
          </Button>
          <Button onClick={handleOpenExistingTicket} color="primary" variant="contained" autoFocus>
            {i18n.t("messagesList.existingTicket.open")}
          </Button>
        </DialogActions>
      </Dialog>
      )}

      {!isReadOnlyMode && (
      <Dialog
        open={openMentionDialog}
        onClose={handleCloseMentionDialog}
        aria-labelledby="mention-dialog-title"
        aria-describedby="mention-dialog-description"
      >
        <DialogTitle id="mention-dialog-title">
          {i18n.t("messagesList.mention.dialogTitle")}
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="mention-dialog-description">
            {i18n.t("messagesList.mention.dialogText")}
          </DialogContentText>
          {selectedMentionParticipant && (
            <div className={classes.mentionDialogInfo}>
              <div><strong>{i18n.t("messagesList.mention.name")}</strong> {selectedMentionParticipant.displayName || i18n.t("common.unknown")}</div>
              <div><strong>{i18n.t("messagesList.mention.number")}</strong> {selectedMentionParticipant.participantNumber || i18n.t("messagesList.mention.notAvailable")}</div>
            </div>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseMentionDialog} color="default">
            {i18n.t("common.close")}
          </Button>
          <Button onClick={handleStartDirectConversationFromMention} color="primary" variant="contained">
            {i18n.t("messagesList.mention.chatPrivately")}
          </Button>
        </DialogActions>
      </Dialog>
      )}

      {!isReadOnlyMode && (
      <Dialog
        open={openSharedContactsDialog}
        onClose={handleCloseSharedContacts}
        fullWidth
        maxWidth="sm"
        aria-labelledby="shared-contacts-dialog-title"
      >
        <DialogTitle id="shared-contacts-dialog-title">
          {i18n.t("messagesList.sharedContact.count", { count: selectedSharedContacts.length })}
        </DialogTitle>
        <DialogContent dividers>
          <List className={classes.sharedContactsDialogList}>
            {selectedSharedContacts.map((contact, index) => (
              <React.Fragment key={`${contact.name}-${contact.number}-${index}`}>
                <ListItem className={classes.sharedContactItem}>
                  <ListItemAvatar>
                    <Avatar className={classes.contactCardAvatar}>
                      {getInitials(contact.name)}
                    </Avatar>
                  </ListItemAvatar>
                  <ListItemText
                    primary={contact.name}
                    secondary={formatPhoneNumber(contact.number)}
                    classes={{
                      primary: classes.sharedContactPrimary,
                      secondary: classes.sharedContactSecondary,
                    }}
                  />
                  <div className={classes.sharedContactActions}>
                    <Button
                      variant="contained"
                      color="primary"
                      className={classes.sharedContactActionButton}
                      onClick={() => handleStartConversationWithSharedContact(contact)}
                      disabled={!contact.number}
                    >
                      {i18n.t("messagesList.sharedContact.chat")}
                    </Button>
                    <Button
                      variant="outlined"
                      color="primary"
                      className={classes.sharedContactActionButton}
                      onClick={() => handleSaveSharedContact(contact)}
                      disabled={!contact.number}
                    >
                      {i18n.t("common.save")}
                    </Button>
                  </div>
                </ListItem>
                {index < selectedSharedContacts.length - 1 && <Divider component="li" />}
              </React.Fragment>
            ))}
          </List>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseSharedContacts} color="default">
            {i18n.t("common.close")}
          </Button>
        </DialogActions>
      </Dialog>
      )}

      <div
        id="messagesList"
        ref={messagesListRef}
        className={classes.messagesList}
        onScroll={handleScroll}
      >
        {/* Loading no topo quando carregando mais mensagens antigas */}
        {loading && pageNumber > 1 && (
          <div className={classes.historyLoadingContainer}>
            <CircularProgress size={24} />
          </div>
        )}
        {displayedMessages.length > 0 ? renderMessages() : []}
      </div>
      {/* Loading inicial quando abre a conversa */}
      {loading && pageNumber === 1 && (
        <div className={classes.circleLoadingContainer}>
          <CircularProgress className={classes.circleLoading} />
        </div>
      )}
      {mediaViewer && (
        <MediaGalleryViewer
          open
          onClose={closeMediaViewer}
          initialItem={mediaViewer.initialItem}
          source={mediaViewer.single || allowedMessageIdSet ? null : mediaGallerySource}
          localItems={mediaViewer.single ? undefined : localGalleryItems}
          onGoToMessage={mediaViewer.single ? undefined : handleMediaViewerGoToMessage}
        />
      )}
      {!isNearBottom && (
        <div className={classes.goToBottomButtonWrapper}>
          <IconButton
            className={classes.goToBottomButton}
            onClick={handleGoToBottom}
            aria-label={i18n.t("messagesList.goToBottom")}
          >
            <ExpandMore />
            {hasUnreadWhileAwayFromBottom && (
              <span className={classes.goToBottomIndicator}>!</span>
            )}
          </IconButton>
        </div>
      )}
    </div>
  );
};

export default MessagesList;
